import { loadConversationPages } from '@/components/messageFeedConversationScope';
// API client and services for moy-gorod frontend
import axios, { type AxiosError } from 'axios';
import { extractUserFacingErrorDetail, interpretUserFacingError } from '@/utils/userFacingError';
import { toSourceIdString, type SourceIdValue } from '@/utils/sourceIds';
import { isActiveContourHeaderEligible, readActiveContour } from '@/utils/activeContour';
import { readActiveWorkspace } from '@/utils/activeWorkspace';
import type {
  AccessAdminCatalog,
  AuthResponse,
  LoginCredentials,
  PlatformEntryContext,
  PlatformWorkspace,
  RegistrationPayload,
  Ticket,
  TicketComment,
  User,
  Message,
  FeedDeltaResponse,
  FeedJumpResponse,
  FeedOpenResponse,
  MessageSummary,
  MessageInspector,
  MessageDeleteCapabilities,
  MessageDeleteExecutionResult,
  MessageDeleteMode,
  MessageReplyCapabilities,
  MessageReplyExecutionResult,
  MessageReplyDeliveryStatus,
  StatusRef,
  DepartmentRef,
  TopicRef,
  LocalityRef,
  StreetRef,
  BuildingRef,
  TerritorialDepartmentRef,
  DistrictRef,
  ManagementCompanyRef,
  TelegramUserRole,
  TelegramUserModerationRow,
  TelegramUserDossier,
  TenantSettings,
  HistoryVisibilityMode,
  ExternalIntegrationConnection,
  CivicAutoReplyPolicy,
  CivicSourceBinding,
  CivicResponseCase,
  IntegrationContainerArea,
  PlannedEventMatchDetail,
  PlannedWorkDetailResponse,
  PlannedWorkSummary,
  FeedPreferences,
  MessageWorkBellCommonState,
  MessageWorkBellHandledResponse,
  FeedOptionsResponse,
  FeedTelegramChannelOption,
  HelpdeskThreadSummary,
  HelpdeskThreadDetail,
  HelpdeskAutopilotRun,
  HelpdeskDraft,
  HelpdeskProblemAnchor,
  HelpdeskAnalyzeResponse,
  HelpdeskSplitConfirmResponse,
  HelpdeskDocsSearchResult,
  HelpdeskDocDetail,
  HelpdeskMasterCase,
  HelpdeskDedupEvaluationResponse,
  SystemTask,
  SystemTaskArtifact,
  SystemTaskArtifactsResponse,
  SystemTaskCompleteRunPayload,
  SystemTaskCreatePayload,
  SystemTaskSendFollowupPayload,
  SystemTasksListResponse,
  HelpdeskContractCase,
  HelpdeskContractCaseCreatePayload,
  HelpdeskContractRun,
  HelpdeskContractRunCompletePayload,
  HelpdeskContractRunFollowupPayload,
  HelpdeskContractUploadResponse,
  LlmWorkbenchCompareResponse,
  LlmWorkbenchConfigPayload,
  LlmWorkbenchInputPayload,
  LlmWorkbenchPayloadPreview,
  LlmWorkbenchRegistryResponse,
  UserCreatePayload,
  FrontendAccessPolicy,
  UserManagementPolicy,
  UserAccessRequest,
  GlobalAccountProfileUpdatePayload,
  ReplaceUserAccessProfilePayload,
  UserUpdatePayload,
  TransportErrorDetail,
  RelatedTicketCountItem,
  RelatedTicketsResponse,
} from '@/types';
/** Извлечь X-Trace-Id из ответа ошибки API */
export function getTraceIdFromError(error: unknown): string | null {
  const err = error as AxiosError<unknown>;
  const id = err.response?.headers?.['x-trace-id'] ?? err.response?.headers?.['X-Trace-Id'];
  return typeof id === 'string' ? id : null;
}

export function getTransportErrorDetail(error: unknown): TransportErrorDetail | null {
  const data = (error as { response?: { data?: { error?: unknown; detail?: unknown; errors?: unknown } } })?.response?.data;
  const firstError = Array.isArray(data?.errors) ? data.errors[0] : null;
  const payload = data?.error ?? data?.detail ?? firstError;
  if (!payload || typeof payload !== 'object') return null;
  const detail = payload as Record<string, unknown>;
  const code = typeof detail.code === 'string' ? detail.code : typeof detail.error === 'string' ? detail.error : null;
  const message = typeof detail.message === 'string' ? detail.message : null;
  if (!code || !message) return null;
  return {
    code,
    message,
    detail: typeof detail.detail === 'string' ? detail.detail : null,
    retryable: typeof detail.retryable === 'boolean' ? detail.retryable : undefined,
    operation: typeof detail.operation === 'string' ? detail.operation : null,
    transport: typeof detail.transport === 'string' ? detail.transport : null,
    upstream_host: typeof detail.upstream_host === 'string' ? detail.upstream_host : null,
    upstream_status: typeof detail.upstream_status === 'number' ? detail.upstream_status : null,
    request_id: typeof detail.request_id === 'string' ? detail.request_id : null,
    correlation_id: typeof detail.correlation_id === 'string' ? detail.correlation_id : null,
    trace_id: typeof detail.trace_id === 'string' ? detail.trace_id : null,
    tenant_id: typeof detail.tenant_id === 'string' ? detail.tenant_id : null,
  };
}

export function extractApiErrorDetail(error: unknown, fallbackMessage?: string): string | null {
  const interpreted = interpretUserFacingError(error, {
    fallbackMessage: fallbackMessage ?? undefined,
  });
  if (interpreted.kind !== 'technical') return interpreted.userMessage;
  const rawDetail = extractUserFacingErrorDetail(error);
  return rawDetail || interpreted.userMessage || null;
}

export function formatTransportError(error: unknown, fallback: string): string {
  const detail = getTransportErrorDetail(error);
  if (detail) {
    return [
      detail.message,
      detail.detail || null,
      detail.code ? `Код ошибки: ${detail.code}` : null,
      detail.trace_id ? `Trace ID: ${detail.trace_id}` : null,
      detail.request_id ? `Request ID: ${detail.request_id}` : null,
    ].filter(Boolean).join(' ');
  }
  const legacyDetail = extractApiErrorDetail(error);
  return legacyDetail ?? fallback;
}

function unwrapTransportResponse<T>(payload: T): T {
  if (!payload || typeof payload !== 'object') return payload;
  const envelope = payload as { ok?: unknown; error?: unknown };
  if (envelope.ok !== false || !envelope.error || typeof envelope.error !== 'object') {
    return payload;
  }
  throw {
    response: {
      data: payload,
    },
  };
}

const FEED_DEBUG_FLAG_KEY = 'MG_DEBUG_FEED';
const FEED_DEBUG_UNTIL_KEY = 'MG_DEBUG_FEED_UNTIL';
const FEED_DEBUG_TTL_MS = 15 * 60 * 1000;

const sanitizeLogUrl = (url: string | undefined | null): string => {
  if (!url) return '';
  try {
    const base =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'http://localhost';
    const parsed = new URL(url, base);
    parsed.searchParams.delete('token');
    parsed.searchParams.delete('sig');
    const query = parsed.searchParams.toString();
    return `${parsed.pathname}${query ? `?${query}` : ''}`;
  } catch {
    return url.replace(/([?&])(token|sig)=[^&]*/gi, '$1$2=<redacted>');
  }
};

const isProdRuntime = (): boolean => import.meta.env.PROD;

const isFeedDebugLogEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  const now = Date.now();
  const search = window.location.search || '';
  if (search.includes('debug_feed=1')) {
    window.localStorage.setItem(FEED_DEBUG_FLAG_KEY, '1');
    window.localStorage.setItem(FEED_DEBUG_UNTIL_KEY, String(now + FEED_DEBUG_TTL_MS));
  }
  const untilRaw = window.localStorage.getItem(FEED_DEBUG_UNTIL_KEY);
  const untilTs = untilRaw ? Number(untilRaw) : 0;
  if (Number.isFinite(untilTs) && untilTs > now) {
    return window.localStorage.getItem(FEED_DEBUG_FLAG_KEY) === '1';
  }
  window.localStorage.removeItem(FEED_DEBUG_UNTIL_KEY);
  if (window.localStorage.getItem(FEED_DEBUG_FLAG_KEY) === '1') {
    window.localStorage.removeItem(FEED_DEBUG_FLAG_KEY);
  }
  return false;
};

/**
 * Ticket cache contract:
 * - cache-first read for instant open;
 * - stale-while-revalidate via force refresh;
 * - lightweight localStorage persistence for fast reload;
 * - LRU + TTL bound to avoid unbounded browser memory growth.
 */
const TICKET_CACHE_MAX_ITEMS = 1000;
const TICKET_CACHE_TTL_MS = 600 * 60 * 1000;
const TICKET_CACHE_PERSIST_MAX_ITEMS = 250;
const TICKET_CACHE_LS_KEY = 'mg_ticket_cache_light_v1';
const REFERENCE_SNAPSHOT_LS_KEY = 'mg_reference_snapshot_v1';
const FEED_CACHE_LS_KEY = 'mg_feed_cache_v1';
const LLM_WORKBENCH_SESSION_LS_KEY = 'mg_llm_workbench_session_v1';
const EXPORT_SESSIONS_LS_KEY = 'export_sessions';
export const PROTECTED_DATA_SCOPE_RESET_EVENT = 'moy-gorod:protected-data-scope-reset';
let protectedDataScopeEpoch = 0;
type TicketCacheSource = 'light' | 'detail';
type TicketCacheEntry = {
  data: Ticket;
  ts: number;
  lastAccessTs: number;
  source: TicketCacheSource;
};
const ticketCache = new Map<number, TicketCacheEntry>();
let ticketCacheHydrated = false;
const ticketCacheTelemetry = {
  hits: 0,
  misses: 0,
  evictions: 0,
  persistedCount: 0,
};

function emitTicketCacheTelemetry(event: string, payload?: Record<string, unknown>): void {
  if (isProdRuntime() && !isFeedDebugLogEnabled()) return;
  try {
    if (payload && Object.keys(payload).length > 0) {
      console.info(`[TICKET_${event}]`, payload);
    } else {
      console.info(`[TICKET_${event}]`);
    }
  } catch {
    // telemetry must never break API layer
  }
}

function safeLocalStorageGet(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalStorageSet(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore quota/storage errors
  }
}

function safeLocalStorageRemove(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore storage errors
  }
}

function getTicketCacheUpdatedAtTs(ticket: Ticket): number {
  const raw = ticket.updated_at ?? ticket.created_at ?? null;
  if (!raw) return 0;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function isIncomingTicketNewerOrUnknown(existing: Ticket | null, incoming: Ticket): boolean {
  if (!existing) return true;
  const existingUpdatedAtTs = getTicketCacheUpdatedAtTs(existing);
  const incomingUpdatedAtTs = getTicketCacheUpdatedAtTs(incoming);
  if (existingUpdatedAtTs === 0 && incomingUpdatedAtTs === 0) return true;
  return incomingUpdatedAtTs > existingUpdatedAtTs;
}

function ticketToLightSnapshot(ticket: Ticket): Ticket {
  return {
    id: ticket.id,
    tenant_id: ticket.tenant_id ?? null,
    status_id: ticket.status_id ?? null,
    status: ticket.status ?? null,
    ticket_no: (ticket.ticket_no as number | null | undefined) ?? null,
    origin_tenant_id: (ticket.origin_tenant_id as string | null | undefined) ?? null,
    origin_tenant_code: (ticket.origin_tenant_code as string | null | undefined) ?? null,
    display_ticket_no: (ticket.display_ticket_no as string | null | undefined) ?? null,
    department_id: ticket.department_id ?? null,
    department: ticket.department ?? null,
    topic_id: ticket.topic_id ?? null,
    topic: ticket.topic ?? null,
    locality_id: ticket.locality_id ?? null,
    locality: ticket.locality ?? null,
    street_id: ticket.street_id ?? null,
    street: ticket.street ?? null,
    building_id: ticket.building_id ?? null,
    building: ticket.building ?? null,
    apartment_number: ticket.apartment_number ?? null,
    territorial_department_id: ticket.territorial_department_id ?? null,
    territorial_department: ticket.territorial_department ?? null,
    management_company_id: ticket.management_company_id ?? null,
    management_company: ticket.management_company ?? null,
    responsible_user_id: ticket.responsible_user_id ?? null,
    responsible_user: ticket.responsible_user ?? null,
    address_raw: ticket.address_raw ?? null,
    address_norm: ticket.address_norm ?? null,
    address_display: (ticket.address_display as string | null | undefined) ?? null,
    address_source: (ticket.address_source as 'ref' | 'sql' | null | undefined) ?? null,
    deadline_at: ticket.deadline_at ?? null,
    lock_version:
      ticket.lock_version == null || !Number.isFinite(Number(ticket.lock_version))
        ? undefined
        : Number(ticket.lock_version),
    created_at: ticket.created_at ?? null,
    updated_at: ticket.updated_at ?? null,
    message_created_at: (ticket.message_created_at as string | null | undefined) ?? null,
    message_updated_at: (ticket.message_updated_at as string | null | undefined) ?? null,
  };
}

function hydrateTicketCacheFromStorage(): void {
  if (ticketCacheHydrated) return;
  ticketCacheHydrated = true;
  const raw = safeLocalStorageGet(TICKET_CACHE_LS_KEY);
  if (!raw) return;
  try {
    const parsed = JSON.parse(raw) as Array<{ id: number; ts: number; lastAccessTs: number; data: Ticket }>;
    const now = Date.now();
    for (const row of parsed ?? []) {
      const id = Number(row?.id);
      if (!Number.isFinite(id) || id <= 0) continue;
      const ts = Number(row?.ts);
      const lastAccessTs = Number(row?.lastAccessTs);
      const data = row?.data;
      if (!data || typeof data !== 'object') continue;
      if (Number.isFinite(ts) && now - ts > TICKET_CACHE_TTL_MS) continue;
      ticketCache.set(id, {
        data: data as Ticket,
        ts: Number.isFinite(ts) ? ts : now,
        lastAccessTs: Number.isFinite(lastAccessTs) ? lastAccessTs : now,
        source: 'light',
      });
    }
  } catch {
    // ignore parse errors
  }
}

function persistTicketCacheLightSnapshot(): void {
  if (typeof window === 'undefined') return;
  const rows = [...ticketCache.entries()]
    .sort((a, b) => b[1].lastAccessTs - a[1].lastAccessTs)
    .slice(0, TICKET_CACHE_PERSIST_MAX_ITEMS)
    .map(([id, entry]) => ({
      id,
      ts: entry.ts,
      lastAccessTs: entry.lastAccessTs,
      data: ticketToLightSnapshot(entry.data),
    }));
  ticketCacheTelemetry.persistedCount = rows.length;
  safeLocalStorageSet(TICKET_CACHE_LS_KEY, JSON.stringify(rows));
}

function enforceTicketCacheBounds(): void {
  const now = Date.now();
  for (const [id, entry] of ticketCache.entries()) {
    if (now - entry.ts > TICKET_CACHE_TTL_MS) {
      ticketCache.delete(id);
      ticketCacheTelemetry.evictions += 1;
      emitTicketCacheTelemetry('CACHE_EVICTED', { reason: 'ttl', ticket_id: id });
    }
  }
  if (ticketCache.size <= TICKET_CACHE_MAX_ITEMS) return;
  const victims = [...ticketCache.entries()]
    .sort((a, b) => a[1].lastAccessTs - b[1].lastAccessTs)
    .slice(0, ticketCache.size - TICKET_CACHE_MAX_ITEMS);
  for (const [id] of victims) {
    ticketCache.delete(id);
    ticketCacheTelemetry.evictions += 1;
    emitTicketCacheTelemetry('CACHE_EVICTED', { reason: 'lru', ticket_id: id });
  }
}

function mergeTicket(existing: Ticket | null, incoming: Ticket): Ticket {
  if (!existing) return incoming;
  if (!isIncomingTicketNewerOrUnknown(existing, incoming)) return existing;
  return {
    ...existing,
    ...incoming,
  };
}

function getTicketFromCache(id: number, requiredSource?: TicketCacheSource): Ticket | null {
  hydrateTicketCacheFromStorage();
  const entry = ticketCache.get(id);
  if (!entry) {
    ticketCacheTelemetry.misses += 1;
    emitTicketCacheTelemetry('CACHE_MISS', { ticket_id: id, reason: 'not_found' });
    return null;
  }
  if (Date.now() - entry.ts > TICKET_CACHE_TTL_MS) {
    ticketCache.delete(id);
    ticketCacheTelemetry.evictions += 1;
    ticketCacheTelemetry.misses += 1;
    emitTicketCacheTelemetry('CACHE_MISS', { ticket_id: id, reason: 'expired' });
    persistTicketCacheLightSnapshot();
    return null;
  }
  if (requiredSource && entry.source !== requiredSource) {
    ticketCacheTelemetry.misses += 1;
    emitTicketCacheTelemetry('CACHE_MISS', {
      ticket_id: id,
      reason: 'source_mismatch',
      source: entry.source,
      required_source: requiredSource,
    });
    return null;
  }
  entry.lastAccessTs = Date.now();
  ticketCacheTelemetry.hits += 1;
  emitTicketCacheTelemetry('CACHE_HIT', { ticket_id: id, source: entry.source });
  return entry.data;
}

function setTicketCache(id: number, data: Ticket, source: TicketCacheSource = 'detail'): Ticket {
  hydrateTicketCacheFromStorage();
  const prev = ticketCache.get(id)?.data ?? null;
  // Detail payloads come from direct GET/PATCH and must win over light snapshots
  // even when updated_at is equal, otherwise lock_version can stay stale/null.
  const merged = source === 'detail'
    ? ({ ...(prev ?? {}), ...data } as Ticket)
    : mergeTicket(prev, data);
  ticketCache.set(id, {
    data: merged,
    ts: Date.now(),
    lastAccessTs: Date.now(),
    source,
  });
  emitTicketCacheTelemetry('CACHE_UPSERTED', {
    ticket_id: id,
    source,
    cache_size: ticketCache.size,
    updated_at: merged.updated_at ?? null,
  });
  enforceTicketCacheBounds();
  persistTicketCacheLightSnapshot();
  return merged;
}

function patchTicketCache(id: number, patch: Partial<Ticket>): Ticket | null {
  const current = getTicketFromCache(id);
  if (!current) return null;
  const next = setTicketCache(id, { ...current, ...patch }, 'light');
  emitTicketCacheTelemetry('CACHE_PATCHED', {
    ticket_id: id,
    changed_fields: Object.keys(patch),
  });
  return next;
}

/** Сбросить кэш задачи (после обновления, чтобы при следующем открытии подтянуть свежие данные). */
export function invalidateTicketCache(id: number): void {
  ticketCache.delete(id);
  emitTicketCacheTelemetry('CACHE_REMOVED', { ticket_id: id });
  persistTicketCacheLightSnapshot();
}

/** Кэш деталей сообщений (inspect). TTL 3 мин. */
const MESSAGE_INSPECTOR_CACHE_TTL_MS = 3 * 60 * 1000;
const messageInspectorCache = new Map<number, { data: MessageInspector; ts: number }>();

function getMessageInspectorFromCache(id: number): MessageInspector | null {
  const entry = messageInspectorCache.get(id);
  if (!entry || Date.now() - entry.ts > MESSAGE_INSPECTOR_CACHE_TTL_MS) return null;
  return entry.data;
}

function setMessageInspectorCache(id: number, data: MessageInspector): void {
  messageInspectorCache.set(id, { data, ts: Date.now() });
}

/** Сбросить кэш inspect-сообщения (после мутаций анализа/пайплайна). */
export function invalidateMessageInspectorCache(id: number): void {
  messageInspectorCache.delete(id);
  pipelineCasePreviewCache.delete(id);
}

/** Кэш сообщений задачи (getMessages). TTL 3 мин. */
const TICKET_MESSAGES_CACHE_TTL_MS = 3 * 60 * 1000;
const ticketMessagesCache = new Map<number, { data: TicketMessageItem[]; ts: number }>();
const TICKET_HISTORY_CACHE_TTL_MS = 60 * 1000;
const ticketHistoryCache = new Map<number, { data: TicketHistoryResponse; ts: number }>();

function getTicketMessagesFromCache(ticketId: number): TicketMessageItem[] | null {
  const entry = ticketMessagesCache.get(ticketId);
  if (!entry || Date.now() - entry.ts > TICKET_MESSAGES_CACHE_TTL_MS) return null;
  return entry.data;
}

function setTicketMessagesCache(ticketId: number, data: TicketMessageItem[]): void {
  ticketMessagesCache.set(ticketId, { data, ts: Date.now() });
}

function clearTicketMessagesCache(ticketId: number): void {
  ticketMessagesCache.delete(ticketId);
}

function getTicketHistoryFromCache(ticketId: number): TicketHistoryResponse | null {
  const entry = ticketHistoryCache.get(ticketId);
  if (!entry || Date.now() - entry.ts > TICKET_HISTORY_CACHE_TTL_MS) return null;
  return entry.data;
}

function setTicketHistoryCache(ticketId: number, data: TicketHistoryResponse): TicketHistoryResponse {
  ticketHistoryCache.set(ticketId, { data, ts: Date.now() });
  return data;
}

function clearTicketHistoryCache(ticketId: number): void {
  ticketHistoryCache.delete(ticketId);
}

type TimedCacheEntry<T> = { data: T; ts: number };
type PersistedReferenceSnapshotRow = { key: string; ts: number; data: unknown };

const sharedRequestInflight = new Map<string, Promise<unknown>>();
const REFERENCE_CACHE_TTL_MS = 5 * 60 * 1000;
const FILTERED_TOPICS_CACHE_TTL_MS = 60 * 1000;
const UI_CACHE_TTL_MS = 30 * 1000;
const PIPELINE_CASE_PREVIEW_CACHE_TTL_MS = 60 * 1000;
const REPLY_PREVIEW_CACHE_TTL_MS = 5 * 60 * 1000;
const COMMENTS_CACHE_TTL_MS = 30 * 1000;
type FeedPreferencesScope = {
  id: number;
  epoch: number;
  token: string | null;
  tenant: string;
  revision: number;
  generation: number | null;
  tail: Promise<void>;
  pending?: { key: string; promise: Promise<FeedPreferences> };
  completedWrite?: { key: string; revision: number; responseKey: string };
  resetPromise?: Promise<FeedPreferences>;
  locked: boolean;
  lockUntilLogout: boolean;
};
let feedPreferencesScope: FeedPreferencesScope | null = null;
let feedPreferencesScopeSequence = 0;

function currentFeedPreferencesScope(): FeedPreferencesScope {
  const token = typeof window === 'undefined' ? null : window.localStorage.getItem('access_token');
  const tenant = runtimeTenantCacheScope();
  if (!feedPreferencesScope || feedPreferencesScope.epoch !== protectedDataScopeEpoch
      || feedPreferencesScope.token !== token || feedPreferencesScope.tenant !== tenant) {
    feedPreferencesScope = {
      id: ++feedPreferencesScopeSequence, epoch: protectedDataScopeEpoch, token, tenant,
      revision: 0, generation: null, tail: Promise.resolve(), locked: false, lockUntilLogout: false,
    };
  }
  return feedPreferencesScope;
}

function requireFeedPreferencesScope(scope: FeedPreferencesScope, revision: number): void {
  if (currentFeedPreferencesScope() !== scope || scope.revision !== revision) {
    throw new Error('feed_preferences_obsolete_response');
  }
}

function feedResetGeneration(value: FeedPreferences): number {
  const generation = value.reset_generation ?? 0;
  if (!Number.isInteger(generation) || generation < 0 || generation > 2147483647
      || (value.reset_generation !== undefined && value.reset_generation === null)) {
    throw new Error('feed_preferences_reset_generation_invalid');
  }
  return generation;
}

function publishFeedPreferences(scope: FeedPreferencesScope, revision: number, value: FeedPreferences): FeedPreferences {
  requireFeedPreferencesScope(scope, revision);
  const generation = feedResetGeneration(value);
  const saved = { ...value, reset_generation: generation };
  // A fresh authoritative read/reset invalidates the previous write identity.
  scope.completedWrite = undefined;
  scope.generation = generation;
  return setTimedCache(feedPreferencesCache, `ui:feed_preferences:${scope.id}`, saved);
}

const referenceCache = new Map<string, TimedCacheEntry<unknown>>();
const filteredTopicsCache = new Map<string, TimedCacheEntry<{ topics: TopicRef[]; total_records?: number }>>();
const menuVisibilityCache = new Map<string, TimedCacheEntry<MenuVisibilityByRole>>();
const feedPreferencesCache = new Map<string, TimedCacheEntry<FeedPreferences>>();
const feedOptionsCache = new Map<string, TimedCacheEntry<FeedOptionsResponse>>();
const classificationStatusesCache = new Map<string, TimedCacheEntry<string[]>>();
const pipelineCasePreviewCache = new Map<number, TimedCacheEntry<Record<string, unknown>>>();
const replyPreviewCache = new Map<
  string,
  TimedCacheEntry<
    | {
        source_channel_id: number;
        channel_message_id: number;
        author_name?: string | null;
        text_preview?: string | null;
        source_created_at?: string | null;
      }
    | null
  >
>();
const ticketCommentsCache = new Map<number, TimedCacheEntry<TicketComment[]>>();
let referenceCacheHydrated = false;

/**
 * Revoke every browser-side data snapshot that may outlive a contour or access
 * envelope. Incrementing the epoch also makes already-running API responses
 * fail before their service continuations can repopulate a cache.
 */
export function clearProtectedDataCaches(reason = 'scope_changed'): number {
  protectedDataScopeEpoch += 1;

  for (const timer of ticketHydrateTimers.values()) clearTimeout(timer);
  ticketHydrateTimers.clear();
  ticketHydrateInFlight.clear();
  sharedRequestInflight.clear();

  ticketCache.clear();
  ticketCacheHydrated = true;
  ticketCacheTelemetry.persistedCount = 0;
  messageInspectorCache.clear();
  ticketMessagesCache.clear();
  ticketHistoryCache.clear();
  referenceCache.clear();
  filteredTopicsCache.clear();
  menuVisibilityCache.clear();
  feedPreferencesCache.clear();
  feedOptionsCache.clear();
  classificationStatusesCache.clear();
  pipelineCasePreviewCache.clear();
  replyPreviewCache.clear();
  ticketCommentsCache.clear();
  referenceCacheHydrated = true;
  feedPreferencesScope = null;

  safeLocalStorageRemove(TICKET_CACHE_LS_KEY);
  safeLocalStorageRemove(REFERENCE_SNAPSHOT_LS_KEY);
  safeLocalStorageRemove(FEED_CACHE_LS_KEY);
  safeLocalStorageRemove(LLM_WORKBENCH_SESSION_LS_KEY);
  safeLocalStorageRemove(EXPORT_SESSIONS_LS_KEY);

  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent(PROTECTED_DATA_SCOPE_RESET_EVENT, {
      detail: { epoch: protectedDataScopeEpoch, reason },
    }));
  }
  return protectedDataScopeEpoch;
}

const DEV_FAKE_ACCESS_TOKEN = 'dev-fake-token';
let clientAuthExpiryTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * The server remains the authority for JWT validation.  The browser only reads
 * `exp` so an already expired local credential cannot keep a stale UI alive
 * until the next API/SSE request happens to return 401.
 */
export function getAccessTokenExpiryMs(token: string | null | undefined): number | null {
  if (!token || token === DEV_FAKE_ACCESS_TOKEN) return null;
  const payloadSegment = token.split('.')[1];
  if (!payloadSegment || typeof window === 'undefined' || typeof window.atob !== 'function') return null;
  try {
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const payload = JSON.parse(window.atob(padded)) as { exp?: unknown };
    const expSeconds = Number(payload.exp);
    return Number.isFinite(expSeconds) && expSeconds > 0 ? Math.floor(expSeconds * 1000) : null;
  } catch {
    return null;
  }
}

export function isAccessTokenExpired(token: string | null | undefined, now = Date.now()): boolean {
  const expiresAt = getAccessTokenExpiryMs(token);
  return expiresAt !== null && expiresAt <= now;
}

/**
 * Clears only the token that caused the logout.  This prevents a delayed 401
 * or expiry timer from erasing a newer token received through a fresh login or
 * workspace handoff.
 */
export function forceClientAuthLogout(
  reason: string,
  options: { expectedToken?: string | null; redirect?: boolean } = {},
): boolean {
  if (typeof window === 'undefined') return false;
  const token = window.localStorage.getItem('access_token');
  if (!token || token === DEV_FAKE_ACCESS_TOKEN) return false;
  if (options.expectedToken !== undefined && token !== options.expectedToken) return false;
  if (clientAuthExpiryTimer != null) {
    window.clearTimeout(clientAuthExpiryTimer);
    clientAuthExpiryTimer = null;
  }
  window.localStorage.removeItem('access_token');
  clearProtectedDataCaches(reason);
  window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason } }));
  if (options.redirect !== false) {
    window.setTimeout(() => {
      if (window.localStorage.getItem('access_token') === null) window.location.href = '/login';
    }, 100);
  }
  return true;
}

/** Arms one token-bound expiry timer and returns false when it has already expired. */
export function scheduleClientAuthExpiry(token: string | null | undefined): boolean {
  if (typeof window === 'undefined') return true;
  if (clientAuthExpiryTimer != null) {
    window.clearTimeout(clientAuthExpiryTimer);
    clientAuthExpiryTimer = null;
  }
  const expiresAt = getAccessTokenExpiryMs(token);
  if (expiresAt === null) return true;
  const delay = expiresAt - Date.now();
  if (delay <= 0) {
    forceClientAuthLogout('token_expired', { expectedToken: token, redirect: true });
    return false;
  }
  clientAuthExpiryTimer = window.setTimeout(() => {
    clientAuthExpiryTimer = null;
    forceClientAuthLogout('token_expired', { expectedToken: token, redirect: true });
  }, delay);
  return true;
}

export function getProtectedDataScopeEpoch(): number {
  return protectedDataScopeEpoch;
}

function stableSerialize(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableSerialize(item)).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entryValue]) => entryValue !== undefined)
      .sort(([left], [right]) => left.localeCompare(right));
    return `{${entries.map(([key, entryValue]) => `${JSON.stringify(key)}:${stableSerialize(entryValue)}`).join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

function sharedKey(prefix: string, payload?: unknown): string {
  return payload === undefined ? prefix : `${prefix}:${stableSerialize(payload)}`;
}

function runtimeTenantCacheScope(): string {
  const activeContour = readActiveContour();
  if (activeContour) return `tenant:${activeContour}`;
  const hostname = typeof window !== 'undefined' ? window.location?.hostname ?? '' : '';
  const subdomain = hostname.split('.')[0]?.toLowerCase() ?? '';
  if (!subdomain) return 'tenant:unknown';
  if (subdomain === 'localhost' || hostname.startsWith('127.')) return 'tenant:artem';
  return `tenant:${subdomain.replace(/_stage$/i, '')}`;
}

function tenantSharedKey(prefix: string, payload?: unknown): string {
  return `${runtimeTenantCacheScope()}:${sharedKey(prefix, payload)}`;
}

function getFreshTimedCache<T>(cache: Map<string, TimedCacheEntry<T>>, key: string, ttlMs: number): T | null {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > ttlMs) {
    cache.delete(key);
    return null;
  }
  return entry.data;
}

function getFreshTimedNumberCache<T>(cache: Map<number, TimedCacheEntry<T>>, key: number, ttlMs: number): T | null {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > ttlMs) {
    cache.delete(key);
    return null;
  }
  return entry.data;
}

function setTimedCache<T>(cache: Map<string, TimedCacheEntry<T>>, key: string, data: T): T {
  cache.set(key, { data, ts: Date.now() });
  return data;
}

/** Reply metadata is evictable UI cache, bounded independently from other caches. */
function setReplyPreviewTimedCache<T>(cache: Map<string, TimedCacheEntry<T>>, key: string, data: T): T {
  cache.delete(key);
  cache.set(key, { data, ts: Date.now() });
  while (cache.size > 256) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
  return data;
}

function setTimedNumberCache<T>(cache: Map<number, TimedCacheEntry<T>>, key: number, data: T): T {
  cache.set(key, { data, ts: Date.now() });
  return data;
}

function isPersistableReferenceKey(key: string): boolean {
  const normalizedKey = key.startsWith('tenant:') ? key.replace(/^tenant:[^:]+:/, '') : key;
  return (
    normalizedKey === sharedKey('ref:statuses') ||
    normalizedKey.startsWith('ref:departments') ||
    normalizedKey.startsWith('ref:topics') ||
    normalizedKey === sharedKey('ref:localities') ||
    normalizedKey === sharedKey('ref:territorial_departments') ||
    normalizedKey === sharedKey('ref:districts') ||
    normalizedKey === sharedKey('ref:management_companies') ||
    normalizedKey === sharedKey('ref:users')
  );
}

function hydrateReferenceCacheFromStorage(): void {
  if (referenceCacheHydrated) return;
  referenceCacheHydrated = true;
  const raw = safeLocalStorageGet(REFERENCE_SNAPSHOT_LS_KEY);
  if (!raw) return;
  try {
    const parsed = JSON.parse(raw) as PersistedReferenceSnapshotRow[];
    const now = Date.now();
    for (const row of parsed ?? []) {
      const key = typeof row?.key === 'string' ? row.key : '';
      const ts = Number(row?.ts);
      if (!key || !Number.isFinite(ts) || now - ts > REFERENCE_CACHE_TTL_MS) continue;
      if (!isPersistableReferenceKey(key)) continue;
      referenceCache.set(key, { data: row?.data, ts });
    }
  } catch {
    // ignore malformed persisted snapshots
  }
}

function persistReferenceCacheSnapshot(): void {
  const rows = [...referenceCache.entries()]
    .filter(([key]) => isPersistableReferenceKey(key))
    .sort((left, right) => right[1].ts - left[1].ts)
    .map(([key, entry]) => ({ key, ts: entry.ts, data: entry.data }));
  safeLocalStorageSet(REFERENCE_SNAPSHOT_LS_KEY, JSON.stringify(rows));
}

function getFreshReferenceCache<T>(key: string): T | null {
  hydrateReferenceCacheFromStorage();
  return getFreshTimedCache(referenceCache as Map<string, TimedCacheEntry<T>>, key, REFERENCE_CACHE_TTL_MS);
}

function setReferenceCache<T>(key: string, data: T): T {
  hydrateReferenceCacheFromStorage();
  const next = setTimedCache(referenceCache as Map<string, TimedCacheEntry<T>>, key, data);
  if (isPersistableReferenceKey(key)) {
    persistReferenceCacheSnapshot();
  }
  return next;
}

function invalidateTenantReferenceCache(prefix: string): void {
  hydrateReferenceCacheFromStorage();
  const scopedPrefix = `${runtimeTenantCacheScope()}:${prefix}`;
  for (const key of referenceCache.keys()) {
    if (key === scopedPrefix || key.startsWith(`${scopedPrefix}:`)) {
      referenceCache.delete(key);
    }
  }
  if (prefix === 'ref:topics') {
    for (const key of filteredTopicsCache.keys()) {
      if (key.includes(`${runtimeTenantCacheScope()}:ref:topics`)) {
        filteredTopicsCache.delete(key);
      }
    }
  }
  persistReferenceCacheSnapshot();
}

function withSharedRequest<T>(key: string, loader: () => Promise<T>): Promise<T> {
  const current = sharedRequestInflight.get(key) as Promise<T> | undefined;
  if (current) return current;
  const request = loader().finally(() => {
    if (sharedRequestInflight.get(key) === request) {
      sharedRequestInflight.delete(key);
    }
  });
  sharedRequestInflight.set(key, request);
  return request;
}

// При включённом туннель-прокси запросы идут на тот же origin (dev-сервер), Vite проксирует /api на туннель с Host: artem.stage.moy-gorod.su
const USE_TUNNEL_PROXY = import.meta.env.VITE_USE_TUNNEL_PROXY === 'true' || import.meta.env.VITE_USE_TUNNEL_PROXY === '1';
// Без туннеля: если VITE_API_BASE_URL не задан, используем same-origin.
// Это важно для multi-tenant доменов (например arsenyev.moy-gorod.su), иначе
// fallback на другой tenant-домен приводит к CORS-блокировке и ERR_NETWORK на логине.
const API_BASE_URL = USE_TUNNEL_PROXY
  ? ''
  : ((import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim() || '');

export function getApiBaseUrl(): string {
  if (USE_TUNNEL_PROXY) {
    return typeof window !== 'undefined' ? window.location.origin : '';
  }
  if (API_BASE_URL) return API_BASE_URL;
  return typeof window !== 'undefined' ? window.location.origin : '';
}

export function buildApiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const base = getApiBaseUrl();
  if (!base) return normalizedPath;
  try {
    return new URL(normalizedPath, base.endsWith('/') ? base : `${base}/`).toString();
  } catch {
    return `${base}${normalizedPath}`;
  }
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let currentTenantTimezone = 'Asia/Vladivostok';

function updateTenantTimezoneFromHeaders(headers: Record<string, unknown> | undefined): void {
  if (!headers) return;
  const raw = headers['x-tenant-timezone'] ?? headers['X-Tenant-Timezone'];
  if (typeof raw !== 'string') return;
  const next = raw.trim();
  if (!next || next === currentTenantTimezone) return;
  currentTenantTimezone = next;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('tenant_timezone_changed', { detail: next }));
  }
}

export function getTenantTimezone(): string {
  return currentTenantTimezone;
}

type ProtectedDataRequestConfig = {
  mgProtectedDataScopeEpoch?: number;
  mgSuppressHttp5xxLog?: boolean;
};

class ProtectedDataScopeChangedError extends Error {
  readonly code = 'PROTECTED_DATA_SCOPE_CHANGED';

  constructor() {
    super('Protected data scope changed while the request was in flight');
    this.name = 'ProtectedDataScopeChangedError';
  }
}

function isStaleProtectedDataRequest(config: unknown): boolean {
  const requestEpoch = (config as ProtectedDataRequestConfig | null | undefined)?.mgProtectedDataScopeEpoch;
  return typeof requestEpoch === 'number' && requestEpoch !== protectedDataScopeEpoch;
}

// Interceptor для добавления токена
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  scheduleClientAuthExpiry(token);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const activeContour = readActiveContour();
  if (activeContour && isActiveContourHeaderEligible(config.url)) {
    config.headers['X-MoyGorod-Contour'] = activeContour;
  }
  const activeWorkspace = readActiveWorkspace();
  if (activeWorkspace && isActiveContourHeaderEligible(config.url)) {
    config.headers['X-MoyGorod-Workspace'] = activeWorkspace;
  }
  if (isActiveContourHeaderEligible(config.url)) {
    (config as typeof config & ProtectedDataRequestConfig).mgProtectedDataScopeEpoch = protectedDataScopeEpoch;
  }
  return config;
});

function isApiRequestUrl(url: string | undefined): boolean {
  const safeUrl = String(url || '').trim().toLowerCase();
  if (!safeUrl) return false;
  return safeUrl.includes('/api/');
}

function suppressAuthLogout(config: unknown): boolean {
  return Boolean((config as { mgSuppressAuthLogout?: boolean } | undefined)?.mgSuppressAuthLogout);
}

function suppressHttp5xxLog(config: unknown): boolean {
  return Boolean((config as ProtectedDataRequestConfig | undefined)?.mgSuppressHttp5xxLog);
}

function isLoginRequestUrl(url: string | undefined): boolean {
  const safeUrl = String(url || '').trim().toLowerCase();
  if (!safeUrl) return false;
  return safeUrl.includes('/api/users/login');
}

function getRequestBearerToken(config: unknown): string | null {
  const headers = (config as { headers?: Record<string, unknown> } | undefined)?.headers;
  const authorization = headers?.Authorization ?? headers?.authorization;
  if (typeof authorization !== 'string') return null;
  const match = authorization.match(/^Bearer\s+(.+)$/i);
  return match?.[1]?.trim() || null;
}

// Interceptor для обработки ошибок
api.interceptors.response.use(
  (response) => {
    if (isStaleProtectedDataRequest(response.config)) {
      return Promise.reject(new ProtectedDataScopeChangedError());
    }
    updateTenantTimezoneFromHeaders((response.headers ?? {}) as Record<string, unknown>);
    return response;
  },
  (error) => {
    if (isStaleProtectedDataRequest(error.config)) {
      return Promise.reject(new ProtectedDataScopeChangedError());
    }
    const status = Number(error.response?.status ?? 0);
    const traceId = getTraceIdFromError(error);
    const method = String(error.config?.method ?? 'get').toUpperCase();
    const safeUrl = sanitizeLogUrl(error.config?.url as string | undefined);
    if (status === 401 || status === 403) {
      const isAuthBound401 = status === 401;
      console.warn('[AUTH_HTTP_ERROR]', {
        method,
        url: safeUrl,
        status,
        trace_id: traceId,
        isAuthBound401,
      });
    } else if (status >= 500 && !suppressHttp5xxLog(error.config)) {
      console.error('[HTTP_5XX]', {
        method,
        url: safeUrl,
        status,
        trace_id: traceId,
      });
    }

    if (status === 401) {
      const token = getRequestBearerToken(error.config) ?? localStorage.getItem('access_token');
      const apiRequest = isApiRequestUrl(error.config?.url as string | undefined);
      const loginRequest = isLoginRequestUrl(error.config?.url as string | undefined);
      const interpreted = interpretUserFacingError(error, {
        context: loginRequest ? 'login' : 'action',
      });
      // В dev режиме с фейковым токеном не делаем редирект, чтобы видеть ошибки
      const isDevMode = token === 'dev-fake-token';
      const shouldLogoutForAuth401 =
        !loginRequest &&
        !suppressAuthLogout(error.config) &&
        apiRequest &&
        interpreted.needsRelogin;
      
      if (!isDevMode && shouldLogoutForAuth401) {
        forceClientAuthLogout('auth_401', { expectedToken: token, redirect: true });
      }
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: async (credentials: LoginCredentials, signal?: AbortSignal): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/api/users/login', credentials, { signal });
    return response.data;
  },

  register: async (payload: RegistrationPayload, signal?: AbortSignal): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/api/users/register', payload, { signal });
    return response.data;
  },

  logout: () => {
    forceClientAuthLogout('logout', { redirect: false });
  },

  getCurrentUser: async (signal?: AbortSignal): Promise<User> => {
    const response = await api.get<User>('/api/users/profile', { signal });
    return response.data;
  },

  getAccessPolicy: async (signal?: AbortSignal): Promise<FrontendAccessPolicy> => {
    const response = await api.get<FrontendAccessPolicy>(
      '/api/users/access_policy',
      { mgSuppressAuthLogout: true, signal } as Record<string, unknown>
    );
    return response.data;
  },

  getEntryContext: async (signal?: AbortSignal): Promise<PlatformEntryContext> => {
    const response = await api.get<PlatformEntryContext>(
      '/api/users/entry-context',
      { mgSuppressAuthLogout: true, signal } as Record<string, unknown>
    );
    return response.data;
  },

  createWorkspaceHandoff: async (
    workspaceKey: string,
    signal?: AbortSignal,
  ): Promise<{
    code: string;
    workspace: PlatformWorkspace;
    target_url: string;
    expires_in_seconds: number;
  }> => {
    const response = await api.post('/api/users/workspace-handoffs', { workspace_key: workspaceKey }, { signal });
    return response.data;
  },

  exchangeWorkspaceHandoff: async (
    code: string,
    signal?: AbortSignal,
  ): Promise<AuthResponse & { workspace: PlatformWorkspace; target_path: string }> => {
    const response = await api.post('/api/users/workspace-handoffs/exchange', { code }, {
      signal,
      mgSuppressAuthLogout: true,
    } as Record<string, unknown>);
    return response.data;
  },

  createAssistantHandoff: async (
    payload: {
      version: '1';
      workspace_key: string;
      active_contour: string | null;
      challenge: string;
    },
    signal?: AbortSignal,
  ): Promise<{
    version: '1';
    code: string;
    expires_in_seconds: number;
  }> => {
    const response = await api.post('/api/assistant/v1/handoffs', payload, { signal });
    return response.data;
  },
};

export type ClientDiagnosticsUploadResponse = {
  ok: boolean;
  received_at_utc: string;
  client_hash: string;
  file_name: string;
  payload_bytes: number;
};

export const clientDiagnosticsService = {
  upload: async (payload: Record<string, unknown>): Promise<ClientDiagnosticsUploadResponse> => {
    const response = await api.post<ClientDiagnosticsUploadResponse>('/api/ui/client-diagnostics', { payload });
    return response.data;
  },
};

export const helpdeskService = {
  searchDocs: async (query: string): Promise<HelpdeskDocsSearchResult[]> => {
    const response = await api.get<HelpdeskDocsSearchResult[]>('/api/helpdesk/docs/search', { params: { q: query } });
    return response.data;
  },
  getDoc: async (docId: string): Promise<HelpdeskDocDetail> => {
    const response = await api.get<HelpdeskDocDetail>(`/api/helpdesk/docs/${docId}`);
    return response.data;
  },
  createDraft: async (payload?: {
    topic?: string;
    category?: string;
    description?: string;
    source?: 'manual' | 'bug_report';
    metadata?: Record<string, unknown>;
  }): Promise<HelpdeskDraft> => {
    const response = await api.post<HelpdeskDraft>('/api/helpdesk/drafts', payload ?? {});
    return response.data;
  },
  getDraft: async (draftId: number): Promise<HelpdeskDraft> => {
    const response = await api.get<HelpdeskDraft>(`/api/helpdesk/drafts/${draftId}`);
    return response.data;
  },
  updateDraft: async (
    draftId: number,
    payload: {
      topic?: string | null;
      category?: string | null;
      description?: string | null;
      source?: 'manual' | 'bug_report' | null;
      status?: string | null;
      metadata?: Record<string, unknown> | null;
    }
  ): Promise<HelpdeskDraft> => {
    const response = await api.patch<HelpdeskDraft>(`/api/helpdesk/drafts/${draftId}`, payload);
    return response.data;
  },
  createAnchor: async (
    draftId: number,
    payload: {
      route: string;
      url: string;
      tenant?: string | null;
      component_name: string;
      element_id?: string | null;
      dom_path: string;
      display_name: string;
      element_type?: string | null;
      text_preview?: string | null;
      bbox?: Record<string, unknown>;
      viewport?: Record<string, unknown>;
      build_sha?: string | null;
      build_time_utc?: string | null;
      comment?: string | null;
      metadata?: Record<string, unknown>;
    }
  ): Promise<{ anchor: HelpdeskProblemAnchor; draft: HelpdeskDraft }> => {
    const response = await api.post<{ anchor: HelpdeskProblemAnchor; draft: HelpdeskDraft }>(
      `/api/helpdesk/drafts/${draftId}/anchors`,
      payload,
    );
    return response.data;
  },
  deleteAnchor: async (draftId: number, anchorId: number): Promise<HelpdeskDraft> => {
    const response = await api.delete<HelpdeskDraft>(`/api/helpdesk/drafts/${draftId}/anchors/${anchorId}`);
    return response.data;
  },
  analyzeDraft: async (draftId: number): Promise<HelpdeskAnalyzeResponse> => {
    const response = await api.post<HelpdeskAnalyzeResponse>('/api/helpdesk/ai/analyze-ticket', { draft_id: draftId });
    return response.data;
  },
  confirmSplit: async (
    draftId: number,
    payload: { tasks: Array<{ title: string; description: string; anchor_ids: number[] }> }
  ): Promise<HelpdeskSplitConfirmResponse> => {
    const response = await api.post<HelpdeskSplitConfirmResponse>(`/api/helpdesk/drafts/${draftId}/split-confirm`, payload);
    return response.data;
  },
  listThreads: async (params?: { scope?: 'mine' | 'all' }): Promise<HelpdeskThreadSummary[]> => {
    const response = await api.get<HelpdeskThreadSummary[]>('/api/helpdesk/threads', { params });
    return response.data;
  },

  getThread: async (threadId: number): Promise<HelpdeskThreadDetail> => {
    const response = await api.get<HelpdeskThreadDetail>(`/api/helpdesk/threads/${threadId}`);
    return response.data;
  },

  createThread: async (payload: {
    topic?: string;
    category?: string;
    message?: string;
    draft_id?: number;
    anchor_ids?: number[];
  }): Promise<HelpdeskThreadDetail> => {
    const response = await api.post<HelpdeskThreadDetail>('/api/helpdesk/threads', payload);
    return response.data;
  },

  submitQuickReport: async (payload: {
    draft_id: number;
    description: string;
  }): Promise<HelpdeskThreadDetail> => {
    const response = await api.post<HelpdeskThreadDetail>('/api/helpdesk/quick-report', payload);
    return response.data;
  },

  createBugReport: async (payload: {
    topic: string;
    description: string;
    steps_to_reproduce?: string | null;
    metadata?: Record<string, unknown>;
  }): Promise<HelpdeskThreadDetail> => {
    const response = await api.post<HelpdeskThreadDetail>('/api/helpdesk/bug-report', payload);
    return response.data;
  },

  addMessage: async (threadId: number, payload: { message: string }): Promise<HelpdeskThreadDetail> => {
    const response = await api.post<HelpdeskThreadDetail>(`/api/helpdesk/threads/${threadId}/messages`, payload);
    return response.data;
  },
  startAutopilot: async (
    threadId: number,
    payload?: {
      target_project?: string;
      planned_edits?: Array<{
        path: string;
        operation?: string;
        find?: string;
        replace?: string;
        content?: string;
        append?: boolean;
      }>;
      checks?: string[];
    }
  ): Promise<{ thread: HelpdeskThreadDetail; run: HelpdeskAutopilotRun }> => {
    const response = await api.post<{ thread: HelpdeskThreadDetail; run: HelpdeskAutopilotRun }>(
      `/api/helpdesk/threads/${threadId}/autopilot/start`,
      payload ?? {},
    );
    return response.data;
  },
  syncAutopilot: async (threadId: number): Promise<{ thread: HelpdeskThreadDetail; run: HelpdeskAutopilotRun }> => {
    const response = await api.post<{ thread: HelpdeskThreadDetail; run: HelpdeskAutopilotRun }>(
      `/api/helpdesk/threads/${threadId}/autopilot/sync`,
    );
    return response.data;
  },
  acceptAutopilot: async (
    runId: number,
    payload?: { reason?: string },
  ): Promise<{ run: HelpdeskAutopilotRun }> => {
    const response = await api.post<{ run: HelpdeskAutopilotRun }>(`/api/helpdesk/execution/${runId}/review/accept`, payload ?? {});
    return response.data;
  },
  rejectAutopilot: async (
    runId: number,
    payload?: { reason?: string },
  ): Promise<{ run: HelpdeskAutopilotRun }> => {
    const response = await api.post<{ run: HelpdeskAutopilotRun }>(`/api/helpdesk/execution/${runId}/review/reject`, payload ?? {});
    return response.data;
  },
  rerunAutopilot: async (
    runId: number,
    payload?: { reason?: string },
  ): Promise<{ run: HelpdeskAutopilotRun }> => {
    const response = await api.post<{ run: HelpdeskAutopilotRun }>(`/api/helpdesk/execution/${runId}/review/rerun`, payload ?? {});
    return response.data;
  },
  manualAutopilot: async (
    runId: number,
    payload?: { reason?: string },
  ): Promise<{ run: HelpdeskAutopilotRun }> => {
    const response = await api.post<{ run: HelpdeskAutopilotRun }>(`/api/helpdesk/execution/${runId}/review/manual`, payload ?? {});
    return response.data;
  },
  applyAutopilot: async (
    runId: number,
    payload?: { target_branch?: string },
  ): Promise<{ run: HelpdeskAutopilotRun }> => {
    const response = await api.post<{ run: HelpdeskAutopilotRun }>(`/api/helpdesk/execution/${runId}/apply`, payload ?? {});
    return response.data;
  },
  getAutopilotApplyStatus: async (runId: number): Promise<{ run: HelpdeskAutopilotRun }> => {
    const response = await api.get<{ run: HelpdeskAutopilotRun }>(`/api/helpdesk/execution/${runId}/apply-status`);
    return response.data;
  },
  getAutopilotReviewHistory: async (
    runId: number,
  ): Promise<{
    execution_run_id: number;
    review_history: HelpdeskAutopilotRun['review_history'];
    apply_receipts: HelpdeskAutopilotRun['apply_receipts'];
    post_apply_receipts: HelpdeskAutopilotRun['post_apply_receipts'];
  }> => {
    const response = await api.get(`/api/helpdesk/execution/${runId}/review-history`);
    return response.data;
  },
  getAutopilotArtifactContent: async (
    runId: number,
    artifactId: number,
  ): Promise<{ artifact_id: number; artifact_type: string; content: string }> => {
    const response = await api.get(`/api/helpdesk/execution/${runId}/artifacts/${artifactId}/content`);
    return response.data;
  },
  downloadAutopilotArtifact: async (runId: number, artifactId: number, filename?: string): Promise<void> => {
    const response = await api.get(`/api/helpdesk/execution/${runId}/artifacts/${artifactId}/download`, {
      responseType: 'blob',
    });
    downloadBlob(filename || `helpdesk_artifact_${artifactId}`, response.data);
  },
  listMasterCases: async (): Promise<HelpdeskMasterCase[]> => {
    const response = await api.get<HelpdeskMasterCase[]>('/api/helpdesk/master-cases');
    return response.data;
  },
  getMasterCase: async (masterCaseId: number): Promise<HelpdeskMasterCase> => {
    const response = await api.get<HelpdeskMasterCase>(`/api/helpdesk/master-cases/${masterCaseId}`);
    return response.data;
  },
  evaluateDedup: async (threadId: number): Promise<HelpdeskDedupEvaluationResponse> => {
    const response = await api.post<HelpdeskDedupEvaluationResponse>(`/api/helpdesk/threads/${threadId}/dedup-evaluate`);
    return response.data;
  },
  linkMasterCase: async (threadId: number, masterCaseId: number): Promise<HelpdeskThreadDetail> => {
    const response = await api.post<HelpdeskThreadDetail>(`/api/helpdesk/threads/${threadId}/link-master-case`, {
      master_case_id: masterCaseId,
    });
    return response.data;
  },
  unlinkMasterCase: async (threadId: number): Promise<HelpdeskThreadDetail> => {
    const response = await api.post<HelpdeskThreadDetail>(`/api/helpdesk/threads/${threadId}/unlink-master-case`);
    return response.data;
  },
  resolveMasterCaseLinked: async (
    masterCaseId: number,
    payload: { resolution_summary: string; resolution_ref?: string | null },
  ): Promise<HelpdeskMasterCase> => {
    const response = await api.post<HelpdeskMasterCase>(`/api/helpdesk/master-cases/${masterCaseId}/resolve-linked`, payload);
    return response.data;
  },
  broadcastMasterCaseResolution: async (
    masterCaseId: number,
    payload: { message: string },
  ): Promise<HelpdeskMasterCase> => {
    const response = await api.post<HelpdeskMasterCase>(`/api/helpdesk/master-cases/${masterCaseId}/broadcast-resolution`, payload);
    return response.data;
  },
};

export const systemTasksService = {
  list: async (params?: {
    tenant_id?: string;
    state?: string;
    origin_kind?: string;
    launch_mode?: string;
    special_launch?: boolean | null;
  }): Promise<SystemTasksListResponse> => {
    const response = await api.get<SystemTasksListResponse>('/api/system-tasks', {
      params: {
        ...params,
        special_launch:
          typeof params?.special_launch === 'boolean'
            ? params.special_launch ? 'true' : 'false'
            : undefined,
      },
    });
    return response.data;
  },
  get: async (id: number): Promise<SystemTask> => {
    const response = await api.get<SystemTask>(`/api/system-tasks/${id}`);
    return response.data;
  },
  create: async (payload: SystemTaskCreatePayload): Promise<SystemTask> => {
    const response = await api.post<SystemTask>('/api/system-tasks', payload);
    return response.data;
  },
  launch: async (id: number): Promise<SystemTask> => {
    const response = await api.post<SystemTask>(`/api/system-tasks/${id}/launch`);
    return response.data;
  },
  sync: async (id: number): Promise<SystemTask> => {
    const response = await api.post<SystemTask>(`/api/system-tasks/${id}/sync`);
    return response.data;
  },
  startRun: async (id: number): Promise<SystemTask> => {
    const response = await api.post<SystemTask>(`/api/system-tasks/${id}/start-run`);
    return response.data;
  },
  completeRun: async (id: number, payload: SystemTaskCompleteRunPayload): Promise<SystemTask> => {
    const response = await api.post<SystemTask>(`/api/system-tasks/${id}/complete-run`, payload);
    return response.data;
  },
  sendFollowup: async (id: number, payload?: SystemTaskSendFollowupPayload): Promise<SystemTask> => {
    const response = await api.post<SystemTask>(`/api/system-tasks/${id}/send-followup`, payload ?? {});
    return response.data;
  },
  artifacts: async (id: number): Promise<SystemTaskArtifactsResponse> => {
    const response = await api.get<SystemTaskArtifactsResponse>(`/api/system-tasks/${id}/artifacts`);
    return response.data;
  },
};

export const helpdeskContractService = {
  list: async (): Promise<{ items: HelpdeskContractCase[]; tenant_scope_ids: string[] }> => {
    const response = await api.get<{ items: HelpdeskContractCase[]; tenant_scope_ids: string[] }>('/api/helpdesk/contracts');
    return response.data;
  },
  upload: async (file: File): Promise<HelpdeskContractUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<HelpdeskContractUploadResponse>('/api/helpdesk/contracts/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  create: async (payload: HelpdeskContractCaseCreatePayload): Promise<HelpdeskContractCase> => {
    const response = await api.post<HelpdeskContractCase>('/api/helpdesk/contracts', payload);
    return response.data;
  },
  get: async (caseId: number): Promise<HelpdeskContractCase> => {
    const response = await api.get<HelpdeskContractCase>(`/api/helpdesk/contracts/${caseId}`);
    return response.data;
  },
  start: async (caseId: number): Promise<HelpdeskContractCase> => {
    const response = await api.post<HelpdeskContractCase>(`/api/helpdesk/contracts/${caseId}/start`);
    return response.data;
  },
  getRun: async (caseId: number, runId: number): Promise<HelpdeskContractRun> => {
    const response = await api.get<HelpdeskContractRun>(`/api/helpdesk/contracts/${caseId}/runs/${runId}`);
    return response.data;
  },
  syncRun: async (caseId: number, runId: number): Promise<HelpdeskContractRun> => {
    const response = await api.post<HelpdeskContractRun>(`/api/helpdesk/contracts/${caseId}/runs/${runId}/sync`);
    return response.data;
  },
  startRun: async (caseId: number, runId: number): Promise<HelpdeskContractRun> => {
    const response = await api.post<HelpdeskContractRun>(`/api/helpdesk/contracts/${caseId}/runs/${runId}/start-run`);
    return response.data;
  },
  completeRun: async (caseId: number, runId: number, payload: HelpdeskContractRunCompletePayload): Promise<HelpdeskContractRun> => {
    const response = await api.post<HelpdeskContractRun>(`/api/helpdesk/contracts/${caseId}/runs/${runId}/complete-run`, payload);
    return response.data;
  },
  sendFollowup: async (caseId: number, runId: number, payload?: HelpdeskContractRunFollowupPayload): Promise<HelpdeskContractRun> => {
    const response = await api.post<HelpdeskContractRun>(`/api/helpdesk/contracts/${caseId}/runs/${runId}/send-followup`, payload ?? {});
    return response.data;
  },
  captureBrowserSnapshot: async (caseId: number, runId: number): Promise<{ item: Record<string, unknown> }> => {
    const response = await api.post<{ item: Record<string, unknown> }>(`/api/helpdesk/contracts/${caseId}/runs/${runId}/snapshot/browser`);
    return response.data;
  },
  captureScreenSnapshot: async (caseId: number, runId: number): Promise<{ item: Record<string, unknown> }> => {
    const response = await api.post<{ item: Record<string, unknown> }>(`/api/helpdesk/contracts/${caseId}/runs/${runId}/snapshot/screen`);
    return response.data;
  },
  cancelRun: async (caseId: number, runId: number): Promise<HelpdeskContractRun> => {
    const response = await api.post<HelpdeskContractRun>(`/api/helpdesk/contracts/${caseId}/runs/${runId}/cancel`);
    return response.data;
  },
  restartTurn: async (caseId: number, runId: number): Promise<HelpdeskContractRun> => {
    const response = await api.post<HelpdeskContractRun>(`/api/helpdesk/contracts/${caseId}/runs/${runId}/restart-turn`);
    return response.data;
  },
  forceRotateScreen: async (caseId: number, runId: number): Promise<HelpdeskContractRun> => {
    const response = await api.post<HelpdeskContractRun>(`/api/helpdesk/contracts/${caseId}/runs/${runId}/force-rotate-screen`);
    return response.data;
  },
  artifacts: async (caseId: number): Promise<{ items: SystemTaskArtifact[]; snapshots: Array<Record<string, unknown>> }> => {
    const response = await api.get<{ items: SystemTaskArtifact[]; snapshots: Array<Record<string, unknown>> }>(`/api/helpdesk/contracts/${caseId}/artifacts`);
    return response.data;
  },
};

export const referenceService = {
  peekStatuses: (): StatusRef[] | null => getFreshReferenceCache<StatusRef[]>(sharedKey('ref:statuses')),
  peekDepartments: (params?: { include_archived?: boolean; include_hidden?: boolean }): DepartmentRef[] | null =>
    getFreshReferenceCache<DepartmentRef[]>(tenantSharedKey('ref:departments', params ?? {})),
  peekTopics: (params?: { include_archived?: boolean; include_hidden?: boolean; department_id?: number }): TopicRef[] | null =>
    getFreshTimedCache(referenceCache as Map<string, TimedCacheEntry<TopicRef[]>>, tenantSharedKey('ref:topics', params ?? {}), REFERENCE_CACHE_TTL_MS),
  peekLocalities: (): LocalityRef[] | null => getFreshReferenceCache<LocalityRef[]>(tenantSharedKey('ref:localities')),
  peekTerritorialDepartments: (): TerritorialDepartmentRef[] | null =>
    getFreshReferenceCache<TerritorialDepartmentRef[]>(tenantSharedKey('ref:territorial_departments')),
  peekDistricts: (): DistrictRef[] | null =>
    getFreshReferenceCache<DistrictRef[]>(tenantSharedKey('ref:districts')),
  peekManagementCompanies: (): ManagementCompanyRef[] | null =>
    getFreshReferenceCache<ManagementCompanyRef[]>(tenantSharedKey('ref:management_companies')),
  peekUsers: (): User[] | null => getFreshReferenceCache<User[]>(tenantSharedKey('ref:users')),
  getStatuses: async (): Promise<StatusRef[]> => {
    const key = sharedKey('ref:statuses');
    const cached = getFreshReferenceCache<StatusRef[]>(key);
    if (cached) return cached;
    return withSharedRequest(key, async () => {
      const response = await api.get<StatusRef[]>('/api/statuses');
      return setReferenceCache<StatusRef[]>(key, response.data);
    });
  },
  getDepartments: async (params?: { include_archived?: boolean; include_hidden?: boolean }): Promise<DepartmentRef[]> => {
    const key = tenantSharedKey('ref:departments', params ?? {});
    const cached = getFreshReferenceCache<DepartmentRef[]>(key);
    if (cached) return cached;
    return withSharedRequest(key, async () => {
      const response = await api.get<DepartmentRef[]>('/api/departments', { params });
      return setReferenceCache<DepartmentRef[]>(key, response.data);
    });
  },
  getTopics: async (params?: { include_archived?: boolean; include_hidden?: boolean; department_id?: number }): Promise<TopicRef[]> => {
    const key = tenantSharedKey('ref:topics', params ?? {});
    const cached = getFreshTimedCache(referenceCache as Map<string, TimedCacheEntry<TopicRef[]>>, key, REFERENCE_CACHE_TTL_MS);
    if (cached) return cached;
    return withSharedRequest(key, async () => {
      const response = await api.get<TopicRef[]>('/api/topics', { params });
      return setTimedCache(referenceCache as Map<string, TimedCacheEntry<TopicRef[]>>, key, response.data);
    });
  },
  getTopicIntersections: async (params?: {
    min_similarity?: number;
    limit?: number;
    include_archived?: boolean;
    include_hidden?: boolean;
  }): Promise<{
    min_similarity: number;
    count: number;
    items: Array<{
      similarity: number;
      topic_a: { topic_id: number; topic_name: string; department_id: number; department_name: string };
      topic_b: { topic_id: number; topic_name: string; department_id: number; department_name: string };
      overlap_tokens: string[];
    }>;
  }> => {
    const response = await api.get('/api/topics/intersections', { params });
    return response.data;
  },
  runTaxonomyAudit: async (params?: {
    force?: boolean;
    cache_ttl_min?: number;
    include_archived?: boolean;
    include_hidden?: boolean;
  }): Promise<{
    status: string;
    cached: boolean;
    tenant_id: string;
    input_hash: string;
    result: {
      summary: string;
      issues: Array<{
        severity: 'low' | 'medium' | 'high';
        scope: 'department' | 'topic' | 'cross_department';
        department_id: number | null;
        topic_id: number | null;
        title: string;
        detail: string;
        suggestion: string;
      }>;
      _meta?: Record<string, unknown>;
      __llm_mode?: string;
      reason?: string;
      error?: string;
    };
  }> => {
    const response = await api.post('/api/taxonomy/audit', null, { params });
    return response.data;
  },
  createDepartment: async (payload: { name: string; description: string; hidden?: boolean; archived?: boolean }): Promise<DepartmentRef> => {
    const response = await api.post<DepartmentRef>('/api/departments', payload);
    invalidateTenantReferenceCache('ref:departments');
    return response.data;
  },
  updateDepartment: async (id: number, payload: { name?: string; description?: string; hidden?: boolean; archived?: boolean }): Promise<DepartmentRef> => {
    const response = await api.put<DepartmentRef>(`/api/departments/${id}`, payload);
    invalidateTenantReferenceCache('ref:departments');
    return response.data;
  },
  deleteDepartment: async (id: number): Promise<DepartmentRef> => {
    const response = await api.delete<DepartmentRef>(`/api/departments/${id}`);
    invalidateTenantReferenceCache('ref:departments');
    return response.data;
  },
  createTopic: async (payload: { name: string; description: string; topicable_type: string; topicable_id: number; hidden?: boolean; archived?: boolean }): Promise<TopicRef> => {
    const response = await api.post<TopicRef>('/api/topics', payload);
    invalidateTenantReferenceCache('ref:topics');
    return response.data;
  },
  updateTopic: async (
    id: number,
    payload: { name?: string; description?: string; topicable_type?: string; topicable_id?: number; hidden?: boolean; archived?: boolean },
  ): Promise<TopicRef> => {
    const response = await api.put<TopicRef>(`/api/topics/${id}`, payload);
    invalidateTenantReferenceCache('ref:topics');
    return response.data;
  },
  deleteTopic: async (id: number): Promise<TopicRef> => {
    const response = await api.delete<TopicRef>(`/api/topics/${id}`);
    invalidateTenantReferenceCache('ref:topics');
    return response.data;
  },
  filterTopics: async (payload: {
    filter: Record<string, unknown>;
    records_per_page?: number;
    current_page?: number;
  }): Promise<{ topics: TopicRef[]; total_records?: number }> => {
    const body = { filter: payload.filter ?? {}, records_per_page: payload.records_per_page ?? 100, current_page: payload.current_page ?? 1 };
    const key = tenantSharedKey('ref:topics/filter', body);
    const cached = getFreshTimedCache(filteredTopicsCache, key, FILTERED_TOPICS_CACHE_TTL_MS);
    if (cached) return cached;
    return withSharedRequest(key, async () => {
      const response = await api.post<{ topics: TopicRef[]; total_records?: number }>('/api/topics/filter', body);
      return setTimedCache(filteredTopicsCache, key, response.data);
    });
  },
  getLocalities: async (): Promise<LocalityRef[]> => {
    const key = tenantSharedKey('ref:localities');
    const cached = getFreshReferenceCache<LocalityRef[]>(key);
    if (cached) return cached;
    return withSharedRequest(key, async () => {
      const response = await api.get<LocalityRef[]>('/api/localities');
      return setReferenceCache<LocalityRef[]>(key, response.data);
    });
  },
  getStreets: async (): Promise<StreetRef[]> => {
    const response = await api.get<StreetRef[]>('/api/streets');
    return response.data;
  },
  getTerritorialDepartments: async (): Promise<TerritorialDepartmentRef[]> => {
    const key = tenantSharedKey('ref:territorial_departments');
    const cached = getFreshReferenceCache<TerritorialDepartmentRef[]>(key);
    if (cached) return cached;
    return withSharedRequest(key, async () => {
      const response = await api.get<TerritorialDepartmentRef[]>('/api/territorial_departments');
      return setReferenceCache<TerritorialDepartmentRef[]>(key, response.data);
    });
  },
  createTerritorialDepartment: async (payload: {
    name: string;
    streets_ids?: number[];
  }): Promise<TerritorialDepartmentRef> => {
    const response = await api.post<TerritorialDepartmentRef>('/api/territorial_departments', payload);
    invalidateTenantReferenceCache('ref:territorial_departments');
    return response.data;
  },
  updateTerritorialDepartment: async (
    id: number,
    payload: {
      name?: string;
      streets_ids?: number[];
    },
  ): Promise<TerritorialDepartmentRef> => {
    const response = await api.patch<TerritorialDepartmentRef>(`/api/territorial_departments/${id}`, payload);
    invalidateTenantReferenceCache('ref:territorial_departments');
    return response.data;
  },
  deleteTerritorialDepartment: async (id: number): Promise<{ id: number }> => {
    const response = await api.delete<{ id: number }>(`/api/territorial_departments/${id}`);
    invalidateTenantReferenceCache('ref:territorial_departments');
    return response.data;
  },
  getDistricts: async (): Promise<DistrictRef[]> => {
    const key = tenantSharedKey('ref:districts');
    const cached = getFreshReferenceCache<DistrictRef[]>(key);
    if (cached) return cached;
    return withSharedRequest(key, async () => {
      const response = await api.get<DistrictRef[]>('/api/districts');
      return setReferenceCache<DistrictRef[]>(key, response.data);
    });
  },
  createDistrict: async (payload: { name: string; locality_id: number; buildings_ids?: number[] }): Promise<DistrictRef> => {
    const response = await api.post<DistrictRef>('/api/districts', payload);
    invalidateTenantReferenceCache('ref:districts');
    return response.data;
  },
  updateDistrict: async (
    id: number,
    payload: { name?: string; locality_id?: number; buildings_ids?: number[] },
  ): Promise<DistrictRef> => {
    const response = await api.patch<DistrictRef>(`/api/districts/${id}`, payload);
    invalidateTenantReferenceCache('ref:districts');
    return response.data;
  },
  deleteDistrict: async (id: number): Promise<{ id: number }> => {
    const response = await api.delete<{ id: number }>(`/api/districts/${id}`);
    invalidateTenantReferenceCache('ref:districts');
    return response.data;
  },
  getManagementCompanies: async (): Promise<ManagementCompanyRef[]> => {
    const key = tenantSharedKey('ref:management_companies');
    const cached = getFreshReferenceCache<ManagementCompanyRef[]>(key);
    if (cached) return cached;
    return withSharedRequest(key, async () => {
      const response = await api.get<ManagementCompanyRef[]>('/api/management_companies');
      return setReferenceCache<ManagementCompanyRef[]>(key, response.data);
    });
  },
  createManagementCompany: async (payload: { name: string }): Promise<ManagementCompanyRef> => {
    const response = await api.post<ManagementCompanyRef>('/api/management_companies', payload);
    invalidateTenantReferenceCache('ref:management_companies');
    return response.data;
  },
  updateManagementCompany: async (id: number, payload: { name: string }): Promise<ManagementCompanyRef> => {
    const response = await api.patch<ManagementCompanyRef>(`/api/management_companies/${id}`, payload);
    invalidateTenantReferenceCache('ref:management_companies');
    return response.data;
  },
  deleteManagementCompany: async (id: number): Promise<{ id: number }> => {
    const response = await api.delete<{ id: number }>(`/api/management_companies/${id}`);
    invalidateTenantReferenceCache('ref:management_companies');
    return response.data;
  },
  getUsers: async (): Promise<User[]> => {
    const key = tenantSharedKey('ref:users');
    const cached = getFreshReferenceCache<User[]>(key);
    if (cached) return cached;
    return withSharedRequest(key, async () => {
      const response = await api.get<User[]>('/api/users');
      return setReferenceCache<User[]>(key, response.data);
    });
  },
  filterStreets: async (payload: {
    filter: Record<string, unknown>;
    records_per_page?: number;
    current_page?: number;
  }, options?: { strictLocality?: boolean }): Promise<{ streets: StreetRef[]; total_records?: number; total_pages?: number; current_page?: number }> => {
    const body = { filter: payload.filter ?? {}, records_per_page: payload.records_per_page ?? 50, current_page: payload.current_page ?? 1 };
    try {
      const response = await api.post<{ streets: StreetRef[]; total_records?: number }>('/api/streets/filter', body);
      return response.data;
    } catch (error) {
      const err = error as AxiosError<{ detail?: unknown }>;
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const isLocalityFilterError =
        status === 400 &&
        typeof detail === 'object' &&
        detail != null &&
        (detail as { attribute?: unknown }).attribute === 'locality_id' &&
        Object.prototype.hasOwnProperty.call(body.filter, 'locality_id');
      if (!isLocalityFilterError || options?.strictLocality) throw error;
      const fallbackFilter = { ...body.filter };
      delete (fallbackFilter as Record<string, unknown>).locality_id;
      const fallbackResponse = await api.post<{ streets: StreetRef[]; total_records?: number }>('/api/streets/filter', {
        ...body,
        filter: fallbackFilter,
      });
      return fallbackResponse.data;
    }
  },
  filterBuildings: async (payload: {
    filter: Record<string, unknown>;
    records_per_page?: number;
    current_page?: number;
  }): Promise<{ buildings: BuildingRef[]; total_records?: number }> => {
    const body = { filter: payload.filter ?? {}, records_per_page: payload.records_per_page ?? 50, current_page: payload.current_page ?? 1 };
    const response = await api.post<{ buildings: BuildingRef[]; total_records?: number }>('/api/buildings/filter', body);
    return response.data;
  },
};

export type MenuVisibilityByRole = {
  roles: Record<string, string[]>;
  updated_at?: string | null;
};

export const uiSettingsService = {
  peekMenuVisibility: (): MenuVisibilityByRole | null => {
    const key = sharedKey('ui:menu_visibility');
    return getFreshTimedCache(menuVisibilityCache, key, UI_CACHE_TTL_MS);
  },
  getMenuVisibility: async (): Promise<MenuVisibilityByRole> => {
    const key = sharedKey('ui:menu_visibility');
    const cached = getFreshTimedCache(menuVisibilityCache, key, UI_CACHE_TTL_MS);
    if (cached) return cached;
    return withSharedRequest(key, async () => {
      const response = await api.get<MenuVisibilityByRole>('/api/ui/menu-visibility');
      return setTimedCache(menuVisibilityCache, key, response.data);
    });
  },
  updateMenuVisibility: async (payload: { roles: Record<string, string[]> }): Promise<MenuVisibilityByRole> => {
    const response = await api.put<MenuVisibilityByRole>('/api/ui/menu-visibility', payload);
    setTimedCache(menuVisibilityCache, sharedKey('ui:menu_visibility'), response.data);
    return response.data;
  },
};

export const tenantSettingsService = {
  get: async (): Promise<TenantSettings> => {
    const response = await api.get<TenantSettings>('/api/tenant/settings');
    return response.data;
  },
  update: async (payload: {
    history_visibility_mode?: HistoryVisibilityMode;
    ai_processing_disabled?: boolean;
    spam_retention_enabled?: boolean;
    spam_retention_ttl_hours?: number;
    launch_enabled?: boolean;
    source_mode?: 'mtproto_only' | 'webhook' | string;
    no_group_bot_mode?: boolean;
    historical_processing_mode?: 'case_pipeline_only' | 'webhook_full' | string;
    moderation_policy?: 'historical_no_full_moderation' | 'full_moderation' | string;
    outbound_policy?: 'bootstrap_blocked' | 'operator_approved' | 'enabled' | string;
    hourly_sync_enabled?: boolean;
    sync_schedule_policy?: 'previous_full_hour' | 'disabled' | string;
    worker_concurrency_cap?: number;
    dictionaries_ready?: boolean;
    processing_hold_enabled?: boolean;
    processing_hold_reason?: string;
    bootstrap_lifecycle_state?: string;
    operational_readiness_state?: string;
    source_mode_transition_confirm_tenant_id?: string;
  }): Promise<TenantSettings> => {
    const response = await api.put<TenantSettings>('/api/tenant/settings', payload);
    return response.data;
  },
  getSourceModeStatus: async (): Promise<NonNullable<TenantSettings['source_mode_status']>> => {
    const response = await api.get<NonNullable<TenantSettings['source_mode_status']>>('/api/tenant/source-mode/status');
    return response.data;
  },
  cutoverToWebhook: async (payload: { confirm_tenant_id: string }): Promise<TenantSettings> => {
    const response = await api.post<TenantSettings>('/api/tenant/source-mode/cutover-to-webhook', payload);
    return response.data;
  },
  getSpamRetentionPreview: async (): Promise<NonNullable<TenantSettings['spam_retention']>> => {
    const response = await api.get<NonNullable<TenantSettings['spam_retention']>>('/api/tenant/settings/spam-retention/preview');
    return response.data;
  },
  purgeAllSpam: async (payload: { confirm_tenant_id: string }): Promise<{
    status: string;
    preview: NonNullable<NonNullable<TenantSettings['spam_retention']>['preview']>;
    result: {
      preview_count: number;
      deleted_messages: number;
    };
  }> => {
    const response = await api.post('/api/tenant/settings/spam-retention/purge', payload);
    return response.data;
  },
  purgeExpiredSpam: async (payload: { confirm_tenant_id: string }): Promise<{
    status: string;
    preview: NonNullable<NonNullable<TenantSettings['spam_retention']>['preview']>;
    result: {
      preview_count: number;
      deleted_messages: number;
    };
  }> => {
    const response = await api.post('/api/tenant/settings/spam-retention/purge-expired', payload);
    return response.data;
  },
  createMaxBot: async (payload: {
    name?: string | null;
    bot_token: string;
  }): Promise<TenantSettings> => {
    const response = await api.post<TenantSettings>('/api/tenant/settings/max/bots', payload);
    return response.data;
  },
  updateMaxBot: async (
    botId: number,
    payload: {
      name?: string | null;
      bot_token?: string | null;
      is_active?: boolean;
    }
  ): Promise<TenantSettings> => {
    const response = await api.patch<TenantSettings>(`/api/tenant/settings/max/bots/${botId}`, payload);
    return response.data;
  },
  revalidateMaxBot: async (botId: number): Promise<TenantSettings> => {
    const response = await api.post<TenantSettings>(`/api/tenant/settings/max/bots/${botId}/revalidate`);
    return response.data;
  },
  createMaxTarget: async (payload: {
    user_defined_name?: string | null;
    target_external_id: number;
    selected_bot_id: number;
  }): Promise<TenantSettings> => {
    const response = await api.post<TenantSettings>('/api/tenant/settings/max/targets', payload);
    return response.data;
  },
  updateMaxTarget: async (
    targetId: number,
    payload: {
      user_defined_name?: string | null;
      selected_bot_id?: number;
      is_active?: boolean;
    }
  ): Promise<TenantSettings> => {
    const response = await api.patch<TenantSettings>(`/api/tenant/settings/max/targets/${targetId}`, payload);
    return response.data;
  },
  revalidateMaxTarget: async (targetId: number): Promise<TenantSettings> => {
    const response = await api.post<TenantSettings>(`/api/tenant/settings/max/targets/${targetId}/revalidate`);
    return response.data;
  },
};

export const civicIntegrationService = {
  listConnections: async (): Promise<ExternalIntegrationConnection[]> => {
    const response = await api.get<{ items: ExternalIntegrationConnection[] }>('/api/integrations');
    return response.data.items;
  },
  createConnection: async (payload: {
    provider?: string;
    name: string;
    base_url: string;
    username: string;
    password: string;
    enabled?: boolean;
    config?: Record<string, unknown>;
  }): Promise<ExternalIntegrationConnection> => {
    const response = await api.post<ExternalIntegrationConnection>('/api/integrations', payload);
    return response.data;
  },
  updateConnection: async (
    id: number,
    payload: Partial<{
      name: string;
      base_url: string;
      username: string;
      password: string;
      enabled: boolean;
      config: Record<string, unknown>;
    }>,
  ): Promise<ExternalIntegrationConnection> => {
    const response = await api.patch<ExternalIntegrationConnection>(`/api/integrations/${id}`, payload);
    return response.data;
  },
  validateConnection: async (id: number): Promise<ExternalIntegrationConnection> => {
    const response = await api.post<ExternalIntegrationConnection>(`/api/integrations/${id}/validate`);
    return response.data;
  },
  syncContainerAreas: async (id: number, syncRunId?: string): Promise<{
    connection_id: number;
    sync_run_id: string;
    pages_read: number;
    rows_observed: number;
    rows_changed: number;
    rows_expired: number;
    total_elements: number;
    complete: boolean;
    next_page?: number | null;
  }> => {
    const response = await api.post(`/api/integrations/${id}/sync-container-areas`, {
      page_size: 100,
      max_pages: 5,
      sync_run_id: syncRunId,
    });
    return response.data;
  },
  listContainerAreas: async (id: number, search?: string): Promise<IntegrationContainerArea[]> => {
    const response = await api.get<{ items: IntegrationContainerArea[] }>(`/api/integrations/${id}/container-areas`, {
      params: { search: search || undefined, limit: 200 },
    });
    return response.data.items;
  },
  getPolicy: async (verticalCode = 'waste_removal'): Promise<CivicAutoReplyPolicy> => {
    const response = await api.get<CivicAutoReplyPolicy>(`/api/civic-responses/policies/${verticalCode}`);
    return response.data;
  },
  updatePolicy: async (
    verticalCode: string,
    payload: Partial<Omit<CivicAutoReplyPolicy, 'id' | 'tenant_id' | 'vertical_code' | 'revision' | 'setting_revisions' | 'created_at' | 'updated_at'>> & {
      expected_revision: number;
      expected_setting_revisions: Record<string, number>;
    },
  ): Promise<CivicAutoReplyPolicy> => {
    const response = await api.put<CivicAutoReplyPolicy>(`/api/civic-responses/policies/${verticalCode}`, payload);
    return response.data;
  },
  listBindings: async (verticalCode = 'waste_removal'): Promise<CivicSourceBinding[]> => {
    const response = await api.get<{ items: CivicSourceBinding[] }>('/api/civic-responses/source-bindings', {
      params: { vertical_code: verticalCode },
    });
    return response.data.items;
  },
  createBinding: async (payload: {
    vertical_code: string;
    connection_id: number;
    source_type: string;
    source_channel_id: number;
    locality_id: number;
    district_id?: number;
    building_ids?: number[];
    address_text?: string;
    external_area_ids: string[];
    confidence: number;
    enabled: boolean;
    location_config?: Record<string, unknown>;
  }): Promise<CivicSourceBinding> => {
    const response = await api.post<CivicSourceBinding>('/api/civic-responses/source-bindings', payload);
    return response.data;
  },
  deleteBinding: async (id: number): Promise<void> => {
    await api.delete(`/api/civic-responses/source-bindings/${id}`);
  },
  listCases: async (verticalCode = 'waste_removal'): Promise<CivicResponseCase[]> => {
    const response = await api.get<{ items: CivicResponseCase[] }>('/api/civic-responses/cases', {
      params: { vertical_code: verticalCode, review_only: true, limit: 100 },
    });
    return response.data.items;
  },
  updateCaseDraft: async (id: number, draftText: string): Promise<CivicResponseCase> => {
    const response = await api.patch<CivicResponseCase>(`/api/civic-responses/cases/${id}`, { draft_text: draftText });
    return response.data;
  },
};

export const dossierService = {
  getTelegramUserDossier: async (
    id: number,
    params?: {
      tickets_page?: number;
      tickets_per_page?: number;
      messages_page?: number;
      messages_per_page?: number;
      phone_sample_per_page?: number;
    },
  ): Promise<TelegramUserDossier> => {
    const response = await api.get<TelegramUserDossier>(`/api/dossier/telegram_user/${id}`, { params });
    return response.data;
  },
};

export interface TicketMessageItem {
  id: number;
  source_type?: string | null;
  source_channel_id?: number | null;
  source_channel_id_text?: string | null;
  source_channel_title?: string | null;
  source_channel_username?: string | null;
  source_display_label?: string | null;
  source_kind?: string | null;
  direct_messages_topic_id?: number | null;
  conversation_root_key?: string | null;
  source_context?: string | null;
  max_context?: string | null;
  platform_hidden?: boolean | null;
  author_name?: string | null;
  chat_operator?: boolean | null;
  private_chat?: boolean | null;
  /** OPERATOR | ONE_OF_US | RESIDENT — для цвета по роли в настройках шрифтов */
  author_role?: string | null;
  content?: string | null;
  caption?: string | null;
  summary_text?: string | null;
  keywords?: string[] | null;
  media_urls?: string[] | null;
  media_thumbs?: string[] | null;
  ticket_media?: Array<{ file_id: string }> | null;
  channel_message_id?: number | null;
  channel_message_id_text?: string | null;
  channel_reply_message_id?: number | null;
  channel_reply_message_id_text?: string | null;
  created_at?: string | null;
  source_created_at?: string | null;
  reply_preview_author_name?: string | null;
  reply_preview_text?: string | null;
  [key: string]: unknown;
}

export interface TicketHistoryMessagePreview {
  message_id: number;
  author_label: string;
  text: string;
  source_label?: string | null;
  occurred_at?: string | null;
}

export interface TicketHistoryEvent {
  id: string;
  occurred_at?: string | null;
  title: string;
  actor_label: string;
  source_label: string;
  summary: string;
  reason_label: string;
  details: string[];
  message_previews: TicketHistoryMessagePreview[];
  severity?: 'neutral' | 'warning' | 'success' | 'info' | string;
  technical_details?: Record<string, unknown>;
}

export interface TicketHistoryResponse {
  ticket_id: number;
  title: string;
  events: TicketHistoryEvent[];
}

export interface TicketSplitDuplicatePrepareResponse {
  source_ticket: Ticket;
  candidate_ticket: Ticket & { draft?: boolean; source_ticket_id?: number | null };
  messages: TicketMessageItem[];
  anchor_message_id?: number | null;
  warnings?: Array<{
    code: string;
    message: string;
    message_ids?: number[];
  }>;
  contract?: {
    assignment_mode?: string;
    requires_atomic_apply?: boolean;
    empty_duplicate_allowed?: boolean;
    empty_source_allowed?: boolean;
    prepare_endpoint?: string;
    apply_endpoint?: string;
  };
}

export interface TicketSplitDuplicateSidePayload {
  status_id?: number | null;
  department_id?: number | null;
  organization_id?: number | null;
  topic_id?: number | null;
  locality_id?: number | null;
  district_id?: number | null;
  territorial_department_id?: number | null;
  street_id?: number | null;
  building_id?: number | null;
  apartment_number?: string | null;
  management_company_id?: number | null;
  responsible_user_id?: number | null;
  notes?: string | null;
  deadline_at?: string | null;
  address_raw?: string | null;
  address_norm?: string | null;
  address_source?: 'ref' | 'sql' | null;
  keywords?: string[] | null;
  source_instance?: string | null;
  lock_version?: number;
}

export interface TicketSplitDuplicateApplyResponse {
  source_ticket: Ticket;
  duplicate_ticket: Ticket;
  moved_message_ids: number[];
  copied_message_ids: number[];
  kept_message_ids: number[];
  anchor_message_id?: number | null;
  audit?: {
    source_ticket_id: number;
    duplicate_ticket_id: number;
    action: string;
  };
}

export interface TicketRealtimeEvent {
  type: 'ticket.created' | 'ticket.updated' | 'ticket.deleted';
  tenant_id?: string | null;
  ticket_id: number;
  ticket_no?: number | null;
  updated_at?: string | null;
  changed_fields?: string[] | null;
  event_id?: string | null;
  raw?: Record<string, unknown>;
}

type TicketHydrateReason = 'sse_signal' | 'sse_needs_hydrate' | 'sse_no_delta';

const TICKET_REALTIME_PATCH_KEYS = new Set<keyof Ticket>([
  'id',
  'tenant_id',
  'status_id',
  'status',
  'ticket_no',
  'origin_tenant_id',
  'origin_tenant_code',
  'display_ticket_no',
  'department_id',
  'department',
  'topic_id',
  'topic',
  'locality_id',
  'locality',
  'street_id',
  'street',
  'building_id',
  'building',
  'territorial_department_id',
  'territorial_department',
  'management_company_id',
  'management_company',
  'responsible_user_id',
  'responsible_user',
  'address_raw',
  'address_norm',
  'apartment_number',
  'deadline_at',
  'notes',
  'lock_version',
  'created_at',
  'updated_at',
]);

function extractTicketRealtimePatch(event: TicketRealtimeEvent): Partial<Ticket> {
  const raw = (event.raw ?? {}) as Record<string, unknown>;
  const patch: Partial<Ticket> = {};
  for (const key of TICKET_REALTIME_PATCH_KEYS) {
    if (key in raw) {
      (patch as Record<string, unknown>)[String(key)] = raw[String(key)];
    }
  }
  if (event.updated_at != null) patch.updated_at = event.updated_at;
  if (event.ticket_no != null) patch.ticket_no = event.ticket_no;
  return patch;
}

type TicketRealtimeListener = (event: TicketRealtimeEvent) => void;
const ticketRealtimeListeners = new Set<TicketRealtimeListener>();
const ticketHydrateTimers = new Map<number, ReturnType<typeof setTimeout>>();
const ticketHydrateInFlight = new Set<number>();
const TICKET_HYDRATE_DEBOUNCE_MS = 350;

function scheduleTicketHydration(ticketId: number, reason: TicketHydrateReason): void {
  const existing = ticketHydrateTimers.get(ticketId);
  if (existing) clearTimeout(existing);
  const timer = setTimeout(() => {
    ticketHydrateTimers.delete(ticketId);
    if (ticketHydrateInFlight.has(ticketId)) return;
    ticketHydrateInFlight.add(ticketId);
    emitTicketCacheTelemetry('HYDRATE_START', { ticket_id: ticketId, reason });
    void ticketService
      .get(ticketId, { force: true })
      .then((fresh) => {
        emitTicketCacheTelemetry('HYDRATE_DONE', {
          ticket_id: ticketId,
          reason,
          lock_version: Number((fresh as { lock_version?: unknown }).lock_version ?? 0) || 0,
          updated_at: (fresh as { updated_at?: string | null }).updated_at ?? null,
        });
      })
      .catch((error) => {
        emitTicketCacheTelemetry('HYDRATE_ERROR', {
          ticket_id: ticketId,
          reason,
          trace_id: getTraceIdFromError(error),
          message: (error as { message?: string })?.message ?? 'unknown',
        });
      })
      .finally(() => {
        ticketHydrateInFlight.delete(ticketId);
      });
  }, TICKET_HYDRATE_DEBOUNCE_MS);
  ticketHydrateTimers.set(ticketId, timer);
}

function parseTicketRealtimeEvent(payload: unknown): TicketRealtimeEvent | null {
  if (!payload || typeof payload !== 'object') return null;
  const row = payload as Record<string, unknown>;
  const rawType = typeof row.type === 'string' ? row.type.trim() : '';
  const rawTicketId = Number(row.ticket_id);
  const ticketId = Number.isFinite(rawTicketId)
    ? Math.trunc(rawTicketId)
    : Number.isFinite(Number(row.id))
      ? Math.trunc(Number(row.id))
      : null;
  if (ticketId == null || ticketId <= 0) return null;

  let type: TicketRealtimeEvent['type'] | null = null;
  if (rawType === 'ticket.created' || rawType === 'ticket_create' || rawType === 'ticket_created') type = 'ticket.created';
  if (rawType === 'ticket.updated' || rawType === 'ticket_update' || rawType === 'ticket_updated' || rawType === 'ticket_changed') type = 'ticket.updated';
  if (rawType === 'ticket.deleted' || rawType === 'ticket_delete' || rawType === 'ticket_deleted') type = 'ticket.deleted';
  if (!type) return null;

  const changedFields = Array.isArray(row.changed_fields)
    ? row.changed_fields.map((v) => String(v)).filter((v) => v.length > 0)
    : null;

  return {
    type,
    tenant_id: typeof row.tenant_id === 'string' ? row.tenant_id : null,
    ticket_id: ticketId,
    ticket_no: Number.isFinite(Number(row.ticket_no)) ? Math.trunc(Number(row.ticket_no)) : null,
    updated_at: typeof row.updated_at === 'string' ? row.updated_at : null,
    changed_fields: changedFields,
    event_id: row.event_id != null ? String(row.event_id) : null,
    raw: row,
  };
}

export function subscribeTicketRealtime(listener: TicketRealtimeListener): () => void {
  ticketRealtimeListeners.add(listener);
  return () => {
    ticketRealtimeListeners.delete(listener);
  };
}

export function notifyTicketRealtime(payload: unknown): void {
  const event = parseTicketRealtimeEvent(payload);
  if (!event) return;
  emitTicketCacheTelemetry('EVENT_RECEIVED', {
    type: event.type,
    ticket_id: event.ticket_id,
    tenant_id: event.tenant_id ?? null,
    updated_at: event.updated_at ?? null,
  });
  if (event.type === 'ticket.deleted') {
    invalidateTicketCache(event.ticket_id);
    clearTicketMessagesCache(event.ticket_id);
    clearTicketHistoryCache(event.ticket_id);
  } else if (event.type === 'ticket.updated') {
    const patch = extractTicketRealtimePatch(event);
    patchTicketCache(event.ticket_id, patch);
    clearTicketMessagesCache(event.ticket_id);
    clearTicketHistoryCache(event.ticket_id);
    const raw = (event.raw ?? {}) as Record<string, unknown>;
    const hint = raw.hint && typeof raw.hint === 'object' ? (raw.hint as Record<string, unknown>) : null;
    const needsHydrate = Boolean(hint?.needs_hydrate);
    if (needsHydrate) {
      scheduleTicketHydration(event.ticket_id, 'sse_needs_hydrate');
    } else if (!event.changed_fields || event.changed_fields.length === 0) {
      scheduleTicketHydration(event.ticket_id, 'sse_no_delta');
    } else {
      // Even with partial fields in SSE, hydrate keeps UI deterministic under reordering.
      scheduleTicketHydration(event.ticket_id, 'sse_signal');
    }
  } else if (event.type === 'ticket.created') {
    const patch = extractTicketRealtimePatch(event);
    if (Object.keys(patch).length > 0) {
      setTicketCache(event.ticket_id, { id: event.ticket_id, ...patch } as Ticket, 'light');
    }
  }
  for (const listener of ticketRealtimeListeners) {
    try {
      listener(event);
    } catch {
      // listener must not break event fanout
    }
  }
}

export function getTicketCacheStats(): {
  total: number;
  light: number;
  detail: number;
  max: number;
  ttl_ms: number;
  hits: number;
  misses: number;
  evictions: number;
  persistedCount: number;
} {
  hydrateTicketCacheFromStorage();
  let light = 0;
  let detail = 0;
  for (const entry of ticketCache.values()) {
    if (entry.source === 'light') light += 1;
    else detail += 1;
  }
  return {
    total: ticketCache.size,
    light,
    detail,
    max: TICKET_CACHE_MAX_ITEMS,
    ttl_ms: TICKET_CACHE_TTL_MS,
    hits: ticketCacheTelemetry.hits,
    misses: ticketCacheTelemetry.misses,
    evictions: ticketCacheTelemetry.evictions,
    persistedCount: ticketCacheTelemetry.persistedCount,
  };
}

export const plannedWorksService = {
  list: async (params?: {
    status?: string;
    assistant_visible?: boolean;
    is_active?: boolean;
    freshness_state?: string;
    review_status?: string;
    needs_owner?: boolean;
    q?: string;
  }): Promise<{ items: PlannedWorkSummary[]; count: number }> => {
    const response = await api.get<{ items: PlannedWorkSummary[]; count: number }>('/api/planned-works', { params });
    return {
      items: Array.isArray(response.data?.items) ? response.data.items : [],
      count: typeof response.data?.count === 'number' ? response.data.count : 0,
    };
  },
  create: async (payload: Record<string, unknown>): Promise<PlannedWorkDetailResponse> => {
    const response = await api.post<PlannedWorkDetailResponse>('/api/planned-works', payload);
    return response.data;
  },
  get: async (id: number): Promise<PlannedWorkDetailResponse> => {
    const response = await api.get<PlannedWorkDetailResponse>(`/api/planned-works/${id}`);
    return response.data;
  },
  update: async (id: number, payload: Record<string, unknown>): Promise<PlannedWorkDetailResponse> => {
    const response = await api.patch<PlannedWorkDetailResponse>(`/api/planned-works/${id}`, payload);
    return response.data;
  },
  putDepartments: async (id: number, payload: { departments: Record<string, unknown>[] }): Promise<PlannedWorkDetailResponse> => {
    const response = await api.put<PlannedWorkDetailResponse>(`/api/planned-works/${id}/departments`, payload);
    return response.data;
  },
  putTerritories: async (id: number, payload: { territories: Record<string, unknown>[] }): Promise<PlannedWorkDetailResponse> => {
    const response = await api.put<PlannedWorkDetailResponse>(`/api/planned-works/${id}/territories`, payload);
    return response.data;
  },
  putSchedule: async (
    id: number,
    payload: { windows: Record<string, unknown>[]; status?: string | null; status_reason?: string | null },
  ): Promise<PlannedWorkDetailResponse> => {
    const response = await api.put<PlannedWorkDetailResponse>(`/api/planned-works/${id}/schedule`, payload);
    return response.data;
  },
  putAssistantSettings: async (id: number, payload: Record<string, unknown>): Promise<PlannedWorkDetailResponse> => {
    const response = await api.put<PlannedWorkDetailResponse>(`/api/planned-works/${id}/assistant-settings`, payload);
    return response.data;
  },
  publish: async (id: number): Promise<PlannedWorkDetailResponse> => {
    const response = await api.post<PlannedWorkDetailResponse>(`/api/planned-works/${id}/publish`);
    return response.data;
  },
  archive: async (id: number): Promise<PlannedWorkDetailResponse> => {
    const response = await api.post<PlannedWorkDetailResponse>(`/api/planned-works/${id}/archive`);
    return response.data;
  },
  preview: async (id: number): Promise<{ planned_work_id: number; preview: PlannedWorkDetailResponse['preview'] }> => {
    const response = await api.post<{ planned_work_id: number; preview: PlannedWorkDetailResponse['preview'] }>(`/api/planned-works/${id}/assistant-preview`);
    return response.data;
  },
  audit: async (id: number): Promise<{ planned_work_id: number; items: PlannedWorkDetailResponse['audit'] }> => {
    const response = await api.get<{ planned_work_id: number; items: PlannedWorkDetailResponse['audit'] }>(`/api/planned-works/${id}/audit`);
    return response.data;
  },
  refreshCandidates: async (id: number): Promise<{
    planned_work_id: number;
    projection_state?: string | null;
    assistant_projection_block_reason?: string | null;
    candidate_count?: number | null;
    warning_codes?: string[] | null;
  }> => {
    const response = await api.post(`/api/planned-works/${id}/refresh-candidates`);
    return response.data;
  },
  assignOwner: async (id: number, payload: { owner_user_id?: number | null; comment?: string | null }): Promise<PlannedWorkDetailResponse> => {
    const response = await api.post<PlannedWorkDetailResponse>(`/api/planned-works/${id}/assign-owner`, payload);
    return response.data;
  },
  markReviewed: async (id: number, payload?: { comment?: string | null }): Promise<PlannedWorkDetailResponse> => {
    const response = await api.post<PlannedWorkDetailResponse>(`/api/planned-works/${id}/mark-reviewed`, payload ?? {});
    return response.data;
  },
  requestReview: async (
    id: number,
    payload?: { target_user_id?: number | null; comment?: string | null },
  ): Promise<PlannedWorkDetailResponse> => {
    const response = await api.post<PlannedWorkDetailResponse>(`/api/planned-works/${id}/request-review`, payload ?? {});
    return response.data;
  },
  confirmFreshness: async (id: number, payload?: { comment?: string | null }): Promise<PlannedWorkDetailResponse> => {
    const response = await api.post<PlannedWorkDetailResponse>(`/api/planned-works/${id}/confirm-freshness`, payload ?? {});
    return response.data;
  },
};

export const DEFAULT_TICKET_CREATE_STATUS_ID = 1;

export type TicketCreatePayload = {
  notes?: string | null;
  messages_ids: number[];
  status_id: number;
  reuse_existing_ticket?: boolean;
  department_id?: number | null;
  topic_id?: number | null;
  [key: string]: unknown;
};

/** A proven visible association conflict offers an explicit choice, never an automatic retry. */
export function resolveTicketCreateConflict(error: unknown): { ticketIds: number[]; message: string } | null {
  type ConflictDetail = { code?: unknown; ticket_ids?: unknown };
  const response = (error as AxiosError<{ detail?: ConflictDetail; error?: ConflictDetail; errors?: ConflictDetail[] }>)?.response;
  const detail = response?.data?.error ?? response?.data?.detail ?? response?.data?.errors?.[0];
  if (response?.status !== 409 || !detail
    || !['existing_ticket_archived', 'existing_ticket_partial_membership'].includes(String(detail.code))) return null;
  const ids = detail.ticket_ids;
  if (!Array.isArray(ids) || ids.length !== 1 || !Number.isSafeInteger(ids[0]) || ids[0] <= 0) return null;
  const ticketIds = ids as number[];
  return {
    ticketIds,
    message: detail.code === 'existing_ticket_archived'
      ? `Сообщение связано с архивной задачей. Создать отдельную задачу, сохранив существующие связи?`
      : `Часть выбранных сообщений уже входит в связанную задачу. Создать отдельную задачу по всем выбранным сообщениям, сохранив существующие связи?`,
  };
}

export function buildTicketCreatePayload(input: {
  messageIds: number[];
  notes?: string | null;
  statusId?: number;
  reuseExistingTicket?: boolean;
  departmentId?: number | null;
  topicId?: number | null;
}): TicketCreatePayload {
  const messageIds = Array.isArray(input.messageIds)
    ? input.messageIds.filter((value) => Number.isFinite(value)).map((value) => Number(value))
    : [];
  if (messageIds.length === 0) {
    throw new Error('ticket.create requires at least one message id');
  }
  const payload: TicketCreatePayload = {
    messages_ids: messageIds,
    status_id: input.statusId ?? DEFAULT_TICKET_CREATE_STATUS_ID,
  };
  const normalizedNotes = typeof input.notes === 'string' ? input.notes.trim() : input.notes;
  if (normalizedNotes) {
    payload.notes = normalizedNotes;
  }
  if (input.departmentId !== undefined) {
    payload.department_id = input.departmentId;
  }
  if (input.topicId !== undefined) {
    payload.topic_id = input.topicId;
  }
  if (input.reuseExistingTicket !== undefined) {
    payload.reuse_existing_ticket = input.reuseExistingTicket;
  }
  return payload;
}

export const ticketService = {
  list: async (): Promise<Ticket[]> => {
    const response = await api.get<Ticket[]>('/api/tickets');
    const list = Array.isArray(response.data) ? response.data : [];
    for (const item of list) {
      if (item && typeof item.id === 'number') setTicketCache(item.id, item, 'light');
    }
    return list;
  },
  filter: async (payload: {
    filter: Record<string, unknown>;
    records_per_page?: number;
    current_page?: number;
  }, options?: { signal?: AbortSignal }): Promise<{ tickets: Ticket[]; total_records?: number }> => {
    const body = { filter: payload.filter ?? {}, records_per_page: payload.records_per_page ?? 100, current_page: payload.current_page ?? 1 };
    const response = await api.post<{ tickets: Ticket[]; total_records?: number }>('/api/tickets/filter', body, {
      signal: options?.signal,
    });
    const tickets = Array.isArray(response.data?.tickets) ? response.data.tickets : [];
    for (const item of tickets) {
      if (item && typeof item.id === 'number') setTicketCache(item.id, item, 'light');
    }
    return { ...response.data, tickets };
  },
  get: async (id: number, options?: { force?: boolean }): Promise<Ticket> => {
    if (!options?.force) {
      const cached = getTicketFromCache(id, 'detail');
      if (cached) return cached;
    }
    return withSharedRequest(`ticket:get:${id}`, async () => {
      const response = await api.get<Ticket>(`/api/tickets/${id}`);
      setTicketCache(id, response.data, 'detail');
      return response.data;
    });
  },
  getCached: (id: number): Ticket | null => getTicketFromCache(id),
  upsertCached: (ticket: Ticket, source: TicketCacheSource = 'detail'): Ticket => setTicketCache(ticket.id, ticket, source),
  patchCached: (id: number, patch: Partial<Ticket>): Ticket | null => patchTicketCache(id, patch),
  removeCached: (id: number): void => invalidateTicketCache(id),
  getRelated: async (id: number, options?: { limit?: number }): Promise<RelatedTicketsResponse> => {
    const params = new URLSearchParams();
    if (options?.limit != null) params.set('limit', String(options.limit));
    const suffix = params.toString();
    const response = await api.get<RelatedTicketsResponse>(`/api/tickets/${id}/related${suffix ? `?${suffix}` : ''}`);
    return response.data;
  },
  getRelatedCounts: async (ids: number[]): Promise<RelatedTicketCountItem[]> => {
    const uniqueIds = Array.from(new Set(ids.filter((id) => Number.isFinite(id) && id > 0))).slice(0, 50);
    if (uniqueIds.length === 0) return [];
    const params = new URLSearchParams();
    uniqueIds.forEach((id) => params.append('ticket_ids', String(id)));
    const response = await api.get<{ items?: RelatedTicketCountItem[] }>(`/api/tickets/related-counts?${params.toString()}`);
    return Array.isArray(response.data?.items) ? response.data.items : [];
  },
  /** Подгрузить задачи в кэш в фоне (при простое), чтобы при клике открывать без задержки. */
  prefetch: (ids: number[]): void => {
    const missing = ids.filter((id) => !getTicketFromCache(id));
    if (missing.length === 0) return;
    const run = () => {
      missing.forEach((id) => ticketService.get(id).catch(() => {}));
    };
    if (typeof (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number }).requestIdleCallback !== 'undefined') {
      (window as unknown as { requestIdleCallback: (cb: () => void, opts?: { timeout?: number }) => number }).requestIdleCallback(run, { timeout: 3000 });
    } else {
      setTimeout(run, 200);
    }
  },
  getMessages: async (id: number, options?: { force?: boolean }): Promise<TicketMessageItem[]> => {
    const cached = options?.force ? null : getTicketMessagesFromCache(id);
    if (cached) return cached;
    return withSharedRequest(`ticket:messages:${id}`, async () => {
      const response = await api.get<TicketMessageItem[]>(`/api/tickets/${id}/messages`);
      setTicketMessagesCache(id, response.data);
      return response.data;
    });
  },
  getMessageMediaBlob: async (ticketId: number, messageId: number, fileId: string, signal?: AbortSignal): Promise<Blob> => {
    const response = await api.get<Blob>(
      `/api/tickets/${ticketId}/messages/${messageId}/media/${encodeURIComponent(fileId)}`,
      { responseType: 'blob', signal },
    );
    return response.data;
  },
  getHistory: async (id: number, options?: { force?: boolean }): Promise<TicketHistoryResponse> => {
    if (!options?.force) {
      const cached = getTicketHistoryFromCache(id);
      if (cached) return cached;
    }
    return withSharedRequest(`ticket:history:${id}`, async () => {
      const response = await api.get<TicketHistoryResponse>(`/api/tickets/${id}/history`);
      return setTicketHistoryCache(id, response.data);
    });
  },
  prepareSplitDuplicate: async (
    id: number,
    payload: { anchor_message_id?: number | null }
  ): Promise<TicketSplitDuplicatePrepareResponse> => {
    const response = await api.post<TicketSplitDuplicatePrepareResponse>(`/api/tickets/${id}/split-duplicate/prepare`, payload);
    return response.data;
  },
  applySplitDuplicate: async (
    id: number,
    payload: {
      anchor_message_id?: number | null;
      moved_message_ids: number[];
      copied_message_ids: number[];
      original: TicketSplitDuplicateSidePayload;
      duplicate: TicketSplitDuplicateSidePayload;
    }
  ): Promise<TicketSplitDuplicateApplyResponse> => {
    const response = await api.post<TicketSplitDuplicateApplyResponse>(`/api/tickets/${id}/split-duplicate/apply`, payload);
    clearTicketMessagesCache(id);
    setTicketCache(id, response.data.source_ticket, 'detail');
    setTicketCache(response.data.duplicate_ticket.id, response.data.duplicate_ticket, 'detail');
    return response.data;
  },
  /** Подгрузить сообщения задач в кэш в фоне (при простое). */
  prefetchMessages: (ticketIds: number[]): void => {
    const missing = ticketIds.filter((id) => !getTicketMessagesFromCache(id));
    if (missing.length === 0) return;
    const run = () => {
      missing.forEach((id) => ticketService.getMessages(id).catch(() => {}));
    };
    if (typeof (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number }).requestIdleCallback !== 'undefined') {
      (window as unknown as { requestIdleCallback: (cb: () => void, opts?: { timeout?: number }) => number }).requestIdleCallback(run, { timeout: 3000 });
    } else {
      setTimeout(run, 300);
    }
  },
  create: async (payload: TicketCreatePayload): Promise<Ticket> => {
    const response = await api.post<Ticket>('/api/tickets', payload);
    return setTicketCache(response.data.id, response.data, 'detail');
  },
  update: async (
    id: number,
    payload: {
      status_id?: number;
      department_id?: number | null;
      topic_id?: number | null;
      locality_id?: number | null;
      street_id?: number | null;
      building_id?: number | null;
      apartment_number?: string | null;
      territorial_department_id?: number | null;
      management_company_id?: number | null;
      responsible_user_id?: number | null;
      notes?: string | null;
      deadline_at?: string | null;
      messages_ids?: number[];
      keywords?: string[] | null;
      address_patch_mode?: 'preserve' | 'replace';
      lock_version?: number;
      [key: string]: unknown;
    }
  ): Promise<Ticket> => {
    // CONTRACTS/OPERATOR_WRITES.md:
    // all operator writes must carry a concrete lock_version to preserve strict optimistic locking.
    let effectivePayload = { ...payload } as typeof payload;
    if (effectivePayload.lock_version == null || !Number.isFinite(Number(effectivePayload.lock_version))) {
      const fresh = await ticketService.get(id, { force: true });
      if (fresh.lock_version == null || !Number.isFinite(Number(fresh.lock_version))) {
        throw new Error(`ticket.update requires lock_version (ticket_id=${id})`);
      }
      effectivePayload = { ...effectivePayload, lock_version: Number(fresh.lock_version) };
    }
    const patchTicket = async (payloadToSend: typeof effectivePayload): Promise<Ticket> => {
      const response = await api.patch<Ticket>(`/api/tickets/${id}`, payloadToSend);
      const updated = setTicketCache(id, response.data, 'detail');
      if ('messages_ids' in payloadToSend && payloadToSend.messages_ids !== undefined) {
        clearTicketMessagesCache(id);
      }
      clearTicketHistoryCache(id);
      return updated;
    };

    try {
      return await patchTicket(effectivePayload);
    } catch (error) {
      const err = error as AxiosError<{ detail?: { error?: string; current_lock_version?: number } }>;
      const isStale409 = Number(err.response?.status ?? 0) === 409 && err.response?.data?.detail?.error === 'stale_lock_version';
      if (!isStale409) throw error;

      // Unified gateway recovery: stale lock -> force GET -> one retry with fresh lock_version.
      const fresh = await ticketService.get(id, { force: true });
      const freshLock = Number((fresh as { lock_version?: unknown }).lock_version ?? NaN);
      if (!Number.isFinite(freshLock)) throw error;
      const retryPayload = { ...effectivePayload, lock_version: freshLock };
      return await patchTicket(retryPayload);
    }
  },
};

export interface VerticalDefinition {
  code: string;
  label: string;
  short_label: string;
  description: string;
  recommended_subdomain: string;
  tenant_ids: string[];
  primary_department_names: string[];
  primary_topic_names: string[];
  secondary_topic_names: string[];
  keyword_profile_code: string;
  keywords: string[];
  processing_start_date: string;
}

export interface VerticalTaxonomyItem {
  tenant_id: string;
  department_id: number;
  department_name: string;
  topic_id: number;
  topic_name: string;
  match_kind: 'primary' | 'secondary' | string;
}

export interface VerticalSourceInventoryItem {
  tenant_id: string;
  source_kind: 'tenant_channel' | 'mtproto_channel' | string;
  public_label: string;
  enabled: boolean;
  last_sync_at?: string | null;
  last_scan_to?: string | null;
}

export interface VerticalProfileResponse {
  definition: VerticalDefinition;
  summary: {
    total_tickets: number;
    cities: Array<{ tenant_id: string; tickets: number }>;
    taxonomy: VerticalTaxonomyItem[];
  };
  source_inventory: VerticalSourceInventoryItem[];
}

export interface VerticalTicketsResponse {
  tickets: Ticket[];
  total_records: number;
  limit: number;
  offset: number;
  taxonomy: VerticalTaxonomyItem[];
}

export interface VerticalMessageItem {
  id: number;
  tenant_id: string;
  author?: { id?: number | null; name?: string | null } | null;
  ticket_id?: number | null;
  ticket?: {
    id: number;
    tenant_id?: string | null;
    status?: { id?: number | null; name?: string | null } | null;
    department?: { id?: number | null; name?: string | null } | null;
    topic?: { id?: number | null; name?: string | null } | null;
    address?: string | null;
    address_norm?: string | null;
    address_raw?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
    message_created_at?: string | null;
    message_updated_at?: string | null;
  } | null;
  ticket_messages?: Array<{
    id: number;
    tenant_id?: string | null;
    author_id?: number | null;
    author_name?: string | null;
    source_type?: string | null;
    source_channel_id?: number | null;
    channel_message_id?: number | null;
    source_created_at?: string | null;
    created_at?: string | null;
    chat_operator?: boolean | null;
    text_preview: string;
  }>;
  author_recent_messages?: Array<{
    id: number;
    tenant_id?: string | null;
    author_id?: number | null;
    author_name?: string | null;
    source_type?: string | null;
    source_channel_id?: number | null;
    channel_message_id?: number | null;
    source_created_at?: string | null;
    created_at?: string | null;
    chat_operator?: boolean | null;
    text_preview: string;
  }>;
  address_related_tickets?: Array<{
    id: number;
    tenant_id?: string | null;
    status?: { id?: number | null; name?: string | null } | null;
    department?: { id?: number | null; name?: string | null } | null;
    topic?: { id?: number | null; name?: string | null } | null;
    address?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
    message_created_at?: string | null;
    message_updated_at?: string | null;
  }>;
  source_type: string;
  source_channel_id: number;
  channel_message_id: number;
  source_created_at?: string | null;
  created_at?: string | null;
  text_preview: string;
  matched_by: {
    task: boolean;
    keyword: boolean;
  };
}

export interface VerticalMessagesResponse {
  messages: VerticalMessageItem[];
  total_records: number;
  limit: number;
  offset: number;
  taxonomy: VerticalTaxonomyItem[];
}

export interface VerticalMapPoint {
  id: number;
  tenant_id: string;
  status?: { id?: number | null; name?: string | null } | null;
  department?: { id?: number | null; name?: string | null } | null;
  topic?: { id?: number | null; name?: string | null } | null;
  address?: string | null;
  coords: { lat: number; lon: number; source?: string | null };
}

export interface VerticalMapResponse {
  points: VerticalMapPoint[];
  total_records: number;
  limit: number;
  bounds?: { south: number; west: number; north: number; east: number } | null;
  taxonomy: VerticalTaxonomyItem[];
}

export interface MyTkoDraftPreview {
  message_id: number;
  draft_text: string;
  address_text: string | null;
  evidence: {
    status: string;
    date?: string | null;
    container_area_id?: string | null;
    observed_at?: string | null;
    source?: string | null;
    operation?: string | null;
  };
  container_area_ids: string[];
}

export const verticalService = {
  list: async (): Promise<{ items: VerticalDefinition[] }> => {
    const response = await api.get<{ items: VerticalDefinition[] }>('/api/verticals');
    return response.data;
  },
  get: async (code: string): Promise<VerticalProfileResponse> => {
    const response = await api.get<VerticalProfileResponse>(`/api/verticals/${encodeURIComponent(code)}`);
    return response.data;
  },
  tickets: async (code: string, params?: { limit?: number; offset?: number }): Promise<VerticalTicketsResponse> => {
    const response = await api.get<VerticalTicketsResponse>(`/api/verticals/${encodeURIComponent(code)}/tickets`, { params });
    return response.data;
  },
  messages: async (code: string, params?: { limit?: number; offset?: number }): Promise<VerticalMessagesResponse> => {
    const response = await api.get<VerticalMessagesResponse>(`/api/verticals/${encodeURIComponent(code)}/messages`, { params });
    return response.data;
  },
  myTkoDraft: async (code: string, messageId: number): Promise<MyTkoDraftPreview> => {
    const response = await api.get<MyTkoDraftPreview>(
      `/api/verticals/${encodeURIComponent(code)}/messages/${messageId}/mytko-draft`,
    );
    return response.data;
  },
  map: async (code: string, params?: { limit?: number }): Promise<VerticalMapResponse> => {
    const response = await api.get<VerticalMapResponse>(`/api/verticals/${encodeURIComponent(code)}/map`, { params });
    return response.data;
  },
};

export const commentService = {
  listByTicket: async (ticketId: number, options?: { force?: boolean }): Promise<TicketComment[]> => {
    if (!options?.force) {
      const cached = getFreshTimedNumberCache(ticketCommentsCache, ticketId, COMMENTS_CACHE_TTL_MS);
      if (cached) return cached;
    }
    return withSharedRequest(`ticket:comments:${ticketId}`, async () => {
      const response = await api.get<TicketComment[]>(`/api/tickets/${ticketId}/comments`);
      return setTimedNumberCache(ticketCommentsCache, ticketId, response.data);
    });
  },
  create: async (payload: { content: string; ticket_id: number }): Promise<TicketComment> => {
    const response = await api.post<TicketComment>('/api/comments', payload);
    ticketCommentsCache.delete(payload.ticket_id);
    return response.data;
  },
};

export interface MapConfig {
  yandex_api_key?: string | null;
  yandex_api_key_safe?: string | null;
  tenant_id?: string | null;
  city_options?: Array<{ id: string; label: string; current?: boolean }>;
  viewport_seed?:
    | {
        kind: 'bounds';
        bounds: [[number, number], [number, number]];
      }
    | null;
  spatial_contract?: {
    default_mode?: 'auto';
    aggregate_zoom_max?: number;
    point_zoom_min?: number;
    point_limit?: number;
    aggregate_grid?: { rows: number; cols: number };
    cluster_grid?: { rows: number; cols: number };
    viewport_seed?:
      | {
          kind: 'bounds';
          bounds: [[number, number], [number, number]];
        }
      | null;
  } | null;
}

export interface MapRoadSegment {
  kind: 'road_segment';
  source?: string | null;
  length_m?: number | null;
  suppressed?: boolean;
  suppression_reason_code?: string | null;
  is_renderable?: boolean;
  geometry_source?: string | null;
  geometry_source_confidence?: number | null;
  geometry_generation_path?: string | null;
  street_match_status?: string | null;
  quality_flags?: string[] | null;
  geom?: {
    type: 'LineString' | 'MultiLineString';
    coordinates: number[][] | number[][][];
  } | null;
}

export interface MapTaskDialogSnippet {
  author?: string | null;
  kind?: 'resident' | 'staff';
  text?: string | null;
  date?: string | null;
}

export interface MapTaskItem {
  id: number;
  created_at: string;
  updated_at?: string;
  status_id: number;
  status_name?: string | null;
  topic_id?: number | null;
  topic_name?: string | null;
  department_id?: number | null;
  department_name?: string | null;
  management_company_id?: number | null;
  management_company_name?: string | null;
  address?: string | null;
  address_warning?: AddressWarningPayload | null;
  coords?: { lat: number; lon: number; source?: string | null } | null;
  geo?: {
    requested_resolution?: 'house' | 'street' | 'locality' | 'unknown' | null;
    resolved_resolution?: 'house' | 'street' | 'locality' | 'unknown' | null;
    marker_policy?: 'exact_only' | 'best_effort' | null;
    marker_visible?: boolean;
    marker_suppressed_reason?: string | null;
    source?: string | null;
    provider?: string | null;
    confidence?: number | null;
    fallback_reason?: string | null;
    updated_by_job?: string | null;
  } | null;
  road_segment?: MapRoadSegment | null;
  trajectory?: MapRoadSegment | null;
  messages_count?: number;
  dialog_preview?: {
    resident?: MapTaskDialogSnippet | null;
    staff?: MapTaskDialogSnippet | null;
  } | null;
}

export interface AddressWarningPayload {
  code?: string | null;
  level?: 'warning' | 'error' | string | null;
  icon?: string | null;
  message?: string | null;
  tooltip?: string | null;
}

export interface MapSpatialEntity {
  id: string;
  kind: 'aggregate' | 'cluster' | 'point';
  count: number;
  coords: { lat: number; lon: number };
  bounds?: {
    south: number;
    west: number;
    north: number;
    east: number;
  } | null;
  ticket_id?: number | null;
}

export interface MapSpatialStatusSummaryItem {
  id: number;
  name?: string | null;
  type?: string | null;
  count: number;
}

export interface MapTasksResponse {
  items: MapTaskItem[];
  entities?: MapSpatialEntity[];
  mode?: 'aggregate' | 'cluster' | 'point';
  overflow?: {
    active: boolean;
    reason: string;
    limit: number;
    matched: number;
    message: string;
  } | null;
  contract?: {
    road_segment_aliases_trajectory?: boolean;
    movement_track_available?: boolean;
    spatial_modes?: Array<'aggregate' | 'cluster' | 'point'>;
    aggregate_zoom_max?: number;
    point_zoom_min?: number;
    point_limit?: number;
  };
  geocode: { cached: number; used: number; skipped: number; lookup?: string };
  stats?: {
    total: number;
    total_returned?: number;
    total_available?: number;
    with_coords: number;
    without_coords: number;
    viewport_total?: number;
    viewport_returned?: number;
  };
  spatial?: {
    requested_mode?: 'auto' | 'aggregate' | 'cluster' | 'point';
    resolved_mode?: 'aggregate' | 'cluster' | 'point';
    zoom?: number | null;
    bbox?: { south: number; west: number; north: number; east: number };
    viewport_total?: number;
    rendered_entities?: number;
    entity_limit?: number;
    dataset_total?: number | null;
    dataset_with_coords?: number | null;
    status_summary?: MapSpatialStatusSummaryItem[];
    contract?: MapConfig['spatial_contract'];
  } | null;
  filters?: {
    statuses: Array<{ id: number; name: string; type?: string | null }>;
    topics: Array<{ id: number; name: string; department_id?: number | null; department_name?: string | null }>;
    departments: Array<{ id: number; name: string }>;
    management_companies?: Array<{ id: number; name: string }>;
  };
}

export interface AnalyticSummaryItem {
  id: number;
  name: string;
  count: number;
}

export interface AnalyticSummary {
  total: number;
  total_messages?: number;
  spam_messages?: number;
  deleted_messages?: number;
  statuses: AnalyticSummaryItem[];
  departments: AnalyticSummaryItem[];
  topics: AnalyticSummaryItem[];
}

export interface AnalyticSourceOption {
  key: string;
  group: string;
  kind: 'type' | 'telegram_channel';
  source_type: string;
  source_channel_id?: string | null;
  label: string;
  subtitle?: string | null;
  count: number;
}

export interface ExportTicketPreviewItem {
  id: number;
  type?: 'task';
  created_at: string;
  ticket_created_at?: string | null;
  activity_at?: string | null;
  updated_at: string;
  deadline_at?: string | null;
  status_id: number;
  status_name?: string | null;
  topic_id?: number | null;
  topic_name?: string | null;
  department_id?: number | null;
  department_name?: string | null;
  address?: string | null;
  keywords?: string[];
  notes?: string | null;
  metadata?: Record<string, unknown>;
  messages_count?: number;
  dialog?: Array<{ author: string; kind: 'resident' | 'staff'; text: string; date: string }>;
}

export interface ExportTicketPreviewResponse {
  items: ExportTicketPreviewItem[];
  total: number;
  total_returned?: number;
  offset?: number;
  limit?: number;
  has_more?: boolean;
  next_offset?: number | null;
  coverage?: ExportCoverage;
}

export interface ExportMessagePreviewItem {
  id: number | string;
  type?: 'message';
  created_at: string;
  tenant?: string | null;
  source_type?: string | null;
  source_channel_id: string | null;
  channel_message_id?: string | null;
  author_id?: string | null;
  author_name?: string | null;
  author_role?: string | null;
  private_chat: boolean;
  chat_operator: boolean;
  spam: boolean;
  deleted: boolean;
  has_media?: boolean | null;
  attachments_count?: number | null;
  task_linkage_available?: boolean;
  storage_tier?: 'live' | 'archive' | string;
  ticket_id?: number | null;
  ticket_display_number?: string | number | null;
  tenant_local_ticket_no?: number | null;
  ticket_status?: string | null;
  status_id?: number | null;
  department_id?: number | null;
  department_name?: string | null;
  topic_id?: number | null;
  topic_name?: string | null;
  address?: string | null;
  membership_status?: string | null;
  membership_proof?: string | null;
  include_reason?: string | null;
  operator_role?: string | null;
  link_to_task?: string | null;
  text: string;
}

export interface ExportMessagePreviewResponse {
  items: ExportMessagePreviewItem[];
  total: number;
  total_returned?: number;
  offset?: number;
  limit?: number;
  has_more?: boolean;
  next_offset?: number | null;
  coverage?: ExportCoverage;
}

export interface ExportCapabilitiesResponse {
  archive_required: boolean;
  archive_configured: boolean;
  archive_until?: string | null;
  messages: { available: boolean; reason?: string | null };
  tickets: { available: boolean; reason?: string | null };
  archive_unsupported_message_filters: string[];
  departments: Array<{ id: string; label: string }>;
  themes: Array<{ id: string; label: string; parent_id: string }>;
}

export interface ExportCoverage {
  status: string;
  mode: string;
  live_rows: number;
  archive_rows: number;
  archive_until?: string | null;
  warnings: string[];
}

export interface AnalyticReport {
  metric_contract_version?: 'analytics.v3' | string;
  generated_at?: string;
  data_revision?: { message_max_id?: string | null; linkage_revision?: string | null; linkage_basis?: string };
  data_availability?: {
    status: 'available' | 'empty_period' | 'filtered_out' | string;
    period_source_total: number;
    included_total: number;
    reason: string;
  };
  scope?: {
    tenant_id?: string;
    timezone: string;
    date_from?: string | null;
    date_to_exclusive?: string | null;
    source_types: string[];
    source_channel_ids: string[];
    private_chat: string;
    actor_scope: string;
    deletion_scope: string;
    spam_scope: string;
    ticket_linkage_basis: string;
    source_counting_mode: string;
    coverage_mode: string;
    metric_contract_version: string;
    scope_hash: string;
  };
  period: { date_from?: string | null; date_to?: string | null; tz?: string; bounds?: string };
  messages: {
    raw_total?: number;
    partitions?: {
      canonical_resident: number;
      operator_official: number;
      service_transport: number;
      deleted_spam: number;
      unknown: number;
    };
    service_transport_breakdown?: Record<string, number>;
    business_conversation?: number;
    linked_to_selected_tasks?: Record<string, number>;
    total: number;
    residents: number;
    operators: number;
    svoyaki?: number;
    spam?: { total?: number; residents: number };
    deleted?: { residents: number };
    clean_total?: number;
    bot: { total: number; residents: number; operators: number; svoyaki?: number };
    group: { total: number; residents: number; operators: number; svoyaki?: number };
    in_tasks: { total: number; residents: number; operators: number; svoyaki?: number };
    other: number;
    summary_v2?: {
      deprecated?: boolean;
      messages_total: number;
      residents_all: number;
      operators_all: number;
      service_transport?: number;
      spam_total: number;
      in_tasks_residents_clean: number;
      in_tasks_operators_clean: number;
      in_tasks_clean_total?: number;
      tickets_total: number;
      tickets_with_operator_messages: number;
    };
  };
  tickets: {
    total: number;
    opened?: number;
    closed?: number;
    closed_basis?: string;
    statuses: AnalyticSummaryItem[];
    departments: AnalyticSummaryItem[];
    topics: AnalyticSummaryItem[];
    keywords?: Array<{ keyword: string; count: number }>;
  };
  sources: {
    total: number;
    items: Array<{ source_type: string; label: string; private_chat: boolean; count: number }>;
  };
  leader_chat: Array<{
    id: number;
    message_uid: string;
    author_name?: string | null;
    author_id?: number | null;
    source_channel_id: number;
    created_at: string;
    text: string;
  }>;
  top_departments: Array<{
    department_id: number;
    department_name: string;
    count: number;
    topics: Array<{ topic_id: number; topic_name: string; count: number }>;
  }>;
  departments_all?: Array<{
    department_id: number;
    department_name: string;
    count: number;
    resident_messages?: number;
    operator_messages?: number;
    topics: Array<{
      topic_id: number;
      topic_name: string;
      count: number;
      resident_messages?: number;
      operator_messages?: number;
    }>;
  }>;
  topic_details: Array<{ topic_id: number; topic_name: string; count: number; address_count: number }>;
  risks: Array<{ topic_id: number; topic_name: string; current: number; previous: number; growth_pct: number }>;
  department_risks?: Array<{ department_id: number; department_name: string; current: number; previous: number; growth_pct: number }>;
  critical: Array<{
    ticket_id: number;
    status_name: string;
    updated_at: string;
    deadline_at?: string | null;
    address?: string | null;
  }>;
  external: Array<{
    id: number;
    message_uid: string;
    source_channel_id: number;
    author_name?: string | null;
    created_at: string;
    text: string;
  }>;
  gratitude: Array<{
    id: number;
    message_uid: string;
    author_name?: string | null;
    created_at: string;
    text: string;
  }>;
  svoyaki?: {
    total: number;
    items: Array<{
      user_id: number;
      display_name: string;
      username?: string | null;
      messages_count: number;
      in_tasks_count: number;
      float_count: number;
      spam_count: number;
      deleted_count: number;
    }>;
  };
  tasks?: {
    selected_total: number;
    with_operator_evidence_in_scope: number;
    closed_in_scope: number;
    closed_basis: string;
    closed_quality: string;
  };
  signals?: { whitelist_aggregate: number; gratitude_aggregate: number };
  quality?: {
    partition_invariant_ok: boolean;
    warnings: string[];
    closed_quality: string;
    classification_version: string;
  };
  export_payload?: Record<string, unknown>;
}

export interface AnalyticSubtopicResponse {
  topic_id?: number | null;
  department_id?: number | null;
  subtopics: Array<{ id: string; name: string }>;
  assignments: Array<{ ticket_id: number; subtopic_id: string }>;
  input?: {
    topic_id?: number | null;
    department_id?: number | null;
    enriched?: boolean;
    tickets: Array<{ ticket_id: number; address: string; text: string }>;
  };
  cached?: boolean;
  _meta?: Record<string, unknown>;
}

export type AnalyticsQuality = 'exact' | 'partial' | 'approximate' | 'unavailable';

export interface AnalyticsCatalogMetric {
  key: string;
  entity: 'message' | 'ticket';
  label: string;
  unit: string;
  quality: AnalyticsQuality;
  definition: string;
  available: boolean;
  phase: string;
  version: string;
}

export interface AnalyticsCatalogDimension {
  key: string;
  entity: 'common' | 'message' | 'ticket';
  label: string;
  quality: AnalyticsQuality;
  definition: string;
  available: boolean;
  phase: string;
  version: string;
}

export interface AnalyticsCatalogResponse {
  version: string;
  tenant_id: string;
  timezone: string;
  limits: {
    interactive_series: number;
    export_series: number;
    interactive_days: number;
  };
  metrics: AnalyticsCatalogMetric[];
  dimensions: AnalyticsCatalogDimension[];
  unavailable: Array<{ key: string; reason: string; phase: string }>;
}

export interface AnalyticsQueryRequest {
  metric: string;
  grain?: 'day' | 'week' | 'month' | string;
  date_range: { from: string; to: string };
  filters?: Record<string, unknown[] | unknown>;
  group_by?: string[];
  comparison?: { mode: string } | null;
  limit?: number;
}

export interface AnalyticsPoint {
  bucket?: string;
  value: number;
  raw_value?: number;
  group?: Record<string, { value: unknown; label: string }>;
}

export interface AnalyticsSeriesResponse {
  id: string;
  label: string;
  metric: string;
  points: AnalyticsPoint[];
  total: number;
  group_keys?: string[];
}

export interface AnalyticsQueryResponse {
  meta: {
    metric: string;
    label: string;
    entity: 'message' | 'ticket';
    grain: string;
    group_by: string[];
    date_range: { from: string; to: string };
    tenants: string[];
    timezone: string;
    quality: AnalyticsQuality;
    warnings: string[];
    generated_at: string;
    data_state?: {
      state: 'ok' | 'unavailable' | string;
      source_mode?: string;
      zero_is_business_value?: boolean;
      reason?: string;
    };
  };
  series: AnalyticsSeriesResponse[];
  comparison?: {
    mode: string;
    baseline_total: number;
    delta: number;
    delta_percent?: number | null;
    date_range: { from: string; to: string };
  };
  detail_query?: Record<string, unknown>;
}

export interface AnalyticsCompareSeriesRequest {
  id?: string;
  label?: string;
  metric?: string;
  grain?: string;
  date_range?: { from: string; to: string };
  filters?: Record<string, unknown[] | unknown>;
}

export interface AnalyticsCompareRequest {
  metric?: string;
  grain?: string;
  date_range: { from: string; to: string };
  filters?: Record<string, unknown[] | unknown>;
  mode?: 'absolute' | 'percent' | 'indexed_to_100' | 'share_of_total' | string;
  series: AnalyticsCompareSeriesRequest[];
}

export interface AnalyticsCompareResponse {
  meta: {
    series_count: number;
    max_series: number;
    mode: string;
    grain: string;
    timezone: string;
    warnings: string[];
    generated_at: string;
  };
  series: AnalyticsSeriesResponse[];
}

export interface AnalyticsPreset {
  id: number;
  name: string;
  preset_type: 'overview' | 'compare' | string;
  visibility: 'private' | 'tenant' | string;
  payload: Record<string, unknown>;
  updated_at?: string | null;
}

export interface AnalyticsDrilldownResponse {
  entity: 'message' | 'ticket';
  items: Array<Record<string, unknown>>;
}

export interface AnalyticsCanonicalResidentMetricsResponse {
  version: string;
  tenant_id: string;
  timezone: string;
  date_range: { from: string; to: string };
  source_mode: 'canonical_direct' | string;
  data_state: {
    state: 'ok' | 'unavailable' | string;
    source_mode?: string;
    zero_is_business_value?: boolean;
    reason?: string;
  };
  values: {
    all_messages_total: number;
    resident_messages_total: number;
    resident_messages_in_tasks: number;
    resident_messages_not_in_tasks: number;
    resident_linked_tasks_total: number;
    resident_message_task_links_total: number;
    resident_messages_multi_task_total: number;
  };
}

export interface WeeklyCityLifeReportDataResponse {
  version: string;
  tenant_id: string;
  city_label?: string;
  timezone: string;
  report_type?: string;
  report_style?: {
    style_id: string;
    name: string;
    description: string;
    theme: string;
    density: string;
    recommended_for: string[];
    is_default?: boolean;
    experimental?: boolean;
  };
  style_id?: string;
  style_name?: string;
  date_range: { from: string; to: string };
  period_start?: string;
  period_end?: string;
  public_period_label?: string;
  official?: boolean;
  preview?: boolean;
  source_mode: 'canonical_direct' | string;
  metric_source?: string;
  metric_basis?: string;
  coverage_mode?: string;
  comparison_mode?: string;
  weather_mode?: string;
  drilldown_context?: Record<string, unknown>;
  drilldown_context_label?: string;
  source_counting_mode?: string;
  source_channel_taxonomy?: {
    source_counting_mode?: string;
    source_counting_mode_label?: string;
    classification_basis?: string;
    items?: Array<{
      source_type: string;
      source_label: string;
      channel_class: string;
      channel_class_label: string;
      ownership_class?: string;
      ownership_class_label?: string;
      source_counting_modes?: string[];
      all_messages: number;
      canonical_resident_messages: number;
      distinct_channels?: number;
      public_safe?: boolean;
      confidence?: string;
      classification_confidence?: string;
      evidence?: string[];
      data_quality_note?: string;
    }>;
    known_gaps?: string[];
  };
  canonical_values?: Record<string, number>;
  diagnostic_values?: Record<string, number>;
  totals: {
    all_messages_total: number;
    loose_non_operator_clean_total?: number;
    resident_messages_total: number;
    operator_messages_total?: number;
    service_system_messages_total?: number;
    deleted_spam_messages_total?: number;
    resident_messages_in_tasks: number;
    resident_messages_not_in_tasks: number;
    resident_linked_tasks_total: number;
    task_message_links_total?: number;
    unresolved_message_references_total?: number;
  };
  daily: Array<{ date: string; resident_messages_in_tasks: number }>;
  sources: Array<{ label: string; value: number }>;
  topics: Array<{ label: string; value: number }>;
  responsible_directions: Array<{ label: string; value: number }>;
  streets: Array<{ label: string; value: number }>;
  data_quality?: {
    unknown_topic_count?: number | null;
    unknown_direction_count?: number | null;
    unknown_street_count?: number | null;
    unknown_district_count?: number | null;
    notes?: string[];
  };
  data_quality_notes?: string[];
  limitations: string[];
}

export interface WeeklyCityLifeReportPeriod {
  from: string;
  to: string;
  label: string;
  report_type?: string;
  report_id?: string | null;
  status: 'available' | 'generation_failed' | 'generating' | 'not_generated' | string;
  download?: string | null;
  html?: string | null;
}

export interface WeeklyCityLifeReportListResponse {
  tenant_id: string;
  timezone: string;
  reporting_week: string;
  report_type?: string;
  periods: WeeklyCityLifeReportPeriod[];
}

export interface WeeklyCityLifeReportManifest {
  id: string;
  version: string;
  tenant_id: string;
  city_label: string;
  report_type?: string;
  report_style?: {
    style_id: string;
    name: string;
    description: string;
    theme: string;
    density: string;
    recommended_for: string[];
    is_default?: boolean;
    experimental?: boolean;
  };
  style_id?: string;
  style_name?: string;
  period_start: string;
  period_end: string;
  timezone: string;
  public_period_label?: string;
  official?: boolean;
  preview?: boolean;
  generated_at: string;
  metric_source: string;
  metric_basis?: string;
  coverage_mode?: string;
  comparison_mode?: string;
  weather_mode?: string;
  drilldown_context?: Record<string, unknown>;
  drilldown_context_label?: string;
  source_counting_mode?: string;
  source_channel_taxonomy?: WeeklyCityLifeReportDataResponse['source_channel_taxonomy'];
  status: 'available' | 'generation_failed' | string;
  safety_scan: { status: 'pass' | 'fail' | string; forbidden_patterns?: string[] };
  pdf: { available: boolean; renderer_status?: Record<string, unknown> };
  public_artifacts?: { download?: string | null; html?: string | null; details?: string | null };
  public_paths: { download?: string | null; details: string; html?: string | null };
  totals: WeeklyCityLifeReportDataResponse['totals'];
}

export interface CityLifeReportCenterSchedule {
  preset_type: 'weekly_sat_fri' | 'weekly_mon_sun' | 'monthly_calendar' | string;
  name: string;
  full_name: string;
  enabled: boolean;
  configured_enabled?: boolean;
  effective_enabled?: boolean;
  period: { from: string; to: string; label: string; start?: string; end?: string };
  next_run_at: string;
  next_run_label: string;
  generation_time_local?: string;
  comparison_rule: string;
  outputs: { executive?: boolean; full?: boolean };
  status: string;
  status_label: string;
  available?: boolean;
  runtime_state?: string;
  state?: string;
  contract_version?: string;
  reason_code?: string | null;
  reason_label?: string | null;
  runtime_label?: string;
  unavailable_reason?: string | null;
  boundary_label?: string;
  preview?: boolean;
  production_ready?: boolean;
  product_official?: boolean;
  official?: boolean;
  can_submit?: boolean;
  may_replace_legacy_default?: boolean;
  supported_report_kinds?: string[];
  supported_artifact_kinds?: string[];
  requested_outputs?: string[];
  product_kind?: string;
}

export interface CityLifeReportCenterSchedulesResponse {
  version: string;
  tenant_id: string;
  city_label: string;
  timezone: string;
  deep_analytics?: {
    available?: boolean;
    runtime_state?: string;
    state?: string;
    contract_version?: string;
    reason_code?: string | null;
    reason_label?: string | null;
    runtime_label?: string;
    unavailable_reason?: string | null;
    boundary_label?: string;
    preview?: boolean;
    production_ready?: boolean;
    product_official?: boolean;
    official?: boolean;
    can_submit?: boolean;
    may_replace_legacy_default?: boolean;
    supported_report_kinds?: string[];
    supported_artifact_kinds?: string[];
    requested_outputs?: string[];
  };
  items: CityLifeReportCenterSchedule[];
}

export interface CityLifeReportCenterJob {
  job_id: string;
  tenant_id: string;
  city_label: string;
  report_type: string;
  type_label: string;
  status: string;
  status_label: string;
  last_error?: string | null;
  last_error_code?: string | null;
  product_kind?: string;
  official?: boolean;
  period: { from: string; to: string; label: string };
  comparison_period?: { from?: string | null; to?: string | null };
  created_at?: string | null;
  updated_at?: string | null;
  requested_by_user_id?: number | null;
  executive_pdf?: string | null;
  full_pdf?: string | null;
  full_docx?: string | null;
  executive_html?: string | null;
  full_html?: string | null;
  basis_json?: string | null;
  bundle?: string | null;
  artifact_family?: string | null;
  preview?: boolean | null;
  required_role?: string | null;
  may_replace_legacy_default?: boolean | null;
  role_gate_verified?: boolean | null;
  immutable_storage_verified?: boolean | null;
  legacy_coexistence_verified?: boolean | null;
  core_adapter_visibility?: string | null;
  bundle_sha256?: string | null;
  manifest_sha256?: string | null;
  boundary_label?: string | null;
  usage_scope_label?: string | null;
  data_quality_status?: string | null;
  coverage_completeness?: string | null;
}

export interface CityLifeReportCenterReportsResponse {
  version: string;
  tenant_id: string;
  city_label: string;
  selected_date?: string | null;
  helper_text: string;
  items: CityLifeReportCenterJob[];
  notifications?: Array<{
    type: string;
    title: string;
    body: string;
    created_at?: string | null;
    read?: boolean;
  }>;
}

export type KpiQuality = 'exact' | 'partial' | 'approximate' | 'unavailable';
export type KpiImplementedStatus = 'implemented' | 'metadata_only' | 'unavailable';
export type KpiDataSourceMode = 'fact_table' | 'live_query' | 'mixed' | 'unavailable' | string;

export interface KpiFactFreshness {
  target: string;
  latest_finished_at: string | null;
  covers_requested_window: boolean;
  requested_window: { from: string; to: string };
}

export interface KpiSectionDefinition {
  key: string;
  label: string;
  description: string;
}

export interface KpiMetricDefinition {
  key: string;
  label: string;
  description: string;
  entity: string;
  grain: string;
  formula: string;
  source_surface: string[];
  source_time_field: string;
  dimensions: string[];
  quality_label: KpiQuality;
  quality_reason: string;
  caveat: string;
  freshness_source: string;
  default_time_range: string;
  drilldown_target: string;
  export_policy: string;
  pii_level: 'none' | 'low' | 'moderate' | 'high' | string;
  allowed_roles: string[];
  implemented_status: KpiImplementedStatus;
  unit: string;
  section: string;
}

export interface KpiMetricValue {
  metric_key: string;
  label: string;
  value: number | Array<{ key: string; value: number }> | null;
  unit: string;
  quality_label: KpiQuality;
  quality_reason: string;
  caveat: string;
  freshness: {
    source: string;
    generated_at: string;
    source_time_field: string;
  };
  source_surface: string[];
  grain: string;
  implemented_status: KpiImplementedStatus;
  data_source_mode?: KpiDataSourceMode;
  fact_freshness?: KpiFactFreshness | null;
  warnings: string[];
}

export interface KpiCatalogResponse {
  version: string;
  tenant_id: string;
  timezone: string;
  limits: { interactive_days: number };
  sections?: KpiSectionDefinition[];
  fact_consumption?: {
    supported_metrics: string[];
    metric_targets?: Record<string, string>;
    fallback: string;
    scheduler_activation: string;
    additional_backfill?: string;
  };
  metrics: KpiMetricDefinition[];
  export_manifest: KpiExportManifestItem[];
  drilldown_manifest: KpiDrilldownManifestItem[];
}

export interface KpiOverviewResponse {
  version: string;
  tenant_id: string;
  timezone: string;
  date_range: { from: string; to: string };
  values: KpiMetricValue[];
  data_source_mode?: KpiDataSourceMode;
  fact_summary?: {
    fact_backed_metrics: string[];
    live_query_metrics: string[];
    unavailable_metrics: string[];
  };
  warnings: string[];
  unavailable: KpiMetricDefinition[];
}

export interface KpiQueryResponse {
  version: string;
  tenant_id: string;
  timezone: string;
  date_range: { from: string; to: string };
  metric: KpiMetricValue;
}

export interface KpiCompareResponse {
  version: string;
  tenant_id: string;
  timezone: string;
  metric_key: string;
  current: { date_range: { from: string; to: string }; metric: KpiMetricValue };
  comparison: {
    mode: string;
    date_range: { from: string; to: string };
    metric: KpiMetricValue;
    delta: number | null;
    delta_percent: number | null;
  };
  warnings: string[];
}

export type KpiExecutiveEntityType =
  | 'metric'
  | 'theme'
  | 'department'
  | 'territory'
  | 'locality'
  | 'district'
  | 'microdistrict'
  | 'street'
  | 'house'
  | 'source'
  | 'channel'
  | 'status'
  | 'employee'
  | 'performer'
  | 'date_bucket'
  | 'risk'
  | 'data_quality'
  | string;

export interface KpiExecutiveDrilldownRef {
  entity_type: KpiExecutiveEntityType;
  entity_id: string;
}

export interface KpiExecutiveCard {
  id: string;
  title: string;
  icon: string;
  metric: KpiMetricValue;
  delta: { value: number | null; percent: number | null; label: string };
  sparkline: Array<{ date: string; messages: number; tickets: number; quality: KpiQuality | string }>;
  drilldown: KpiExecutiveDrilldownRef;
  export_allowed: boolean;
  pii_level: string;
  summary?: string;
  detail_paths?: string[];
  graph_preset?: string;
  lines?: Array<{
    label: string;
    value: number | null;
    share?: number | null;
    quality?: KpiQuality | string;
    metric_kind?: 'flow' | 'snapshot' | 'coverage' | 'unavailable' | string;
    caveat?: string | null;
    source_mode?: KpiDataSourceMode | string;
  }>;
  comparison_rows?: Record<string, unknown>;
  info?: {
    definition?: string;
    included?: string[];
    excluded?: string[];
    caveat?: string;
  };
}

export interface KpiExecutiveInsightChip {
  id: string;
  title: string;
  value: string;
  items?: Array<{ label: string; value: number | string; value_display?: string; subtitle?: string }>;
  quality: KpiQuality | string;
  drilldown: KpiExecutiveDrilldownRef;
}

export interface KpiExecutiveRow {
  id: string;
  label: string;
  value: number | null;
  value_display?: string;
  share?: number;
  quality: KpiQuality | string;
  small_cell?: boolean;
  description?: string;
  subtitle?: string;
  parent_id?: string;
  parent_label?: string;
  source_type?: string;
  source_channel_id?: string;
  bucket_date?: string;
  bucket_hour?: string;
  group?: string;
  reason?: string;
  caveat?: string | null;
  source_mode?: KpiDataSourceMode | string;
  drilldown?: KpiExecutiveDrilldownRef;
}

export interface KpiExecutiveChartToolConfig {
  series?: Array<{ id: string; label: string; default?: boolean }>;
  value_modes?: string[];
  granularities?: string[];
  top_n?: number[];
  drilldown_variants?: string[];
  label_policy?: string;
  actions?: string[];
  export?: string;
}

export type KpiExecutiveGraphChartType = 'line' | 'bar' | 'combined';
export type KpiExecutiveGraphValueMode = 'count' | 'share' | 'delta';
export type KpiExecutiveGraphGranularity = 'day' | 'week' | 'month';

export interface KpiExecutiveGraphOption {
  id: string;
  label: string;
  subtitle?: string;
  value?: number;
  enabled?: boolean;
  reason?: string;
  quality?: KpiQuality | string;
  parent_id?: string;
  parent_label?: string;
  source_type?: string;
  source_channel_id?: string;
}

export interface KpiExecutiveGraphSeriesPoint {
  bucket: string;
  value: number;
  bucket_end?: string;
  quality?: KpiQuality | string;
  caveat?: string;
  freshness_at?: string | null;
}

export interface KpiExecutiveGraphSeries {
  id: string;
  option_id?: string;
  label: string;
  group: string;
  enabled: boolean;
  unavailable_reason?: string;
  points: KpiExecutiveGraphSeriesPoint[];
  previous_points?: KpiExecutiveGraphSeriesPoint[];
  source_mode?: KpiDataSourceMode | string;
  quality?: KpiQuality | string;
  fact_freshness?: KpiFactFreshness | null;
  caveat?: string | null;
  pii_level?: string;
}

export interface KpiExecutiveGraphResponse {
  version: string;
  tenant_id: string;
  period: {
    current: { from: string; to: string };
    comparison: { from: string; to: string };
    granularity: KpiExecutiveGraphGranularity | string;
  };
  title: string;
  subtitle: string;
  available_chart_types: Array<{ id: KpiExecutiveGraphChartType | string; label: string; enabled: boolean; reason?: string; supported_series_count?: number }>;
  value_modes: Array<{ id: KpiExecutiveGraphValueMode | string; label: string; enabled: boolean; help?: string }>;
  granularities: Array<{ id: KpiExecutiveGraphGranularity | string; label: string; enabled: boolean; reason?: string }>;
  instrument_groups: Array<{ id: string; label: string; options: KpiExecutiveGraphOption[]; depends_on?: string }>;
  default_selection: {
    chart_type: KpiExecutiveGraphChartType | string;
    value_mode: KpiExecutiveGraphValueMode | string;
    granularity: KpiExecutiveGraphGranularity | string;
    metrics: string[];
    statuses: string[];
    departments: string[];
    themes: string[];
    localities: string[];
    streets: string[];
    sources: string[];
    tags: string[];
    infopole_mode?: 'all' | 'attention' | string;
    include_parent_locality_total: boolean;
    show_previous_period: boolean;
    show_point_labels?: boolean;
  };
  selection: {
    chart_type?: KpiExecutiveGraphChartType | string;
    value_mode?: KpiExecutiveGraphValueMode | string;
    show_point_labels?: boolean;
    metrics: string[];
    statuses: string[];
    departments: string[];
    themes: string[];
    localities: string[];
    streets: string[];
    sources: string[];
    tags: string[];
    infopole_mode?: 'all' | 'attention' | string;
    include_parent_locality_total: boolean;
    show_previous_period: boolean;
    option_profile?: 'compact' | 'full' | string;
  };
  comparison?: {
    mode: string;
    periods: Array<{ id: string; raw: string }>;
    max_periods: number;
  };
  navigation?: Record<string, unknown>;
  series: KpiExecutiveGraphSeries[];
  unavailable_series: KpiExecutiveGraphSeries[];
  constraints: Array<{ id: string; active: boolean; message: string }>;
  pulse_presets: Array<{
    id: string;
    label: string;
    chart_type?: KpiExecutiveGraphChartType | string;
    metrics?: string[];
    statuses?: string[];
    departments?: string[];
    themes?: string[];
    localities?: string[];
    streets?: string[];
    sources?: string[];
    tags?: string[];
    unavailable_reason?: string;
    instrument_focus?: string;
  }>;
  privacy: {
    mode: string;
    private_content_returned: boolean;
    pii_level: string;
  };
}

export interface KpiExecutiveSection {
  id: string;
  title: string;
  description?: string;
  rows: KpiExecutiveRow[];
  entity_type?: KpiExecutiveEntityType | string | null;
  empty_reason?: string | null;
}

export interface KpiExecutiveInsight {
  id: string;
  title: string;
  severity: string;
  text: string;
  evidence: unknown[];
  quality: KpiQuality | string;
  drilldown: KpiExecutiveDrilldownRef;
}

export interface KpiExecutivePulseResponse {
  version: string;
  mode?: 'pulse_day' | 'comparison' | string;
  tenant_id: string;
  timezone: string;
  page: {
    title: string;
    subtitle: string;
    access_label: string;
    version_label: string;
  };
  comparison_contract?: Record<string, unknown>;
  period: {
    current: { from: string; to: string };
    comparison: { from: string; to: string };
    granularity: string;
  };
  source_mode: KpiDataSourceMode;
  freshness: {
    generated_at: string;
    fact_targets: string[];
  };
  cards: KpiExecutiveCard[];
  insight_chips: KpiExecutiveInsightChip[];
  panels: {
    trend: { title: string; points: Array<{ date: string; messages: number; tickets: number; quality: string }>; quality: string; tool_config?: KpiExecutiveChartToolConfig };
    status: { title: string; segments: KpiExecutiveRow[]; quality: string; tool_config?: KpiExecutiveChartToolConfig };
    themes: { title: string; rows: KpiExecutiveRow[]; quality: string; tool_config?: KpiExecutiveChartToolConfig };
    tags: { title: string; rows: KpiExecutiveRow[]; quality: string; tool_config?: KpiExecutiveChartToolConfig };
    territories: { title: string; rows: KpiExecutiveRow[]; quality: string; tool_config?: KpiExecutiveChartToolConfig };
    sources: { title: string; rows: KpiExecutiveRow[]; quality: string; tool_config?: KpiExecutiveChartToolConfig };
    insights: { title: string; items: KpiExecutiveInsight[]; quality: string };
    trust: { title: string; items: KpiExecutiveRow[]; quality: string };
  };
  export_policy: {
    mode: string;
    private_content: string;
    message: string;
  };
  warnings: string[];
}

export interface KpiExecutiveCardDetailResponse {
  version: string;
  tenant_id: string;
  card_id: string;
  title: string;
  subtitle?: string;
  active_tab: string;
  tabs: Array<{ id: string; label: string }>;
  summary: Array<{ label: string; value: number | null; share?: number | null; quality?: KpiQuality | string }>;
  rows: KpiExecutiveRow[];
  graph_preset?: string;
  privacy: Record<string, unknown>;
}

export interface KpiTaskQualityIssue {
  task_id: number;
  public_number: string;
  created_at: string;
  status_label: string;
  source_label: string;
  missing_fields: string[];
  quality_label: KpiQuality | string;
  editor_url: string;
}

export interface KpiTaskQualityIssuesResponse {
  version: string;
  tenant_id: string;
  issue_type: string;
  items: KpiTaskQualityIssue[];
  privacy: Record<string, unknown>;
}

export interface KpiExecutiveDrilldownResponse {
  version: string;
  tenant_id: string;
  date_range: { from: string; to: string };
  block_id: string;
  level: {
    blockId: string;
    entityType: KpiExecutiveEntityType;
    entityId: string;
    entityLabel: string;
  };
  title: string;
  subtitle?: string;
  breadcrumb?: Array<{ label: string; entityType: string; entityId: string }>;
  summary?: Array<{ id: string; label: string; value: number | null; value_display?: string; quality: KpiQuality | string }>;
  rows: KpiExecutiveRow[];
  sections?: KpiExecutiveSection[];
  next_entity_type: KpiExecutiveEntityType | 'none';
  empty_reason: string | null;
  tool_config?: KpiExecutiveChartToolConfig;
  privacy: {
    mode: string;
    private_content_returned: boolean;
    small_cell_suppression: boolean;
  };
}

export interface KpiRegionCity {
  tenant_id: string;
  city_label: string;
  official: boolean;
  service_mode: 'official_webhook' | 'monitoring_mtproto' | string;
  source_mode: string;
  no_group_bot_mode?: boolean;
  hourly_sync_enabled?: boolean;
  in_region_scope?: boolean;
  instance_id?: string | null;
}

export interface KpiRegionCitiesResponse {
  version: string;
  tenant_id: string;
  timezone: string;
  items: KpiRegionCity[];
}

export interface KpiRegionCityRow extends KpiRegionCity {
  resident_messages_total: number;
  unique_residents_total: number;
  resident_messages_in_tasks: number;
  resident_linked_tasks_total: number;
  answered_resident_linked_tasks_total: number;
  operator_messages_in_resident_tasks_total: number;
  operator_messages_total: number;
  tasks_created: number;
  tasks_new: number;
  tasks_in_progress: number;
  tasks_done: number;
  tasks_total: number;
  share?: number | null;
  population_baseline?: number | null;
  population_baseline_source?: string | null;
  reach_percent_city?: number | null;
  regional_resident_share_percent?: number | null;
  rank_by_reach_percent?: number | null;
  rank_by_regional_resident_share?: number | null;
  messages_per_unique_resident?: number | null;
  answer_rate_percent?: number | null;
}

export interface KpiRegionPulseResponse {
  version: string;
  tenant_id: string;
  timezone: string;
  page: {
    title: string;
    subtitle: string;
    access_label: string;
    version_label: string;
  };
  period: {
    current: { from: string; to: string };
    granularity: string;
  };
  totals: Record<string, number>;
  resident_reach?: {
    region: {
      unique_residents: number;
      population_baseline?: number | null;
      reach_percent?: number | null;
      baseline_source?: string | null;
    };
    cities: Array<{
      tenant_id: string;
      city_label: string;
      unique_residents: number;
      population_baseline?: number | null;
      reach_percent?: number | null;
      regional_resident_share_percent?: number | null;
      rank_by_reach_percent?: number | null;
      rank_by_regional_resident_share?: number | null;
      baseline_source?: string | null;
    }>;
  };
  experimental_tools?: {
    version?: string;
    resident_reach_compare?: {
      available: boolean;
      cities: Array<{
        tenant_id: string;
        city_label: string;
        unique_residents: number;
        population_baseline?: number | null;
        city_reach_percent?: number | null;
        regional_resident_share_percent?: number | null;
        rank_by_reach_percent?: number | null;
        rank_by_regional_share?: number | null;
      }>;
    };
    answer_effectiveness?: {
      available: boolean;
      cities: Array<{
        tenant_id: string;
        city_label: string;
        tasks_total: number;
        tasks_with_answer: number;
        answer_rate_percent?: number | null;
        operator_messages?: number;
      }>;
    };
    conversion_funnel?: {
      available: boolean;
      cities: Array<{
        tenant_id: string;
        city_label: string;
        resident_messages: number;
        unique_residents: number;
        tasks_from_messages: number;
        task_conversion_percent?: number | null;
        messages_per_task?: number | null;
      }>;
    };
    topic_load?: {
      available: boolean;
      cities: Array<{
        tenant_id: string;
        city_label: string;
        top_topics: Array<{
          topic: string;
          tasks: number;
          city_task_share_percent?: number | null;
          regional_topic_share_percent?: number | null;
        }>;
      }>;
    };
    place_hotspots?: {
      available: boolean;
      cities: Array<{
        tenant_id: string;
        city_label: string;
        hotspots: Array<{
          locality?: string | null;
          street?: string | null;
          tasks: number;
          with_geo: number;
          without_geo: number;
        }>;
      }>;
    };
    activity_trend?: {
      available: boolean;
      granularity: 'day' | 'week' | string;
      cities: Array<{
        tenant_id: string;
        city_label: string;
        points: Array<{
          date: string;
          resident_messages: number;
          tasks_created: number;
          answered_tasks: number;
          unique_residents: number;
        }>;
      }>;
    };
  };
  cities: KpiRegionCity[];
  city_rows: KpiRegionCityRow[];
  warnings: string[];
  freshness: { generated_at: string };
}

export interface KpiRegionGraphResponse {
  version: string;
  tenant_id: string;
  metric: string;
  period: {
    current: { from: string; to: string };
    granularity: string;
  };
  selected_path?: {
    department?: string | null;
    topic?: string | null;
    locality?: string | null;
    street?: string | null;
  };
  series: Array<{
    id: string;
    label: string;
    value: number;
    official: boolean;
    service_mode?: string;
  }>;
}

export interface KpiRegionCityDetailResponse {
  version: string;
  tenant_id: string;
  city: KpiRegionCity;
  period: {
    current: { from: string; to: string };
    granularity: string;
  };
  summary?: {
    resident_messages_total: number;
    unique_residents_total: number;
    resident_messages_in_tasks: number;
    resident_linked_tasks_total: number;
    answered_resident_linked_tasks_total: number;
    operator_messages_in_resident_tasks_total: number;
    operator_messages_total: number;
    period_tasks_total?: number;
    period_answered_tasks_total?: number;
    tasks_created: number;
    tasks_new: number;
    tasks_in_progress: number;
    tasks_done: number;
    tasks_total: number;
  };
  rows: Array<{
    department: string;
    topic: string;
    resident_messages_total?: number;
    unique_residents_total?: number;
    operator_messages_total?: number;
    tasks_total: number;
    tasks_new: number;
    tasks_in_progress: number;
    tasks_done: number;
  }>;
  localities?: Array<{
    label: string;
    tasks_total: number;
    tasks_new: number;
    tasks_in_progress: number;
    tasks_done: number;
  }>;
  streets?: Array<{
    label: string;
    locality_label?: string;
    tasks_total: number;
    tasks_new: number;
    tasks_in_progress: number;
    tasks_done: number;
  }>;
}

export interface KpiRegionTaskItem {
  id: number;
  status_name?: string | null;
  status_type?: string | null;
  department?: string | null;
  topic?: string | null;
  locality?: string | null;
  street?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  deadline_at?: string | null;
  has_operator_answer?: boolean;
  operator_messages_total?: number;
  detail_url?: string | null;
}

export interface KpiRegionTasksResponse {
  version: string;
  tenant_id: string;
  city: KpiRegionCity;
  period: {
    current: { from: string; to: string };
    granularity: string;
  };
  filters: {
    department?: string | null;
    topic?: string | null;
    locality?: string | null;
    street?: string | null;
  };
  summary: {
    tasks_total: number;
    tasks_new: number;
    tasks_in_progress: number;
    tasks_done: number;
    tasks_with_answer: number;
  };
  items: KpiRegionTaskItem[];
  pagination: {
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

export interface KpiExportManifestItem {
  metric_key: string;
  label?: string;
  description?: string;
  export_allowed: 'YES' | 'NO' | string;
  export_grain: string;
  grain_label?: string;
  pii_level: string;
  redaction_required: 'YES' | 'NO' | string;
  redaction_policy?: string;
  allowed_roles: string[];
  role_scope?: string;
  included_columns: string[];
  excluded_columns: string[];
  definition_included: 'YES' | 'NO' | string;
  caveat_included: 'YES' | 'NO' | string;
  disabled_reason?: string;
}

export interface KpiDrilldownManifestItem {
  metric_key: string;
  label?: string;
  description?: string;
  target_entity_type: string;
  target_entity_label?: string;
  target_route: string;
  allowed_roles: string[];
  role_scope?: string;
  contains_private_content: 'YES' | 'NO' | string;
  requires_redaction: 'YES' | 'NO' | string;
  redaction_policy?: string;
  max_rows: number;
  reason: string;
  disabled_reason?: string;
}

export interface TicketEditorActionsReport {
  period: {
    date_from: string;
    date_to: string;
    min_supported_date: string;
  };
  totals: {
    events: number;
    tickets: number;
    actors: number;
    message_links_added: number;
    message_links_removed: number;
  };
  by_action: Array<{ action: string; count: number }>;
  by_field: Array<{ field: string; count: number }>;
  by_day: Array<{ day: string; count: number }>;
  by_actor: Array<{ actor_id: number; count: number }>;
  samples: Array<{
    id: number;
    ticket_id?: number | null;
    action: string;
    actor_id?: number | null;
    created_at?: string | null;
    before_state?: Record<string, unknown> | null;
    after_state?: Record<string, unknown> | null;
  }>;
}

export interface ForecastListResponse {
  months: string[];
}

export interface ForecastReportResponse {
  month: string;
  total: number;
  topics: AnalyticSummaryItem[];
  departments: AnalyticSummaryItem[];
  daily: Array<{ date: string; count: number }>;
}

export interface DashboardDailyResponse {
  kind: 'daily';
  day: string; // YYYY-MM-DD (local)
  timezone: string;
  progress_last_2_days: {
    yesterday: { day: string; total: number; topics: AnalyticSummaryItem[]; departments: AnalyticSummaryItem[] };
    day2: { day: string; total: number; topics: AnalyticSummaryItem[]; departments: AnalyticSummaryItem[] };
  };
  forecast: {
    expected_total: number;
    topics: Array<{ id: number; name?: string | null; yesterday: number; day2: number; expected: number }>;
  };
  actual: { day: string; total: number; topics: AnalyticSummaryItem[]; departments: AnalyticSummaryItem[] };
  reconciliation?: { expected_total: number; actual_total: number; delta_total: number } | null;
  weather?: {
    status?: string;
    summary?: string;
    hourly?: Array<{
      time?: string;
      temp_c?: number | null;
      feels_like_c?: number | null;
      wind_speed_m_s?: number | null;
      wind_gust_m_s?: number | null;
      wind_dir_deg?: number | null;
      precip_mm?: number | null;
      precip_prob_pct?: number | null;
      weather_desc?: string | null;
      risk_tags?: string[];
    }>;
    highlights?: string[];
  };
  events?: {
    status?: string;
    categories?: Record<string, Array<{ summary?: string; when?: string; source?: string; link?: string | null; created_at?: string; category?: string }>>;
    items?: Array<{ summary?: string; when?: string; source?: string; link?: string | null; created_at?: string; category?: string }>;
  };
  residents?: {
    date: string;
    new_total: number;
    in_work_total: number;
    overdue_total: number;
    top_clusters: Array<{ cluster_key: string; address: string; size: number; ticket_ids: number[] }>;
  };
  execution?: {
    date: string;
    opened_total: number;
    closed_total: number;
    overdue_total: number;
    responsibles: Array<{ responsible_user_id: number | null; overdue: number }>;
  };
  daily_brief?: {
    version: 'DailyBriefV1';
    date: string;
    tenant_id: string;
    yesterday: {
      overview: string[];
      incidents: Array<{ title: string; category: string; severity: string; status: string; evidence_ids: string[] }>;
      volumes: Record<string, number>;
      operations: Array<{ text: string; evidence_ids: string[] }>;
    };
    today: {
      overview: string[];
      risk_forecast: Array<{ category: string; area: string; probability: 'low' | 'med' | 'high'; rationale: string; evidence_ids: string[] }>;
      planned_operations: Array<{ text: string; task_id: number | null; evidence_ids: string[] }>;
    };
    weather: { hourly_table: Array<Record<string, unknown>>; impact_hints: string[] };
    alerts: { mchs: Array<{ text: string; evidence_ids: string[] }> };
    actions: { top_actions: Array<{ text: string; task_id: number | null; priority: 'low' | 'med' | 'high'; evidence_ids: string[] }> };
    evidence_index: Record<string, { source_type: string; source_ref: Record<string, unknown>; snippet: string; ts: string }>;
    __llm_mode?: string;
  };
  cached?: boolean;
  _meta?: Record<string, unknown>;
}

export interface DashboardWeeklyResponse {
  kind: 'weekly';
  week_start: string; // YYYY-MM-DD (local, Monday)
  timezone: string;
  progress_last_2_weeks: {
    prev2: { week_start: string; total: number; topics: AnalyticSummaryItem[]; departments: AnalyticSummaryItem[] };
    prev: { week_start: string; total: number; topics: AnalyticSummaryItem[]; departments: AnalyticSummaryItem[] };
  };
  forecast: {
    expected_total: number;
    topics: Array<{ id: number; name?: string | null; prev_week: number; prev2_week: number; expected: number }>;
  };
  actual: { week_start: string; total: number; topics: AnalyticSummaryItem[]; departments: AnalyticSummaryItem[] };
  reconciliation?: { expected_total: number; actual_total: number; delta_total: number } | null;
  weather?: {
    status?: string;
    summary?: string;
    highlights?: string[];
  };
  events?: {
    status?: string;
    categories?: Record<string, Array<{ summary?: string; when?: string; source?: string; link?: string | null; created_at?: string; category?: string }>>;
    items?: Array<{ summary?: string; when?: string; source?: string; link?: string | null; created_at?: string; category?: string }>;
  };
  cached?: boolean;
  _meta?: Record<string, unknown>;
}

export interface DashboardMonthlyResponse {
  kind: 'monthly';
  month: string; // YYYY-MM
  timezone: string;
  sourcePeriod?: { from: string; to: string; sourceMonthKey?: string };
  input?: Record<string, unknown> | null;
  report?:
    | (Record<string, unknown> & {
        version: 'MonthlyCityForecastV1';
        cityCode: string;
        monthKey: string;
        overview: string;
      })
    | null;
  cached?: boolean;
  _meta?: Record<string, unknown>;
}

export const mapService = {
  getConfig: async (): Promise<MapConfig> => {
    const response = await api.get<MapConfig>('/api/map/config');
    return response.data;
  },
  getTasks: async (
    params: {
      date_from?: string;
      date_to?: string;
      city_id?: string;
      bbox?: string;
      zoom?: number;
      mode?: 'auto' | 'aggregate' | 'cluster' | 'point';
      offset?: number;
      limit?: number;
      geocode?: boolean;
      geocode_limit?: number;
      status_ids?: string;
      topic_ids?: string;
      department_ids?: string;
      management_company_ids?: string;
      ticket_ids?: string;
      road_only?: boolean;
      only_with_road_segments?: boolean;
      only_with_trajectories?: boolean;
      response_profile?: 'full' | 'summary';
      include_meta?: boolean;
      include_dialog_preview?: boolean;
    },
    options?: { signal?: AbortSignal }
  ): Promise<MapTasksResponse> => {
    const response = await api.get<MapTasksResponse>('/api/map/tasks', { params, signal: options?.signal });
    return response.data;
  },
};

function downloadBlob(filename: string, data: Blob) {
  const url = window.URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

async function blobApiError(error: unknown): Promise<Error> {
  if (!axios.isAxiosError(error) || !(error.response?.data instanceof Blob)) {
    return error instanceof Error ? error : new Error('Не удалось сформировать выгрузку.');
  }
  try {
    const payload = JSON.parse(await error.response.data.text()) as { detail?: string | { message?: string } };
    const detail = payload.detail;
    return new Error(typeof detail === 'string' ? detail : detail?.message || 'Не удалось сформировать выгрузку.');
  } catch {
    return new Error('Не удалось сформировать выгрузку.');
  }
}

function buildAuthHeaders() {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : undefined;
}

export const exportService = {
  getCapabilities: async (params: { date_from?: string; date_to?: string }): Promise<ExportCapabilitiesResponse> => {
    const response = await api.get<ExportCapabilitiesResponse>('/api/export/capabilities', { params });
    return response.data;
  },
  downloadConfiguredCsv: async (params: Record<string, string | undefined>, filename: string): Promise<number> => {
    try {
      const response = await api.get('/api/export/configured.csv', {
        params,
        headers: buildAuthHeaders(),
        responseType: 'blob',
      });
      downloadBlob(filename, response.data);
      return Number(response.headers['x-export-rows'] ?? 0);
    } catch (error) {
      throw await blobApiError(error);
    }
  },
  downloadTickets: async (params: { date_from?: string; date_to?: string }) => {
    const response = await api.get('/api/export/tickets', {
      params,
      headers: buildAuthHeaders(),
      responseType: 'blob',
    });
    downloadBlob('tickets_export.csv', response.data);
  },
  downloadMessages: async (params: { date_from?: string; date_to?: string }) => {
    const response = await api.get('/api/export/messages', {
      params,
      headers: buildAuthHeaders(),
      responseType: 'blob',
    });
    downloadBlob('messages_export.csv', response.data);
  },
  downloadExpertResidentMessagesByTickets: async (params: {
    date_from?: string;
    date_to?: string;
    limit?: number;
    status_id?: number;
    topic_ids?: string;
    department_ids?: string;
    ticket_ids?: string;
  }) => {
    const response = await api.get('/api/export/expert/tickets/resident_messages', {
      params,
      headers: buildAuthHeaders(),
      responseType: 'blob',
    });
    downloadBlob('expert_resident_messages_by_ticket.csv', response.data);
  },
  getTicketsPreview: async (params: {
    date_from?: string;
    date_to?: string;
    period_basis?: 'new' | 'activity';
    source_type?: string;
    source_types?: string;
    source_channel_ids?: string;
    private_chat?: string;
    chat_operator?: string;
    spam?: string;
    limit?: number;
    offset?: number;
    status_id?: number;
    status_type?: string;
    topic_id?: number;
    topic_ids?: string;
    department_id?: number;
    department_ids?: string;
    department_topic_scope?: string;
    search?: string;
  }): Promise<ExportTicketPreviewResponse> => {
    const response = await api.get<ExportTicketPreviewResponse>('/api/export/tickets/full', { params });
    return response.data;
  },
  getMessagesPreview: async (params: {
    date_from?: string;
    date_to?: string;
    limit?: number;
    offset?: number;
    source_type?: string;
    source_channel_id?: string;
    author_id?: string;
    private_chat?: string;
    chat_operator?: string;
    spam?: string;
    status_id?: number;
    status_type?: string;
    department_id?: number;
    department_ids?: string;
    department_topic_scope?: string;
    topic_id?: number;
    topic_ids?: string;
    ticket_id?: number;
    ticket_ids?: string;
    include_media_only?: boolean | string;
    has_media?: string;
    without_task?: string;
    search?: string;
  }): Promise<ExportMessagePreviewResponse> => {
    const response = await api.get<ExportMessagePreviewResponse>('/api/export/messages/full', { params });
    return response.data;
  },
};

export const analyticsService = {
  catalog: async (): Promise<AnalyticsCatalogResponse> => {
    const response = await api.get<AnalyticsCatalogResponse>('/api/analytics/catalog');
    return response.data;
  },
  query: async (payload: AnalyticsQueryRequest): Promise<AnalyticsQueryResponse> => {
    const response = await api.post<AnalyticsQueryResponse>('/api/analytics/query', payload);
    return response.data;
  },
  compare: async (payload: AnalyticsCompareRequest): Promise<AnalyticsCompareResponse> => {
    const response = await api.post<AnalyticsCompareResponse>('/api/analytics/compare', payload);
    return response.data;
  },
  exportQuery: async (payload: AnalyticsQueryRequest, filename = 'analytics_query.csv'): Promise<void> => {
    const response = await api.post('/api/analytics/export', {
      kind: 'query',
      format: 'csv',
      query: payload,
    }, {
      headers: buildAuthHeaders(),
      responseType: 'blob',
    });
    downloadBlob(filename, response.data);
  },
  exportCompare: async (payload: AnalyticsCompareRequest, filename = 'analytics_compare.csv'): Promise<void> => {
    const response = await api.post('/api/analytics/export', {
      kind: 'compare',
      format: 'csv',
      compare: payload,
    }, {
      headers: buildAuthHeaders(),
      responseType: 'blob',
    });
    downloadBlob(filename, response.data);
  },
  listPresets: async (preset_type?: string): Promise<{ items: AnalyticsPreset[] }> => {
    const response = await api.get<{ items: AnalyticsPreset[] }>('/api/analytics/presets', {
      params: preset_type ? { preset_type } : undefined,
    });
    return response.data;
  },
  createPreset: async (payload: {
    name: string;
    preset_type: 'overview' | 'compare';
    visibility?: 'private' | 'tenant';
    payload: Record<string, unknown>;
  }): Promise<AnalyticsPreset> => {
    const response = await api.post<AnalyticsPreset>('/api/analytics/presets', payload);
    return response.data;
  },
  drilldown: async (params: {
    entity?: 'message' | 'ticket';
    date_from?: string;
    date_to?: string;
    source_type?: string;
    limit?: number;
  }): Promise<AnalyticsDrilldownResponse> => {
    const response = await api.get<AnalyticsDrilldownResponse>('/api/analytics/drilldown', { params });
    return response.data;
  },
  canonicalResidentMetrics: async (payload: {
    date_range: { from: string; to: string };
    filters?: Record<string, unknown[] | unknown>;
  }): Promise<AnalyticsCanonicalResidentMetricsResponse> => {
    const response = await api.post<AnalyticsCanonicalResidentMetricsResponse>('/api/analytics/canonical-resident-metrics', payload);
    return response.data;
  },
  weeklyCityLifeData: async (payload: {
    date_range: { from: string; to: string };
    filters?: Record<string, unknown[] | unknown>;
    drilldown_context?: Record<string, unknown>;
    report_type?: string;
  }): Promise<WeeklyCityLifeReportDataResponse> => {
    const response = await api.post<WeeklyCityLifeReportDataResponse>('/api/analytics/weekly-city-life', payload);
    return response.data;
  },
  weeklyCityLifeReports: async (params?: { report_type?: string; limit?: number }): Promise<WeeklyCityLifeReportListResponse> => {
    const response = await api.get<WeeklyCityLifeReportListResponse>('/api/analytics/weekly-city-life/reports', { params });
    return response.data;
  },
  generateWeeklyCityLifeReport: async (payload: {
    date_range: { from: string; to: string };
    filters?: Record<string, unknown[] | unknown>;
    drilldown_context?: Record<string, unknown>;
    report_type?: string;
    report_style?: string;
    force?: boolean;
    at?: string | null;
  }): Promise<WeeklyCityLifeReportManifest> => {
    const response = await api.post<WeeklyCityLifeReportManifest>('/api/analytics/weekly-city-life/reports/generate', payload);
    return response.data;
  },
  weeklyCityLifeReportDetails: async (reportId: string): Promise<{ report: WeeklyCityLifeReportManifest; data: WeeklyCityLifeReportDataResponse | null }> => {
    const response = await api.get<{ report: WeeklyCityLifeReportManifest; data: WeeklyCityLifeReportDataResponse | null }>(`/api/analytics/weekly-city-life/reports/${encodeURIComponent(reportId)}`);
    return response.data;
  },
  weeklyCityLifeReportDownloadUrl: (reportId: string): string => `/api/analytics/weekly-city-life/reports/${encodeURIComponent(reportId)}/download`,
  weeklyCityLifeReportHtmlUrl: (reportId: string): string => `/api/analytics/weekly-city-life/reports/${encodeURIComponent(reportId)}/html`,
  downloadWeeklyCityLifePdf: async (reportId: string, filename = 'weekly-city-life.pdf'): Promise<void> => {
    const response = await api.get(`/api/analytics/weekly-city-life/reports/${encodeURIComponent(reportId)}/download`, {
      headers: buildAuthHeaders(),
      responseType: 'blob',
    });
    downloadBlob(filename, response.data);
  },
  cityLifeReportCenterSchedules: async (): Promise<CityLifeReportCenterSchedulesResponse> => {
    const response = await api.get<CityLifeReportCenterSchedulesResponse>('/api/analytics/city-life/report-center/schedules');
    return response.data;
  },
  updateCityLifeReportCenterSchedules: async (payload: { items: Array<Partial<CityLifeReportCenterSchedule> & { preset_type: string }> }): Promise<CityLifeReportCenterSchedulesResponse> => {
    const response = await api.put<CityLifeReportCenterSchedulesResponse>('/api/analytics/city-life/report-center/schedules', payload);
    return response.data;
  },
  cityLifeReportCenterReports: async (params: { date?: string | null; limit?: number }): Promise<CityLifeReportCenterReportsResponse> => {
    const cleanParams: Record<string, string | number> = {};
    if (params.date) cleanParams.date = params.date;
    if (params.limit) cleanParams.limit = params.limit;
    const response = await api.get<CityLifeReportCenterReportsResponse>('/api/analytics/city-life/report-center/reports', { params: cleanParams });
    return response.data;
  },
  downloadCityLifeReportCenterFile: async (url: string, filename: string): Promise<void> => {
    const response = await api.get(url, {
      headers: buildAuthHeaders(),
      responseType: 'blob',
    });
    downloadBlob(filename, response.data);
  },
  fetchCityLifeReportCenterFile: async (url: string): Promise<Blob> => {
    const response = await api.get(url, {
      headers: buildAuthHeaders(),
      responseType: 'blob',
    });
    const contentType = String(response.headers['content-type'] || response.data?.type || '').toLowerCase();
    if (!contentType.startsWith('text/html')) {
      throw new Error('Unexpected report artifact content type');
    }
    return response.data;
  },
  createCityLifeReportCenterJob: async (payload: {
    report_type: string;
    period: { from: string; to: string };
    comparison_mode?: string;
    comparison_period?: { from: string; to: string } | null;
    outputs?: { executive?: boolean; full?: boolean };
    report_style?: string;
    filters?: Record<string, unknown>;
    analysis_profile?: string;
  }): Promise<{ version: string; job: CityLifeReportCenterJob; next_action: string }> => {
    const response = await api.post<{ version: string; job: CityLifeReportCenterJob; next_action: string }>('/api/analytics/city-life/report-center/jobs', payload);
    return response.data;
  },
  cityLifeReportCenterJob: async (jobId: string): Promise<{ version: string; job: CityLifeReportCenterJob }> => {
    const response = await api.get<{ version: string; job: CityLifeReportCenterJob }>(`/api/analytics/city-life/report-center/jobs/${encodeURIComponent(jobId)}`);
    return response.data;
  },
};

export const kpiService = {
  regionCities: async (): Promise<KpiRegionCitiesResponse> => {
    const response = await api.get<KpiRegionCitiesResponse>('/api/kpi/region/cities');
    return response.data;
  },
  regionPulse: async (
    params?: { date_from?: string; date_to?: string },
    options?: { signal?: AbortSignal },
  ): Promise<KpiRegionPulseResponse> => {
    const response = await api.get<KpiRegionPulseResponse>('/api/kpi/region/pulse', {
      params,
      signal: options?.signal,
    });
    return response.data;
  },
  regionGraph: async (params?: { metric?: string; tenant_ids?: string[]; date_from?: string; date_to?: string }): Promise<KpiRegionGraphResponse> => {
    const search = new URLSearchParams();
    Object.entries(params ?? {}).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach((item) => search.append(key, String(item)));
      } else {
        search.append(key, String(value));
      }
    });
    const query = search.toString();
    const response = await api.get<KpiRegionGraphResponse>(`/api/kpi/region/graph${query ? `?${query}` : ''}`);
    return response.data;
  },
  regionCityDetail: async (params: {
    tenant_id: string;
    date_from?: string;
    date_to?: string;
    department?: string;
    topic?: string;
    locality?: string;
    street?: string;
  }, options?: { signal?: AbortSignal }): Promise<KpiRegionCityDetailResponse> => {
    const response = await api.get<KpiRegionCityDetailResponse>('/api/kpi/region/city-detail', {
      params,
      signal: options?.signal,
    });
    return response.data;
  },
  regionTasks: async (params: {
    tenant_id: string;
    date_from?: string;
    date_to?: string;
    department?: string;
    topic?: string;
    locality?: string;
    street?: string;
    limit?: number;
    offset?: number;
  }, options?: { signal?: AbortSignal }): Promise<KpiRegionTasksResponse> => {
    const response = await api.get<KpiRegionTasksResponse>('/api/kpi/region/tasks', {
      params,
      signal: options?.signal,
    });
    return response.data;
  },
  catalog: async (params?: { tenant_id?: string }): Promise<KpiCatalogResponse> => {
    const response = await api.get<KpiCatalogResponse>('/api/kpi/catalog', { params });
    return response.data;
  },
  overview: async (params?: { tenant_id?: string; date_from?: string; date_to?: string }): Promise<KpiOverviewResponse> => {
    const response = await api.get<KpiOverviewResponse>('/api/kpi/overview', { params });
    return response.data;
  },
  query: async (params: { metric: string; tenant_id?: string; date_from?: string; date_to?: string }): Promise<KpiQueryResponse> => {
    const response = await api.get<KpiQueryResponse>('/api/kpi/query', { params });
    return response.data;
  },
  compare: async (params: { metric: string; tenant_id?: string; date_from?: string; date_to?: string }): Promise<KpiCompareResponse> => {
    const response = await api.get<KpiCompareResponse>('/api/kpi/compare', { params });
    return response.data;
  },
  executivePulse: async (params?: { tenant_id?: string; date_from?: string; date_to?: string; granularity?: string; comparison_periods?: string[] }, options?: { signal?: AbortSignal }): Promise<KpiExecutivePulseResponse> => {
    const search = new URLSearchParams();
    Object.entries(params ?? {}).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach((item) => search.append(key, String(item)));
      } else {
        search.append(key, String(value));
      }
    });
    const query = search.toString();
    const response = await api.get<KpiExecutivePulseResponse>(`/api/kpi/executive/pulse${query ? `?${query}` : ''}`, { signal: options?.signal });
    return response.data;
  },
  executiveGraph: async (params?: {
    tenant_id?: string;
    date_from?: string;
    date_to?: string;
    granularity?: string;
    metrics?: string[];
    statuses?: string[];
    departments?: string[];
    themes?: string[];
    localities?: string[];
    streets?: string[];
    sources?: string[];
    tags?: string[];
    infopole_mode?: string;
    include_parent_locality_total?: boolean;
    show_previous_period?: boolean;
    chart_type?: string;
    value_mode?: string;
    show_point_labels?: boolean;
    comparison_periods?: string[];
    option_profile?: 'compact' | 'full' | string;
  }, options?: { signal?: AbortSignal }): Promise<KpiExecutiveGraphResponse> => {
    const search = new URLSearchParams();
    Object.entries(params ?? {}).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach((item) => search.append(key, String(item)));
      } else {
        search.append(key, String(value));
      }
    });
    const query = search.toString();
    const response = await api.get<KpiExecutiveGraphResponse>(`/api/kpi/executive/graph${query ? `?${query}` : ''}`, { signal: options?.signal });
    return response.data;
  },
  executiveDrilldown: async (params: {
    block_id: string;
    entity_type: string;
    entity_id: string;
    entity_label?: string;
    tenant_id?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<KpiExecutiveDrilldownResponse> => {
    const response = await api.get<KpiExecutiveDrilldownResponse>('/api/kpi/executive/drilldown', { params });
    return response.data;
  },
  exportManifest: async (params?: { tenant_id?: string }): Promise<{ version: string; tenant_id: string; items: KpiExportManifestItem[] }> => {
    const response = await api.get('/api/kpi/export-manifest', { params });
    return response.data;
  },
  drilldownManifest: async (params?: { tenant_id?: string }): Promise<{ version: string; tenant_id: string; items: KpiDrilldownManifestItem[] }> => {
    const response = await api.get('/api/kpi/drilldown-manifest', { params });
    return response.data;
  },
  executiveCardDetail: async (params: {
    card_id: string;
    tab?: string;
    tenant_id?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<KpiExecutiveCardDetailResponse> => {
    const response = await api.get<KpiExecutiveCardDetailResponse>('/api/kpi/executive/card-detail', { params });
    return response.data;
  },
  executiveTaskQualityIssues: async (params?: {
    tenant_id?: string;
    date_from?: string;
    date_to?: string;
    issue_type?: string;
    limit?: number;
  }): Promise<KpiTaskQualityIssuesResponse> => {
    const response = await api.get<KpiTaskQualityIssuesResponse>('/api/kpi/executive/task-quality-issues', { params });
    return response.data;
  },
};

export const analyticService = {
  getSummary: async (params: { date_from?: string; date_to?: string }): Promise<AnalyticSummary> => {
    const response = await api.get<AnalyticSummary>('/api/analytic/summary', { params });
    return response.data;
  },
  getSourceOptions: async (): Promise<{ items: AnalyticSourceOption[]; total: number }> => {
    const response = await api.get<{ items: AnalyticSourceOption[]; total: number }>('/api/analytic/source_options');
    return response.data;
  },
  getReport: async (params: {
    date_from?: string;
    date_to?: string;
    source_type?: string;
    source_types?: string;
    source_channel_ids?: string;
    private_chat?: string;
    chat_operator?: string;
    spam?: string;
    top_limit?: number;
    risk_threshold?: number;
    idle_hours?: number;
    whitelist?: string;
  }): Promise<AnalyticReport> => {
    const response = await api.get<AnalyticReport>('/api/analytic/report', { params });
    return response.data;
  },
  getSubtopics: async (payload: {
    topic_id?: number;
    department_id?: number;
    date_from?: string;
    date_to?: string;
    source_types?: string;
    source_channel_ids?: string;
    private_chat?: string;
    chat_operator?: string;
    spam?: string;
    scope_hash?: string;
    top_limit?: number;
    risk_threshold?: number;
    idle_hours?: number;
    whitelist?: string;
    source_counting_mode?: 'business_conversation' | 'raw_rows';
    max_tickets?: number;
    cache_ttl_min?: number;
    force?: boolean;
  }): Promise<AnalyticSubtopicResponse> => {
    const response = await api.post<AnalyticSubtopicResponse>('/api/analytic/subtopics/generate', payload);
    return response.data;
  },
  getTicketEditorActions: async (params: { date_from?: string; date_to?: string }): Promise<TicketEditorActionsReport> => {
    const response = await api.get<TicketEditorActionsReport>('/api/analytic/ticket_editor_actions', { params });
    return response.data;
  },
};

export const forecastService = {
  list: async (): Promise<ForecastListResponse> => {
    const response = await api.get<ForecastListResponse>('/api/forecast/list');
    return response.data;
  },
  report: async (month: string): Promise<ForecastReportResponse> => {
    const response = await api.get<ForecastReportResponse>('/api/forecast/report', { params: { month } });
    return response.data;
  },
};

export const dashboardService = {
  daily: async (params: { day: string; force?: boolean; cache_ttl_min?: number }): Promise<DashboardDailyResponse> => {
    const response = await api.get<DashboardDailyResponse>('/api/dashboard/daily', { params });
    return response.data;
  },
  dailyList: async (): Promise<{ days: string[] }> => {
    const response = await api.get<{ days: string[] }>('/api/dashboard/daily/list');
    return response.data;
  },
  dailyEvidence: async (params: { day: string; evidence_id: string }): Promise<{
    evidence_id: string;
    item: { source_type: string; source_ref: Record<string, unknown>; snippet: string; ts: string };
    detail: Record<string, unknown>;
  }> => {
    const response = await api.get('/api/dashboard/daily/evidence', { params });
    return response.data;
  },
  weekly: async (params: { day: string; force?: boolean; cache_ttl_min?: number }): Promise<DashboardWeeklyResponse> => {
    const response = await api.get<DashboardWeeklyResponse>('/api/dashboard/weekly', { params });
    return response.data;
  },
  weeklyList: async (): Promise<{ weeks: number[] }> => {
    const response = await api.get<{ weeks: number[] }>('/api/dashboard/weekly/list');
    return response.data;
  },
  monthly: async (params: { month: string; force?: boolean; cache_ttl_min?: number }): Promise<DashboardMonthlyResponse> => {
    const response = await api.get<DashboardMonthlyResponse>('/api/dashboard/monthly', { params });
    return response.data;
  },
  monthlyList: async (): Promise<{ months: string[] }> => {
    const response = await api.get<{ months: string[] }>('/api/dashboard/monthly/list');
    return response.data;
  },
};

export const messageService = {
  list: async (): Promise<Message[]> => {
    const response = await api.get<Message[]>('/api/messages');
    return response.data;
  },
  getSummary: async (id: number): Promise<MessageSummary> => (
    withSharedRequest(`messages:summary:${id}`, async () => {
      const response = await api.get<MessageSummary>(`/api/messages/${id}`);
      return response.data;
    })
  ),
  listInspect: async (options?: MessageInspectOptions): Promise<MessageSummary[]> => {
    const params = buildMessageInspectParams(options);
    const key = sharedKey('messages:inspect:list', params);
    return withSharedRequest(key, async () => {
      const response = await api.get<MessageSummary[]>('/api/messages/inspect', {
        params: Object.keys(params).length ? params : undefined,
      });
      return response.data;
    });
  },
  getConversation: async (
    threadId: number,
    options: { tenant_id?: string | null; vertical_code?: string | null; isCurrent: () => boolean },
  ): Promise<{ items: MessageSummary[]; complete: boolean }> => loadConversationPages(
    threadId,
    (page) => messageService.listInspect({ ...page, tenant_id: options.tenant_id,
      vertical_code: options.vertical_code, view: 'detail' }),
    options.isCurrent,
  ),
  feedOpen: async (options?: MessageFeedOpenOptions): Promise<FeedOpenResponse> => {
    const params = buildFeedOpenParams(options);
    const response = await api.get<FeedOpenResponse>('/api/messages/feed_open', {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  },
  openFeed: async (options?: MessageFeedOpenOptions): Promise<FeedOpenResponse> => (
    messageService.feedOpen(options)
  ),
  feedJump: async (options: MessageFeedJumpOptions): Promise<FeedJumpResponse> => {
    const params = buildFeedJumpParams(options);
    const response = await api.get<FeedJumpResponse>('/api/messages/feed_jump', {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  },
  feedDelta: async (options?: { cursor?: string | null; limit?: number }): Promise<FeedDeltaResponse> => {
    const params: Record<string, number | string> = {};
    if (options?.cursor) params.cursor = options.cursor;
    if (typeof options?.limit === 'number') params.limit = options.limit;
    const response = await api.get<FeedDeltaResponse>('/api/feed/delta', {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  },
  inspectAround: async (options: {
    message_id?: number;
    channel_message_id?: SourceIdValue;
    source_channel_id?: SourceIdValue;
    limit?: number;
  }): Promise<MessageSummary[]> => {
    const params: Record<string, number | string> = {};
    if (typeof options.message_id === 'number') params.message_id = options.message_id;
    const channelMessageId = toSourceIdString(options.channel_message_id);
    const sourceChannelId = toSourceIdString(options.source_channel_id);
    if (channelMessageId) params.channel_message_id = channelMessageId;
    if (sourceChannelId) params.source_channel_id = sourceChannelId;
    if (typeof options.limit === 'number') params.limit = options.limit;
    params.view = 'detail';
    return withSharedRequest(sharedKey('messages:inspect_around', params), async () => {
      const response = await api.get<MessageSummary[]>('/api/messages/inspect_around', { params });
      return response.data;
    });
  },
  jumpFeed: async (options: MessageFeedJumpOptions): Promise<FeedJumpResponse> => (
    messageService.feedJump(options)
  ),
  replyPreview: async (options: {
    source_channel_id: SourceIdValue;
    channel_message_id: SourceIdValue;
    provider?: 'max' | 'telegram';
    child_tenant_id?: string | null;
    child_id?: number;
    child_source_created_at?: string | null;
    child_message_uid?: string | null;
    child_source_type?: string | null;
  }): Promise<{
    source_channel_id: SourceIdValue;
    source_channel_id_text?: string | null;
    channel_message_id: SourceIdValue;
    channel_message_id_text?: string | null;
    author_name?: string | null;
    text_preview?: string | null;
    source_created_at?: string | null;
  } | null> => {
    const key = sharedKey('messages:reply_preview', options);
    const cachedEntry = replyPreviewCache.get(key);
    if (cachedEntry && Date.now() - cachedEntry.ts <= REPLY_PREVIEW_CACHE_TTL_MS) {
      return cachedEntry.data;
    }
    if (cachedEntry) {
      replyPreviewCache.delete(key);
    }
    return withSharedRequest(key, async () => {
      const response = await api.get('/api/messages/reply_preview', {
        params: {
          source_channel_id: toSourceIdString(options.source_channel_id),
          channel_message_id: toSourceIdString(options.channel_message_id),
          ...((options.provider === 'max' || options.provider === 'telegram') ? {
            provider: options.provider, child_tenant_id: options.child_tenant_id,
            child_id: options.child_id, child_source_created_at: options.child_source_created_at,
            child_message_uid: options.child_message_uid, child_source_type: options.child_source_type,
          } : {}),
        },
        validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
      });
      if (response.status === 404) {
        return setReplyPreviewTimedCache(replyPreviewCache, key, null);
      }
      const payload = (response.data ?? {}) as { missing_parent?: unknown };
      if (payload.missing_parent === true) {
        return setReplyPreviewTimedCache(replyPreviewCache, key, null);
      }
      return setReplyPreviewTimedCache(replyPreviewCache, key, response.data);
    });
  },
  inspect: async (
    id: number,
    options?: { force?: boolean; cache?: boolean }
  ): Promise<MessageInspector> => {
    const useCache = options?.cache !== false;
    if (!options?.force && useCache) {
      const cached = getMessageInspectorFromCache(id);
      if (cached) return cached;
    }
    const key = sharedKey('messages:inspect:detail', {
      id,
      force: options?.force === true,
      cache: useCache,
    });
    return withSharedRequest(key, async () => {
      const response = await api.get<MessageInspector>(`/api/messages/${id}/inspect`);
      if (useCache) {
        setMessageInspectorCache(id, response.data);
      }
      return response.data;
    });
  },
  getPlannedEventMatch: async (id: number): Promise<PlannedEventMatchDetail> => {
    const response = await api.get<PlannedEventMatchDetail>(`/api/messages/${id}/planned-event-match`);
    return response.data;
  },
  recomputePlannedEventMatch: async (id: number): Promise<PlannedEventMatchDetail> => {
    const response = await api.post<PlannedEventMatchDetail>(`/api/messages/${id}/planned-event-match/recompute`);
    return response.data;
  },
  approvePlannedEventReply: async (id: number, payload?: { note?: string }): Promise<PlannedEventMatchDetail> => {
    const response = await api.post<PlannedEventMatchDetail>(`/api/messages/${id}/planned-event-reply/approve`, payload ?? {});
    return response.data;
  },
  rejectPlannedEventReply: async (id: number, payload?: { reason?: string }): Promise<PlannedEventMatchDetail> => {
    const response = await api.post<PlannedEventMatchDetail>(`/api/messages/${id}/planned-event-reply/reject`, payload ?? {});
    return response.data;
  },
  pipelineCasePreview: async (id: number): Promise<Record<string, unknown>> => {
    const cached = getFreshTimedNumberCache(pipelineCasePreviewCache, id, PIPELINE_CASE_PREVIEW_CACHE_TTL_MS);
    if (cached) return cached;
    return withSharedRequest(sharedKey('messages:pipeline_case_preview', { id }), async () => {
      const response = await api.get<Record<string, unknown>>(`/api/messages/${id}/pipeline_case_preview`);
      return setTimedNumberCache(pipelineCasePreviewCache, id, response.data);
    });
  },
  fetchMediaOnDemand: async (id: number): Promise<{
    status: string;
    message_id: number;
    media_status?: string | null;
    media_fetch_mode?: string | null;
    media_on_demand_available?: boolean | null;
    has_media?: boolean | null;
    attachments_count?: number | null;
    media_items?: Array<Record<string, unknown>>;
    media_thumbs?: string[];
    media_urls?: string[];
  }> => {
    const response = await api.post(`/api/messages/${id}/media_on_demand`, {});
    return response.data;
  },
  startPipelineRecheckJob: async (payload: { message_ids: number[] }): Promise<{ status: string; job_id: string; reused?: boolean }> => {
    const response = await api.post('/api/messages/pipeline_recheck_jobs', payload);
    return response.data;
  },
  getPipelineRecheckJob: async (jobId: string): Promise<{
    job_id: string;
    status: 'queued' | 'running' | 'success' | 'error';
    progress?: { total_messages?: number; processed_messages?: number; current_message_id?: number | null };
    summary?: Record<string, number>;
    error?: string | null;
    warning?: string | null;
  }> => {
    const response = await api.get(`/api/messages/pipeline_recheck_jobs/${encodeURIComponent(jobId)}`);
    return response.data;
  },
  /** Подгрузить детали сообщений в кэш в фоне (при простое). */
  prefetchInspect: (messageIds: number[]): void => {
    const missing = messageIds.filter((id) => !getMessageInspectorFromCache(id));
    if (missing.length === 0) return;
    const run = () => {
      missing.forEach((id) => messageService.inspect(id).catch(() => {}));
    };
    if (typeof (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number }).requestIdleCallback !== 'undefined') {
      (window as unknown as { requestIdleCallback: (cb: () => void, opts?: { timeout?: number }) => number }).requestIdleCallback(run, { timeout: 3000 });
    } else {
      setTimeout(run, 200);
    }
  },
  reprocess: async (id: number): Promise<{ status: string }> => {
    const response = await api.post<{ status: string }>(`/api/messages/${id}/reprocess`);
    return response.data;
  },
  reviewModeration: async (
    id: number,
    payload: {
      action: 'clear_message' | 'ban_user' | 'mark_spam' | 'mark_not_spam';
      reason?: string;
      violation_scopes?: Array<'nickname' | 'text' | 'media'>;
      delete_task?: boolean;
    }
  ): Promise<{
    status: string;
    action: string;
    missing_llm_steps?: string[];
    violation_scopes?: string[];
    source_delete_state?: string | null;
    source_delete_confirmed?: boolean;
    spam_origin_deleted_ticket_ids?: number[];
    spam_origin_logically_deleted_ticket_ids?: number[];
    spam_origin_skipped_ticket_ids?: number[];
  }> => {
    const response = await api.post(`/api/messages/${id}/moderation/review`, payload);
    return response.data;
  },
  cleanupCard: async (
    id: number,
    payload: {
      clear_all?: boolean;
      clear_message_analysis?: boolean;
      clear_message_llm?: boolean;
      clear_message_audit?: boolean;
      clear_message_links?: boolean;
      clear_related_tickets?: boolean;
      clear_message_versions?: boolean;
      clear_media_enrichment?: boolean;
      clear_metadata?: boolean;
      reset_message_state?: boolean;
    }
  ): Promise<{
    status: string;
    message_id: number;
    message_analysis_deleted?: number;
    message_llm_deleted?: number;
    message_audit_deleted?: number;
    message_links_deleted?: number;
    message_versions_deleted?: number;
    media_enrichment_deleted?: number;
    tickets_deleted?: number;
    ticket_comments_deleted?: number;
    ticket_links_deleted?: number;
    ticket_llm_deleted?: number;
    ticket_audit_deleted?: number;
    ticket_delete_skipped_lock_timeout?: number;
    flags?: Record<string, boolean>;
  }> => {
    const response = await api.post(`/api/messages/${id}/cleanup_card`, payload);
    return response.data;
  },
  setPending: async (id: number): Promise<{ status: string; message_id?: number; classification_status?: string }> => {
    const response = await api.post<{ status: string; message_id?: number; classification_status?: string }>(`/api/messages/${id}/set_pending`);
    return response.data;
  },
  getClassificationStatuses: async (): Promise<string[]> => {
    const key = sharedKey('messages:classification_statuses');
    const cached = getFreshTimedCache(classificationStatusesCache, key, REFERENCE_CACHE_TTL_MS);
    if (cached) return cached;
    return withSharedRequest(key, async () => {
      try {
        const response = await api.get<{ statuses?: string[] }>('/api/messages/meta/classification-statuses');
        return setTimedCache(classificationStatusesCache, key, Array.isArray(response.data?.statuses) ? response.data.statuses : []);
      } catch {
        const response = await api.get<{ statuses?: string[] }>('/api/messages/classification-statuses');
        return setTimedCache(classificationStatusesCache, key, Array.isArray(response.data?.statuses) ? response.data.statuses : []);
      }
    });
  },
  setClassificationStatus: async (
    id: number,
    payload: { classification_status: string; reprocess_ai?: boolean }
  ): Promise<{ status: string; message_id?: number; classification_status?: string; updated?: number; reprocessed?: number }> => {
    const response = await api.post<{ status: string; message_id?: number; classification_status?: string; updated?: number; reprocessed?: number }>(
      `/api/messages/${id}/classification-status`,
      payload
    );
    return response.data;
  },
  bulkSetClassificationStatus: async (payload: {
    message_ids: number[];
    classification_status: string;
    reprocess_ai?: boolean;
  }): Promise<{
    status: string;
    classification_status?: string;
    updated?: number;
    reprocessed?: number;
    message_analysis_deleted?: number;
    message_llm_deleted?: number;
    message_links_deleted?: number;
    tickets_deleted?: number;
    ticket_comments_deleted?: number;
    ticket_links_deleted?: number;
    ticket_llm_deleted?: number;
    ticket_audit_deleted?: number;
  }> => {
    const response = await api.post<{
      status: string;
      classification_status?: string;
      updated?: number;
      reprocessed?: number;
      message_analysis_deleted?: number;
      message_llm_deleted?: number;
      message_links_deleted?: number;
      tickets_deleted?: number;
      ticket_comments_deleted?: number;
      ticket_links_deleted?: number;
      ticket_llm_deleted?: number;
      ticket_audit_deleted?: number;
    }>('/api/messages/meta/bulk/classification-status', payload);
    return response.data;
  },
  previewPipeline: async (id: number): Promise<{
    status: string;
    before: unknown;
    after: unknown;
    changes: Array<{ path: string; before: unknown; after: unknown }>;
    missing_llm_steps?: string[];
  }> => {
    const response = await api.post(`/api/messages/${id}/pipeline/preview`);
    return response.data;
  },
  applyPipeline: async (id: number): Promise<{
    status: string;
    before: unknown;
    after: unknown;
    changes: Array<{ path: string; before: unknown; after: unknown }>;
    missing_llm_steps?: string[];
  }> => {
    const response = await api.post(`/api/messages/${id}/pipeline/apply`);
    return response.data;
  },
  reprocessModeration: async (id: number): Promise<{ status: string }> => {
    const response = await api.post<{ status: string }>(`/api/messages/${id}/moderation`);
    return response.data;
  },
  reprocessDepartmentTop3: async (id: number): Promise<{ status: string }> => {
    const response = await api.post<{ status: string }>(`/api/messages/${id}/analysis/departmentTop3`);
    return response.data;
  },
  reprocessTopicAndExtraction: async (id: number): Promise<{ status: string }> => {
    const response = await api.post<{ status: string }>(`/api/messages/${id}/analysis/topicAndExtraction`);
    return response.data;
  },
  reprocessResolutionSignal: async (id: number): Promise<{ status: string }> => {
    const response = await api.post<{ status: string }>(`/api/messages/${id}/analysis/resolutionSignal`);
    return response.data;
  },
  inspectByUid: async (
    messageUid: string,
    options?: { signal?: AbortSignal },
  ): Promise<MessageInspector> => {
    const response = await api.get<MessageInspector>(
      `/api/messages/by_uid/${messageUid}/inspect`,
      { signal: options?.signal },
    );
    return response.data;
  },
  delete: async (id: number): Promise<{ status: string }> => {
    const response = await api.delete<{ status: string }>(`/api/messages/${id}`);
    return unwrapTransportResponse(response.data);
  },
  getDeleteCapabilities: async (id: number): Promise<MessageDeleteCapabilities> => {
    const response = await api.get<MessageDeleteCapabilities>(`/api/messages/${id}/delete-capabilities`);
    return response.data;
  },
  getReplyCapabilities: async (id: number): Promise<MessageReplyCapabilities> => {
    const response = await api.get<MessageReplyCapabilities>(`/api/messages/${id}/reply-capabilities`);
    return response.data;
  },
  getReplyDeliveries: async (id: number): Promise<MessageReplyDeliveryStatus[]> => {
    const response = await api.get<{ items: MessageReplyDeliveryStatus[] }>(`/api/messages/${id}/reply-deliveries`);
    return response.data.items;
  },
  deleteWithMode: async (
    id: number,
    payload: { mode: MessageDeleteMode; delete_empty_ticket_ids?: number[] },
  ): Promise<MessageDeleteExecutionResult> => {
    const response = await api.post<MessageDeleteExecutionResult>(`/api/messages/${id}/delete`, payload);
    return unwrapTransportResponse(response.data);
  },
  getHistory: async (id: number): Promise<unknown[]> => {
    const response = await api.get<unknown[]>(`/api/messages/${id}/history`);
    return response.data ?? [];
  },
  reply: async (
    id: number,
    payload: {
      content: string;
      tickets_ids?: number[];
      attachments?: File[];
      idempotency_key?: string;
      platform_default_confirmation?: {
        accepted: boolean;
        suppress_today?: boolean;
        scope_key?: string | null;
      };
    }
  ): Promise<MessageReplyExecutionResult> => {
    const attachments = payload.attachments === undefined ? [] : payload.attachments;
    if (!Array.isArray(attachments) || attachments.some((item) => !(item instanceof File) || item.size <= 0)) {
      throw new Error('Не удалось подготовить вложение. Выберите файл повторно.');
    }
    if (attachments.length > 0) {
      const form = new FormData();
      form.set('content', payload.content ?? '');
      form.set('attachment_count', String(attachments.length));
      if (payload.idempotency_key) form.set('idempotency_key', payload.idempotency_key);
      if (Array.isArray(payload.tickets_ids) && payload.tickets_ids.length > 0) {
        form.set('tickets_ids', JSON.stringify(payload.tickets_ids));
      }
      if (payload.platform_default_confirmation) {
        form.set('platform_default_confirmation', JSON.stringify(payload.platform_default_confirmation));
      }
      attachments.forEach((file) => form.append('media', file));
      const response = await api.post<MessageReplyExecutionResult>(`/api/messages/${id}/reply`, form, {
        // Override the JSON default; Axios/browser supplies the multipart boundary.
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return unwrapTransportResponse(response.data);
    }
    const body: {
      content: string;
      tickets_ids?: number[];
      idempotency_key?: string;
      platform_default_confirmation?: {
        accepted: boolean;
        suppress_today?: boolean;
        scope_key?: string | null;
      };
    } = {
      content: payload.content,
      tickets_ids: payload.tickets_ids,
      idempotency_key: payload.idempotency_key,
    };
    if (payload.platform_default_confirmation) {
      body.platform_default_confirmation = payload.platform_default_confirmation;
    }
    const response = await api.post<MessageReplyExecutionResult>(`/api/messages/${id}/reply`, body);
    return unwrapTransportResponse(response.data);
  },
  filter: async (payload: {
    filter?: Record<string, unknown>;
    records_per_page?: number;
    current_page?: number;
  }): Promise<{ messages: Message[]; total_records?: number; total_pages?: number }> => {
    const response = await api.post<{
      messages: Message[];
      total_records?: number;
      total_pages?: number;
    }>('/api/messages/filter', payload);
    return response.data;
  },
};

type MessageFeedFilterOptions = {
  filter?: 'all' | 'errors' | 'tickets';
  q?: string;
  ticket_id?: string;
  department_id?: number | null;
  topic_id?: number | null;
  locality_id?: number | null;
  district_id?: number | null;
  territorial_department_id?: number | null;
  street_id?: number | null;
  management_company_id?: number | null;
  channel_message_id?: number | null;
  author_id?: number | null;
  source_type?: string;
  source_selection?: string;
  source_channel_id?: number | null;
  thread_ref_message_id?: number | null;
  pipeline_ref_message_id?: number | null;
  source_instance?: string | null;
  tenant_id?: string | null;
  vertical_code?: string | null;
  classification_status?: string;
  has_error?: string;
  time_from?: string;
  time_to?: string;
  author_query?: string;
  private_chat?: string;
  chat_operator?: string;
  spam?: string;
  deleted?: string;
  manual_platform_blocked?: string;
  author_banned?: string;
  moderation_context?: string;
  in_tasks?: string;
  include_informational?: string;
  pipe_cases?: string;
  edited?: string;
  pipeline_case_size?: string;
  snapshot_max_id?: number | null;
};

type MessageInspectOptions = MessageFeedFilterOptions & {
  limit?: number;
  cursor?: number | null;
  cursor_ts?: string | null;
  cursor_direction?: 'older' | 'newer';
  profile?: string;
  view?: 'list' | 'detail' | 'feed';
};

type MessageFeedOpenOptions = MessageFeedFilterOptions & {
  limit?: number;
  warm_limit?: number;
};

type MessageFeedJumpOptions = MessageFeedFilterOptions & {
  message_id?: number | null;
  target_ts?: string | null;
  limit?: number;
};

function applyMessageFeedFilterParams(
  params: Record<string, number | string>,
  options?: MessageFeedFilterOptions,
): void {
  if (!options) return;
  if (options.filter && options.filter !== 'all') params.filter = options.filter;
  if (options.q) params.q = options.q;
  if (options.ticket_id) params.ticket_id = options.ticket_id;
  if (typeof options.department_id === 'number') params.department_id = options.department_id;
  if (typeof options.topic_id === 'number') params.topic_id = options.topic_id;
  if (typeof options.locality_id === 'number') params.locality_id = options.locality_id;
  if (typeof options.district_id === 'number') params.district_id = options.district_id;
  if (typeof options.territorial_department_id === 'number') params.territorial_department_id = options.territorial_department_id;
  if (typeof options.street_id === 'number') params.street_id = options.street_id;
  if (typeof options.management_company_id === 'number') params.management_company_id = options.management_company_id;
  if (typeof options.channel_message_id === 'number') params.channel_message_id = options.channel_message_id;
  if (typeof options.author_id === 'number') params.author_id = options.author_id;
  if (options.source_type) params.source_type = options.source_type;
  if (options.source_selection) params.source_selection = options.source_selection;
  if (typeof options.source_channel_id === 'number') params.source_channel_id = options.source_channel_id;
  if (typeof options.thread_ref_message_id === 'number') params.thread_ref_message_id = options.thread_ref_message_id;
  if (typeof options.pipeline_ref_message_id === 'number') params.pipeline_ref_message_id = options.pipeline_ref_message_id;
  if (options.source_instance) params.source_instance = options.source_instance;
  if (options.tenant_id) params.tenant_id = options.tenant_id;
  if (options.vertical_code) params.vertical_code = options.vertical_code;
  if (options.author_query) params.author_query = options.author_query;
  if (options.classification_status) params.classification_status = options.classification_status;
  if (options.has_error) params.has_error = options.has_error;
  if (options.time_from) params.time_from = options.time_from;
  if (options.time_to) params.time_to = options.time_to;
  if (options.private_chat) params.private_chat = options.private_chat;
  if (options.chat_operator) params.chat_operator = options.chat_operator;
  if (options.spam) params.spam = options.spam;
  if (options.deleted) params.deleted = options.deleted;
  if (options.manual_platform_blocked) params.manual_platform_blocked = options.manual_platform_blocked;
  if (options.author_banned) params.author_banned = options.author_banned;
  if (options.moderation_context) params.moderation_context = options.moderation_context;
  if (options.in_tasks) params.in_tasks = options.in_tasks;
  if (options.include_informational) params.include_informational = options.include_informational;
  if (options.pipe_cases) params.pipe_cases = options.pipe_cases;
  if (options.edited) params.edited = options.edited;
  if (options.pipeline_case_size) params.pipeline_case_size = options.pipeline_case_size;
  if (typeof options.snapshot_max_id === 'number') params.snapshot_max_id = options.snapshot_max_id;
}

function buildMessageInspectParams(options?: MessageInspectOptions): Record<string, number | string> {
  const params: Record<string, number | string> = {};
  if (typeof options?.limit === 'number') params.limit = options.limit;
  if (typeof options?.cursor === 'number') params.cursor = options.cursor;
  if (options?.cursor_ts) params.cursor_ts = options.cursor_ts;
  if (options?.cursor_direction) params.cursor_direction = options.cursor_direction;
  if (options?.profile) params.profile = options.profile;
  applyMessageFeedFilterParams(params, options);
  params.view = options?.view ?? 'detail';
  return params;
}

function buildFeedOpenParams(options?: MessageFeedOpenOptions): Record<string, number | string> {
  const params: Record<string, number | string> = {};
  if (typeof options?.limit === 'number') params.limit = options.limit;
  if (typeof options?.warm_limit === 'number') params.warm_limit = options.warm_limit;
  applyMessageFeedFilterParams(params, options);
  return params;
}

function buildFeedJumpParams(options: MessageFeedJumpOptions): Record<string, number | string> {
  const params: Record<string, number | string> = {};
  if (typeof options.message_id === 'number') params.message_id = options.message_id;
  if (options.target_ts) params.target_ts = options.target_ts;
  if (typeof options.limit === 'number') params.limit = options.limit;
  applyMessageFeedFilterParams(params, options);
  return params;
}

export const feedPreferencesService = {
  get: async (): Promise<FeedPreferences> => {
    const scope = currentFeedPreferencesScope();
    if (scope.locked) throw new Error('feed_preferences_reset_in_progress');
    const revision = scope.revision;
    const key = `ui:feed_preferences:${scope.id}`;
    const cached = getFreshTimedCache(feedPreferencesCache, key, UI_CACHE_TTL_MS);
    if (cached) return cached;
    return withSharedRequest(`${key}:get:${revision}`, async () => {
      const response = await api.get<FeedPreferences>('/api/ui/feed-preferences');
      return publishFeedPreferences(scope, revision, response.data);
    });
  },
  update: (payload: FeedPreferences): Promise<FeedPreferences> => {
    const scope = currentFeedPreferencesScope();
    if (scope.locked) return Promise.reject(new Error('feed_preferences_reset_in_progress'));
    const suppliedGeneration = payload.reset_generation;
    if (suppliedGeneration !== undefined && (!Number.isInteger(suppliedGeneration)
        || suppliedGeneration < 0 || suppliedGeneration > 2147483647)) {
      return Promise.reject(new Error('feed_preferences_reset_generation_conflict'));
    }
    // The queued body and its identity must own the same detached wire data.
    const requestPayload = JSON.parse(JSON.stringify({ ...payload, reset_generation: suppliedGeneration })) as FeedPreferences;
    const generation = requestPayload.reset_generation ?? scope.generation ?? 0;
    if (!Number.isInteger(generation) || generation < 0 || generation > 2147483647
        || (scope.generation !== null && generation !== scope.generation)
        || (scope.generation !== null && scope.generation > 0 && requestPayload.reset_generation === undefined)) {
      return Promise.reject(new Error('feed_preferences_reset_generation_conflict'));
    }
    const key = stableSerialize({ ...requestPayload, reset_generation: generation });
    if (scope.pending?.key === key) return scope.pending.promise;
    const cached = getFreshTimedCache(feedPreferencesCache, `ui:feed_preferences:${scope.id}`, UI_CACHE_TTL_MS);
    if (!scope.pending && cached && scope.completedWrite?.key === key
        && scope.completedWrite.revision === scope.revision
        && scope.completedWrite.responseKey === stableSerialize(cached)) {
      return Promise.resolve(cached);
    }
    const revision = ++scope.revision;
    feedPreferencesCache.delete(`ui:feed_preferences:${scope.id}`);
    const task = scope.tail.then(async () => {
      // A reset supersedes queued old intent before it can acquire a new generation.
      if (currentFeedPreferencesScope() !== scope || scope.locked) throw new Error('feed_preferences_obsolete_write');
      const body = { ...requestPayload };
      delete body.reset_generation;
      const response = await api.put<FeedPreferences>('/api/ui/feed-preferences', {
        ...body, expected_reset_generation: generation,
      });
      if (feedResetGeneration(response.data) !== generation) throw new Error('feed_preferences_reset_generation_conflict');
      const saved = publishFeedPreferences(scope, revision, response.data);
      scope.completedWrite = { key, revision, responseKey: stableSerialize(saved) };
      return saved;
    });
    scope.tail = task.then(() => undefined, () => undefined);
    const promise = task.finally(() => {
      if (scope.pending?.promise === promise) scope.pending = undefined;
    });
    scope.pending = { key, promise };
    return promise;
  },
  reset: (options: { lockUntilLogout?: boolean } = {}): Promise<FeedPreferences> => {
    const scope = currentFeedPreferencesScope();
    if (scope.resetPromise) {
      if (options.lockUntilLogout) scope.lockUntilLogout = true;
      return scope.resetPromise;
    }
    if (scope.locked) return Promise.reject(new Error('feed_preferences_reset_in_progress'));
    scope.locked = true;
    scope.lockUntilLogout = Boolean(options.lockUntilLogout);
    const revision = ++scope.revision;
    feedPreferencesCache.delete(`ui:feed_preferences:${scope.id}`);
    const deadline = performance.now() + 15000;
    const controller = new AbortController();
    let expired = false;
    let postIssued = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const check = () => {
      requireFeedPreferencesScope(scope, revision);
      if (expired || controller.signal.aborted || performance.now() >= deadline) {
        throw new Error('feed_preferences_reset_unverified_deadline');
      }
    };
    const timeout = new Promise<never>((_resolve, reject) => {
      timer = setTimeout(() => {
        expired = true;
        scope.lockUntilLogout = true;
        scope.locked = true;
        scope.revision += 1;
        controller.abort();
        reject(new Error('feed_preferences_reset_unverified_deadline'));
      }, Math.max(0, deadline - performance.now()));
    });
    const work = scope.tail.then(async () => {
      check();
      postIssued = true;
      const response = await api.post<FeedPreferences>('/api/ui/feed-preferences/reset', undefined, {
        signal: controller.signal, timeout: Math.max(1, deadline - performance.now()),
      });
      check();
      if (feedResetGeneration(response.data) <= 0) throw new Error('feed_preferences_reset_unverified');
      return publishFeedPreferences(scope, revision, response.data);
    });
    const task = Promise.race([work, timeout]);
    scope.tail = task.then(() => undefined, () => undefined);
    const promise = task.catch((error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (expired || (postIssued && !(typeof status === 'number' && status >= 400 && status < 500))) {
        // An issued reset without a confirmed response cannot unlock old intent.
        scope.lockUntilLogout = true;
        scope.locked = true;
        scope.revision += 1;
      }
      throw error;
    }).finally(() => {
      clearTimeout(timer);
      controller.abort();
      if (scope.resetPromise === promise) scope.resetPromise = undefined;
      if (!scope.lockUntilLogout) scope.locked = false;
    });
    scope.resetPromise = promise;
    return promise;
  },
  getReadPosition: async (scopeKey: string): Promise<{ scope_key: string; position: import('@/types').FeedReadPosition | null }> => {
    const scope = currentFeedPreferencesScope();
    if (scope.locked) throw new Error('feed_preferences_reset_in_progress');
    const revision = scope.revision;
    const response = await api.get('/api/ui/feed-read-position', { params: { scope_key: scopeKey } });
    requireFeedPreferencesScope(scope, revision);
    return response.data;
  },
  updateReadPosition: (
    scopeKey: string,
    messageId: number,
    expectedGeneration?: number,
  ): Promise<{ scope_key: string; position: import('@/types').FeedReadPosition }> => {
    const scope = currentFeedPreferencesScope();
    if (scope.locked) return Promise.reject(new Error('feed_preferences_reset_in_progress'));
    const generation = expectedGeneration ?? scope.generation ?? 0;
    if (!Number.isInteger(generation) || generation < 0 || generation > 2147483647
        || (scope.generation !== null && generation !== scope.generation)
        || (scope.generation !== null && scope.generation > 0 && expectedGeneration === undefined)) {
      return Promise.reject(new Error('feed_preferences_reset_generation_conflict'));
    }
    const revision = scope.revision;
    const task = scope.tail.then(async () => {
      if (currentFeedPreferencesScope() !== scope || scope.locked) throw new Error('feed_preferences_obsolete_write');
      const response = await api.put('/api/ui/feed-read-position', {
        scope_key: scopeKey, message_id: messageId, expected_reset_generation: generation,
      });
      requireFeedPreferencesScope(scope, revision);
      return response.data;
    });
    scope.tail = task.then(() => undefined, () => undefined);
    return task;
  },
};

export const messageWorkBellService = {
  getCommonState: async (): Promise<MessageWorkBellCommonState> => {
    return {
      mode: 'common',
      done_keys: [],
      updated_at: new Date().toISOString(),
    };
  },
  putCommonState: async (payload: MessageWorkBellCommonState): Promise<MessageWorkBellCommonState> => {
    return {
      mode: 'common',
      done_keys: Array.from(new Set((payload.done_keys ?? []).map((value) => String(value || '').trim()).filter(Boolean))),
      updated_at: payload.updated_at || new Date().toISOString(),
    };
  },
  markDoneCommonKeys: async (keys: string[]): Promise<MessageWorkBellCommonState> => {
    return {
      mode: 'common',
      done_keys: Array.from(new Set((keys ?? []).map((value) => String(value || '').trim()).filter(Boolean))),
      updated_at: new Date().toISOString(),
    };
  },
  setManualState: async (
    messageIds: number[],
    manualState: boolean,
  ): Promise<MessageWorkBellHandledResponse> => {
    const payload = {
      message_ids: Array.from(new Set((messageIds ?? []).map((value) => Number(value)).filter((value) => Number.isFinite(value) && value > 0))),
      source: 'manual' as const,
      manual_state: Boolean(manualState),
    };
    const response = await api.post<MessageWorkBellHandledResponse>('/api/messages/work-bell/handled', payload);
    return response.data;
  },
};

export type TelegramBotAccessAuditRow = {
  channel: {
    id: number;
    chat_id: number;
    label: string;
    username?: string | null;
    enabled: boolean;
  };
  chat?: {
    id?: number;
    type?: string;
    title?: string;
    username?: string;
  } | null;
  membership?: {
    status?: string;
    can_post_messages?: boolean | null;
    can_delete_messages?: boolean | null;
    can_manage_chat?: boolean | null;
  } | null;
  access_status: 'ok' | 'warning' | 'insufficient' | 'error';
  sufficient: boolean;
  missing: string[];
  hint?: string | null;
  error?: { message?: string; error?: string; hint?: string } | null;
};

export type TelegramBotAccessAudit = {
  bot: {
    id?: number | null;
    name: string;
    bot_username?: string | null;
    bot_user_id?: number | null;
    enabled: boolean;
    stored: boolean;
  };
  channels_total: number;
  ok_count: number;
  warning_count: number;
  insufficient_count: number;
  rows: TelegramBotAccessAuditRow[];
  instructions: string[];
};

export const sourcesService = {
  listTelegramChannels: async (): Promise<FeedTelegramChannelOption[]> => {
    const response = await api.get<FeedTelegramChannelOption[]>('/api/sources/telegram_channels');
    return response.data;
  },
  feedOptions: async (): Promise<FeedOptionsResponse> => {
    const key = sharedKey('sources:feed_options');
    const cached = getFreshTimedCache(feedOptionsCache, key, REFERENCE_CACHE_TTL_MS);
    if (cached) return cached;
    return withSharedRequest(key, async () => {
      const response = await api.get<FeedOptionsResponse>('/api/sources/feed_options');
      return setTimedCache(feedOptionsCache, key, response.data);
    });
  },
  peekFeedOptions: (): FeedOptionsResponse | null => {
    const key = sharedKey('sources:feed_options');
    return getFreshTimedCache(feedOptionsCache, key, REFERENCE_CACHE_TTL_MS);
  },
  listBots: async (): Promise<Array<{ id: number; name: string; bot_username?: string | null; bot_user_id?: number | null; enabled: boolean }>> => {
    const response = await api.get<Array<{ id: number; name: string; bot_username?: string | null; bot_user_id?: number | null; enabled: boolean }>>('/api/sources/bots');
    return response.data;
  },
  upsertBot: async (payload: { name: string; bot_token: string }): Promise<{ id: number; name: string; bot_username?: string | null; bot_user_id?: number | null; enabled: boolean }> => {
    const response = await api.post<{ id: number; name: string; bot_username?: string | null; bot_user_id?: number | null; enabled: boolean }>('/api/sources/bots', payload);
    return response.data;
  },
  validateBotToken: async (payload: { bot_token: string }): Promise<TelegramBotAccessAudit> => {
    const response = await api.post<TelegramBotAccessAudit>('/api/sources/bots/validate', payload);
    return response.data;
  },
  auditBotChannels: async (botId: number): Promise<TelegramBotAccessAudit> => {
    const response = await api.post<TelegramBotAccessAudit>(`/api/sources/bots/${botId}/audit_channels`);
    return response.data;
  },
  attachAccessibleChannels: async (botId: number): Promise<{ status: string; attached_channel_ids: number[]; audit: TelegramBotAccessAudit }> => {
    const response = await api.post<{ status: string; attached_channel_ids: number[]; audit: TelegramBotAccessAudit }>(`/api/sources/bots/${botId}/attach_accessible_channels`);
    return response.data;
  },
  updateBot: async (botId: number, payload: { name?: string; enabled?: boolean }): Promise<{ id: number; name: string; bot_username?: string | null; bot_user_id?: number | null; enabled: boolean }> => {
    const response = await api.patch<{ id: number; name: string; bot_username?: string | null; bot_user_id?: number | null; enabled: boolean }>(`/api/sources/bots/${botId}`, payload);
    return response.data;
  },
  listChannels: async (): Promise<Array<{ id: number; chat_id: number; display_name?: string | null; title?: string | null; username?: string | null; enabled: boolean; bots?: Array<{ id: number; name: string; bot_username?: string | null; enabled: boolean }> }>> => {
    const response = await api.get<Array<{ id: number; chat_id: number; display_name?: string | null; title?: string | null; username?: string | null; enabled: boolean; bots?: Array<{ id: number; name: string; bot_username?: string | null; enabled: boolean }> }>>('/api/sources/channels');
    return response.data;
  },
  upsertChannel: async (payload: { chat_id: number; display_name?: string | null; title?: string | null; username?: string | null; enabled?: boolean }): Promise<{ id: number; chat_id: number; display_name?: string | null; title?: string | null; username?: string | null; enabled: boolean }> => {
    const response = await api.post<{ id: number; chat_id: number; display_name?: string | null; title?: string | null; username?: string | null; enabled: boolean }>('/api/sources/channels', payload);
    return response.data;
  },
  deleteChannel: async (channelId: number): Promise<{ status: string; channel_id: number; chat_id: number }> => {
    const response = await api.delete<{ status: string; channel_id: number; chat_id: number }>(`/api/sources/channels/${channelId}`);
    return response.data;
  },
  attachBotToChannel: async (channelId: number, botId: number): Promise<{ status: string }> => {
    const response = await api.post<{ status: string }>(`/api/sources/channels/${channelId}/bots`, { bot_id: botId });
    return response.data;
  },
  testBotAccess: async (channelId: number, botId: number): Promise<{ ok: boolean; chat?: { id?: number; type?: string; title?: string; username?: string } }> => {
    const response = await api.post<{ ok: boolean; chat?: { id?: number; type?: string; title?: string; username?: string } }>(`/api/sources/channels/${channelId}/test_bot_access`, { bot_id: botId });
    return response.data;
  },
};

export interface MaxWebAppFeedbackRow {
  id: number;
  tenant_id: string;
  max_user_id: number;
  max_username?: string | null;
  max_first_name?: string | null;
  max_last_name?: string | null;
  max_chat_id: number;
  max_chat_type?: string | null;
  query_id: string;
  start_param?: string | null;
  form_last_name?: string | null;
  form_first_name?: string | null;
  form_middle_name?: string | null;
  form_phone?: string | null;
  form_address?: string | null;
  form_comment?: string | null;
  linked_ticket_id?: number | null;
  linked_message_id?: number | null;
  status: string;
  validated_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  raw_init_data_masked?: Record<string, unknown>;
}

export interface MaxWebAppContextRow {
  id: number;
  tenant_id: string;
  context_key: string;
  source_chat_id?: number | null;
  allowed_max_user_id?: number | null;
  linked_ticket_id?: number | null;
  linked_message_id?: number | null;
  prompt_text: string;
  button_text: string;
  request_message_mid?: string | null;
  request_deep_link?: string | null;
  access_expires_at?: string | null;
  status: string;
  created_by_user_id?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface MaxWebAppSessionPayload {
  initData: string;
  startParam?: string | null;
}

export interface MaxWebAppSessionInfo {
  ok: boolean;
  allowed: boolean;
  deny_reason?: string | null;
  tenant_id?: string | null;
  context: MaxWebAppContextRow;
  user?: {
    id: number;
    username?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  } | null;
  chat?: {
    id?: number | null;
    type?: string | null;
  } | null;
}

export interface MaxWebAppFeedbackSubmitPayload {
  initData: string;
  startParam?: string | null;
  form: {
    lastName?: string | null;
    firstName?: string | null;
    middleName?: string | null;
    phone?: string | null;
    address?: string | null;
    comment?: string | null;
  };
}

export interface MaxWebAppRequestCreatePayload {
  chat_id?: number | null;
  message_id?: number | null;
  ticket_id?: number | null;
  prompt_text?: string | null;
  button_text?: string | null;
}

export const maxWebAppService = {
  resolveSession: async (payload: MaxWebAppSessionPayload): Promise<MaxWebAppSessionInfo> => {
    const response = await api.post<MaxWebAppSessionInfo>('/api/max/webapp/session', payload);
    return response.data;
  },
  submitFeedback: async (
    payload: MaxWebAppFeedbackSubmitPayload,
  ): Promise<{ ok: boolean; duplicate?: boolean; feedback: MaxWebAppFeedbackRow }> => {
    const response = await api.post<{ ok: boolean; duplicate?: boolean; feedback: MaxWebAppFeedbackRow }>(
      '/api/max/webapp/feedback',
      payload,
    );
    return response.data;
  },
  listFeedback: async (params?: {
    ticket_id?: number;
    message_id?: number;
  }): Promise<MaxWebAppFeedbackRow[]> => {
    const response = await api.get<MaxWebAppFeedbackRow[]>('/api/max/webapp/feedback', { params });
    return response.data;
  },
  listRequests: async (): Promise<MaxWebAppContextRow[]> => {
    const response = await api.get<MaxWebAppContextRow[]>('/api/max/webapp/requests');
    return response.data;
  },
  createRequest: async (payload: MaxWebAppRequestCreatePayload): Promise<MaxWebAppContextRow> => {
    const response = await api.post<MaxWebAppContextRow>('/api/max/webapp/requests', payload);
    return response.data;
  },
  getAddressLocalities: async (params?: { startParam?: string | null; chatId?: number | null }): Promise<LocalityRef[]> => {
    const response = await api.get<LocalityRef[]>('/api/max/webapp/address/localities', {
      params: {
        startParam: params?.startParam ?? null,
        chatId: params?.chatId ?? null,
      },
    });
    return response.data;
  },
  getAddressStreets: async (
    localityId?: number | null,
    params?: { startParam?: string | null; chatId?: number | null },
  ): Promise<StreetRef[]> => {
    const response = await api.post<{ streets: StreetRef[] }>('/api/max/webapp/address/streets', {
      locality_id: localityId ?? null,
      startParam: params?.startParam ?? null,
      chatId: params?.chatId ?? null,
    });
    return Array.isArray(response.data?.streets) ? response.data.streets : [];
  },
  getAddressBuildings: async (
    streetId?: number | null,
    params?: { startParam?: string | null; chatId?: number | null },
  ): Promise<BuildingRef[]> => {
    const response = await api.post<{ buildings: BuildingRef[] }>('/api/max/webapp/address/buildings', {
      street_id: streetId ?? null,
      startParam: params?.startParam ?? null,
      chatId: params?.chatId ?? null,
    });
    return Array.isArray(response.data?.buildings) ? response.data.buildings : [];
  },
};
/** Отправка отладочного отчёта в Telegram разработчику (при отсутствии сообщения в БД и т.п.). */
export const debugService = {
  sendReport: async (text: string): Promise<void> => {
    await api.post('/api/debug/send-report', { text });
  },
};

type TelegramActionResult = {
  action?: string;
  attempted?: boolean;
  ok?: boolean;
  method?: string;
  status_code?: number | null;
  error?: string | null;
};

type TelegramActionResponse = {
  status: string;
  expires_at?: string | null;
  telegram?: TelegramActionResult;
};

export const telegramUserService = {
  directory: async (payload: {
    q?: string;
    channel_id?: number | null;
    only_active?: boolean;
    records_per_page?: number;
    current_page?: number;
  }): Promise<{
    telegram_users: TelegramUserModerationRow[];
    total_records?: number;
    total_pages?: number;
    current_page?: number;
    records_per_page?: number;
  }> => {
    const response = await api.post('/api/telegram_users_directory', payload);
    return response.data;
  },
  mute: async (
    id: number,
    payload: { channel_id: number; duration: number }
  ): Promise<TelegramActionResponse> => {
    const response = await api.post<TelegramActionResponse>(`/api/telegram_users/${id}/mute`, payload);
    return unwrapTransportResponse(response.data);
  },
  unmute: async (id: number, payload: { channel_id: number }): Promise<TelegramActionResponse> => {
    const response = await api.post<TelegramActionResponse>(`/api/telegram_users/${id}/unmute`, payload);
    return unwrapTransportResponse(response.data);
  },
  ban: async (
    id: number,
    payload: { channel_id: number; duration: number }
  ): Promise<TelegramActionResponse> => {
    const response = await api.post<TelegramActionResponse>(`/api/telegram_users/${id}/ban`, payload);
    return unwrapTransportResponse(response.data);
  },
  unban: async (id: number, payload: { channel_id: number }): Promise<TelegramActionResponse> => {
    const response = await api.post<TelegramActionResponse>(`/api/telegram_users/${id}/unban`, payload);
    return unwrapTransportResponse(response.data);
  },
};

export const sourceUserModerationService = {
  banMessageAuthor: async (
    messageId: number,
    payload: { duration?: number; reason?: string } = {}
  ): Promise<TelegramActionResponse> => {
    const response = await api.post<TelegramActionResponse>(`/api/messages/${messageId}/source_user/ban`, payload);
    return unwrapTransportResponse(response.data);
  },
  unbanMessageAuthor: async (messageId: number): Promise<TelegramActionResponse & { closed_count?: number }> => {
    const response = await api.post<TelegramActionResponse & { closed_count?: number }>(`/api/messages/${messageId}/source_user/unban`);
    return unwrapTransportResponse(response.data);
  },
};

export const telegramRolesService = {
  listOperators: async (): Promise<TelegramUserRole[]> => {
    const response = await api.get('/api/telegram_users/operators');
    return response.data.operators || [];
  },
  listOneOfUs: async (): Promise<TelegramUserRole[]> => {
    const response = await api.get('/api/telegram_users/one_of_us');
    return response.data.operators || [];
  },
  setOperator: async (id: number): Promise<void> => {
    await api.post(`/api/telegram_users/${id}/set_operator`);
  },
  setOneOfUs: async (id: number): Promise<void> => {
    await api.post(`/api/telegram_users/${id}/set_one_of_us`);
  },
  removeRole: async (id: number): Promise<void> => {
    await api.delete(`/api/telegram_users/${id}/remove_role`);
  },
  applyRolesToChatHistory: async (
    source_channel_id: number
  ): Promise<{
    tenant_id: string;
    source_channel_id: number;
    operator_count: number;
    updated_operator_true: number;
    updated_operator_false: number;
  }> => {
    const response = await api.post('/api/telegram_users/apply_roles_to_chat_history', {
      source_channel_id,
    });
    return response.data;
  },
};

export const userManagementService = {
  list: async (): Promise<User[]> => {
    const response = await api.get<User[]>('/api/users');
    return response.data;
  },
  getPolicy: async (): Promise<UserManagementPolicy> => {
    const response = await api.get<UserManagementPolicy>('/api/users/management_policy');
    return response.data;
  },
  getAccessAdminCatalog: async (): Promise<AccessAdminCatalog> => {
    const response = await api.get<AccessAdminCatalog>('/api/users/access-admin/catalog');
    return response.data;
  },
  listAccessRequests: async (): Promise<UserAccessRequest[]> => {
    const response = await api.get<UserAccessRequest[]>('/api/users/access-requests');
    return response.data;
  },
  createGlobalAccessRequest: async (payload: RegistrationPayload): Promise<UserAccessRequest> => {
    const response = await api.post<UserAccessRequest>('/api/users/access-requests', payload);
    return response.data;
  },
  replaceAccessProfile: async (
    id: number,
    payload: ReplaceUserAccessProfilePayload,
  ): Promise<UserAccessRequest> => {
    const response = await api.put<UserAccessRequest>(`/api/users/${id}/access_grants`, payload);
    return response.data;
  },
  updateGlobalAccountProfile: async (
    id: number,
    payload: GlobalAccountProfileUpdatePayload,
  ): Promise<UserAccessRequest> => {
    const response = await api.put<UserAccessRequest>(`/api/users/${id}/access-account/profile`, payload);
    return response.data;
  },
  resetGlobalTemporaryPassword: async (id: number, password: string): Promise<UserAccessRequest> => {
    const response = await api.post<UserAccessRequest>(`/api/users/${id}/access-account/password`, { password });
    return response.data;
  },
  create: async (payload: UserCreatePayload): Promise<User> => {
    const response = await api.post<User>('/api/users', payload);
    return response.data;
  },
  update: async (id: number, payload: UserUpdatePayload): Promise<User> => {
    const response = await api.put<User>(`/api/users/${id}`, payload);
    return response.data;
  },
  assignRole: async (id: number, role: string, department_ids?: number[]): Promise<void> => {
    await api.post(`/api/users/${id}/assign_role`, { role, department_ids });
  },
  changePassword: async (id: number, password: string): Promise<void> => {
    await api.post(`/api/users/${id}/password`, { password });
  },
  changeOwnPassword: async (payload: { current_password: string; new_password: string }): Promise<void> => {
    await api.post('/api/users/password/self', payload);
  },
  deactivate: async (id: number, reason?: string): Promise<User> => {
    const response = await api.post<{ user: User }>(`/api/users/${id}/deactivate`, { reason: reason || null });
    return response.data.user;
  },
  reactivate: async (id: number): Promise<User> => {
    const response = await api.post<{ user: User }>(`/api/users/${id}/reactivate`);
    return response.data.user;
  },
  linkMaxIdentity: async (
    id: number,
    payload: { proof_token: string; confirmation: 'CONFIRM_MAX_IDENTITY_LINK' },
  ): Promise<void> => {
    await api.post(`/api/users/${id}/max-identity`, payload);
  },
  listMaxIdentityCandidates: async (): Promise<{
    items: Array<{ proof_token: string; display_name: string; observed_at?: string | null; proof_expires_at?: string | null }>;
    total: number;
    raw_ids_exposed: false;
  }> => {
    const response = await api.get('/api/users/max-identity-candidates');
    return response.data;
  },
  remove: async (id: number): Promise<void> => {
    await api.delete(`/api/users/${id}`);
  },
};

export type PlatformConstructorTenant = {
  tenant_id: string;
};

export type PlatformConstructorDepartment = {
  tenant_id: string;
  id: number;
  name: string;
  description?: string | null;
  hidden?: boolean;
  archived?: boolean;
};

export type PlatformConstructorTopic = {
  tenant_id: string;
  id: number;
  name: string;
  description?: string | null;
  department_id?: number | null;
  department_name?: string | null;
  hidden?: boolean;
  archived?: boolean;
};

export type PlatformConstructorStreet = {
  tenant_id: string;
  id: number;
  name: string;
  type?: string | null;
  locality_id?: number | null;
  source_instance?: string | null;
};

export type PlatformConstructorMtprotoProfile = {
  tenant_id: string;
  id: number;
  name: string;
  phone: string;
  status: string;
  enabled: boolean;
  parser_enabled: boolean;
  last_test_ok?: boolean | null;
  last_test_at?: string | null;
};

export type PlatformConstructorMtprotoChannel = {
  tenant_id: string;
  id: number;
  profile_id: number;
  profile_name?: string | null;
  profile_phone?: string | null;
  channel_id: number;
  title?: string | null;
  username?: string | null;
  type?: string | null;
  enabled: boolean;
  in_parser_folder: boolean;
  participants_count?: number | null;
  recommended: boolean;
};

export type PlatformConstructorMtprotoRecommendation = {
  channel_id: number;
  title?: string | null;
  username?: string | null;
  type?: string | null;
  participants_count?: number | null;
  access_hash?: number | null;
  already_tracked?: boolean;
};

export type PlatformConstructorCatalog = {
  scope_tenant_id: string;
  tenants: PlatformConstructorTenant[];
  departments: PlatformConstructorDepartment[];
  topics: PlatformConstructorTopic[];
  streets_by_tenant: Record<string, PlatformConstructorStreet[]>;
  mtproto_profiles: PlatformConstructorMtprotoProfile[];
  mtproto_channels: PlatformConstructorMtprotoChannel[];
};

export type PlatformConstructorConfig = {
  tenant_ids: string[];
  taxonomy_mode: 'global' | 'tenant';
  department_keys: string[];
  topic_keys: string[];
  streets_by_tenant: Record<string, number[]>;
  keywords: string[];
  mtproto_profile_ids: number[];
  mtproto_channel_ids: number[];
  notes?: string;
};

export type PlatformConstructorTemplate = {
  id: number;
  scope_tenant_id: string;
  name: string;
  description?: string | null;
  slug?: string | null;
  config: PlatformConstructorConfig;
  archived: boolean;
  created_by_user_id?: number | null;
  updated_by_user_id?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export const platformConstructorService = {
  getCatalog: async (params?: {
    include_archived?: boolean;
    include_hidden?: boolean;
    street_limit_per_tenant?: number;
  }): Promise<PlatformConstructorCatalog> => {
    const response = await api.get<PlatformConstructorCatalog>('/api/platform-constructor/catalog', { params });
    return response.data;
  },
  listTemplates: async (params?: { include_archived?: boolean }): Promise<{ templates: PlatformConstructorTemplate[] }> => {
    const response = await api.get<{ templates: PlatformConstructorTemplate[] }>('/api/platform-constructor/templates', { params });
    return response.data;
  },
  createTemplate: async (payload: {
    name: string;
    description?: string | null;
    slug?: string | null;
    config: PlatformConstructorConfig;
  }): Promise<PlatformConstructorTemplate> => {
    const response = await api.post<PlatformConstructorTemplate>('/api/platform-constructor/templates', payload);
    return response.data;
  },
  updateTemplate: async (
    templateId: number,
    payload: { name: string; description?: string | null; slug?: string | null; config: PlatformConstructorConfig },
  ): Promise<PlatformConstructorTemplate> => {
    const response = await api.put<PlatformConstructorTemplate>(`/api/platform-constructor/templates/${templateId}`, payload);
    return response.data;
  },
  archiveTemplate: async (templateId: number): Promise<PlatformConstructorTemplate> => {
    const response = await api.delete<PlatformConstructorTemplate>(`/api/platform-constructor/templates/${templateId}`);
    return response.data;
  },
  getChannelRecommendations: async (channelId: number): Promise<{ channels: PlatformConstructorMtprotoRecommendation[] }> => {
    const response = await api.get<{ channels: PlatformConstructorMtprotoRecommendation[] }>(`/api/platform-constructor/mtproto/channels/${channelId}/recommendations`);
    return response.data;
  },
  authorizeChannel: async (
    profileId: number,
    payload: { identifier?: string; channel_id?: number; access_hash?: number; enabled?: boolean },
  ): Promise<PlatformConstructorMtprotoChannel> => {
    const response = await api.post<PlatformConstructorMtprotoChannel>(`/api/platform-constructor/mtproto/profiles/${profileId}/channels/authorize`, payload);
    return response.data;
  },
};

export const parserService = {
  listProfiles: async (): Promise<{ profiles: { id: number; name: string; phone: string; api_id: number; status: string; enabled: boolean; bot_username?: string | null; bot_token_configured?: boolean; last_test_at?: string | null; last_test_ok?: boolean | null; parser_enabled?: boolean; scan_interval_sec?: number; max_per_min?: number; lookback_sec?: number }[] }> => {
    const response = await api.get('/api/parser/mtproto/profiles');
    return response.data;
  },
  createProfile: async (payload: { name: string; api_id: number; api_hash: string; phone: string }): Promise<Record<string, unknown>> => {
    const response = await api.post('/api/parser/mtproto/profiles', payload);
    return response.data;
  },
  updateProfile: async (profileId: number, payload: { name?: string; enabled?: boolean; bot_username?: string; bot_token?: string; parser_enabled?: boolean; scan_interval_sec?: number; max_per_min?: number; lookback_sec?: number }): Promise<Record<string, unknown>> => {
    const response = await api.patch(`/api/parser/mtproto/profiles/${profileId}`, payload);
    return response.data;
  },
  deleteProfile: async (profileId: number): Promise<Record<string, unknown>> => {
    const response = await api.delete(`/api/parser/mtproto/profiles/${profileId}`);
    return response.data;
  },
  startProfile: async (profileId: number, payload: { method: 'auto' | 'sms' }): Promise<Record<string, unknown>> => {
    const response = await api.post(`/api/parser/mtproto/profiles/${profileId}/start`, payload);
    return response.data;
  },
  confirmProfileCode: async (profileId: number, payload: { code: string }): Promise<Record<string, unknown>> => {
    const response = await api.post(`/api/parser/mtproto/profiles/${profileId}/confirm_code`, payload);
    return response.data;
  },
  confirmProfilePassword: async (profileId: number, payload: { password: string }): Promise<Record<string, unknown>> => {
    const response = await api.post(`/api/parser/mtproto/profiles/${profileId}/confirm_password`, payload);
    return response.data;
  },
  testProfile: async (profileId: number): Promise<Record<string, unknown>> => {
    const response = await api.post(`/api/parser/mtproto/profiles/${profileId}/test`);
    return response.data;
  },
  syncChannels: async (profileId: number, limit = 500, require_folder = true): Promise<Record<string, unknown>> => {
    const response = await api.post(
      `/api/parser/mtproto/profiles/${profileId}/channels/sync`,
      undefined,
      { params: { limit, require_folder } },
    );
    return response.data;
  },
  clearTrackedChannels: async (profileId: number): Promise<Record<string, unknown>> => {
    const response = await api.post(`/api/parser/mtproto/profiles/${profileId}/channels/clear`);
    return response.data;
  },
  listChannels: async (profileId: number): Promise<{ channels: { id: number; channel_id: number; title?: string | null; username?: string | null; type?: string | null; enabled: boolean; last_sync_at?: string | null; last_scan_from?: string | null; last_scan_to?: string | null; participants_count?: number | null; in_parser_folder?: boolean }[] }> => {
    const response = await api.get(`/api/parser/mtproto/profiles/${profileId}/channels`);
    return response.data;
  },
  getDashboardConfig: async (): Promise<{
    enabled: boolean;
    weather_enabled: boolean;
    weather_city?: string | null;
    weather_lat?: number | null;
    weather_lon?: number | null;
    service_channels: Array<{ mtproto_channel_id: number; category: 'electricity' | 'water' | 'mchs' | 'other' }>;
    category_rules: Record<'electricity' | 'water' | 'mchs' | 'other', string[]>;
    available_channels: Array<{
      mtproto_channel_id: number;
      profile_id: number;
      channel_id: number;
      title?: string | null;
      username?: string | null;
      enabled: boolean;
      type?: string | null;
    }>;
    updated_at?: string | null;
  }> => {
    const response = await api.get('/api/parser/mtproto/dashboard/config');
    return response.data;
  },
  updateDashboardConfig: async (payload: {
    enabled?: boolean;
    weather_enabled?: boolean;
    weather_city?: string | null;
    weather_lat?: number | null;
    weather_lon?: number | null;
    service_channels?: Array<{ mtproto_channel_id: number; category: 'electricity' | 'water' | 'mchs' | 'other' }>;
    category_rules?: Partial<Record<'electricity' | 'water' | 'mchs' | 'other', string[]>>;
  }): Promise<Record<string, unknown>> => {
    const response = await api.put('/api/parser/mtproto/dashboard/config', payload);
    return response.data;
  },
  getChannelRecommendations: async (profileId: number, channelId: number): Promise<{ channels: { channel_id: number; title?: string | null; username?: string | null; type?: string; participants_count?: number | null; access_hash?: number | null }[] }> => {
    const response = await api.get(`/api/parser/mtproto/profiles/${profileId}/channels/recommendations`, { params: { channel_id: channelId } });
    return response.data;
  },
  getChannelRecommendationsByPeer: async (profileId: number, payload: { tg_channel_id?: number; username?: string | null }): Promise<{ channels: { channel_id: number; title?: string | null; username?: string | null; type?: string; participants_count?: number | null; access_hash?: number | null }[] }> => {
    const response = await api.get(`/api/parser/mtproto/profiles/${profileId}/channels/recommendations`, { params: payload });
    return response.data;
  },
  authorizeChannel: async (
    profileId: number,
    payload: { identifier?: string; channel_id?: number; access_hash?: number; enabled?: boolean }
  ): Promise<Record<string, unknown>> => {
    const response = await api.post(`/api/parser/mtproto/profiles/${profileId}/channels/authorize`, payload);
    return response.data;
  },
  updateChannel: async (channelId: number, enabled: boolean): Promise<Record<string, unknown>> => {
    const response = await api.patch(`/api/parser/mtproto/channels/${channelId}`, undefined, { params: { enabled } });
    return response.data;
  },
  listKeywords: async (channelId: number): Promise<{ keywords: { id: number; keyword: string; match_mode?: string | null; exact_match?: boolean | null; enabled_ai?: boolean | null; prompt_override?: string | null; vertical_code?: string | null }[] }> => {
    const response = await api.get(`/api/parser/mtproto/channels/${channelId}/keywords`);
    return response.data;
  },
  addKeyword: async (channelId: number, payload: { keyword: string; match_mode?: string; exact_match?: boolean; vertical_code?: string | null }): Promise<Record<string, unknown>> => {
    const response = await api.post(`/api/parser/mtproto/channels/${channelId}/keywords`, payload);
    return response.data;
  },
  updateKeyword: async (keywordId: number, payload: { enabled_ai?: boolean; prompt_override?: string | null; vertical_code?: string | null }): Promise<Record<string, unknown>> => {
    const response = await api.patch(`/api/parser/mtproto/keywords/${keywordId}`, payload);
    return response.data;
  },
  listProfileKeywords: async (profileId: number): Promise<{ keywords: { id: number; keyword: string; match_mode?: string | null; exact_match?: boolean | null; enabled_ai?: boolean | null; prompt_override?: string | null; vertical_code?: string | null }[] }> => {
    const response = await api.get(`/api/parser/mtproto/profiles/${profileId}/keywords`);
    return response.data;
  },
  addProfileKeyword: async (profileId: number, payload: { keyword: string; match_mode?: string; exact_match?: boolean; vertical_code?: string | null }): Promise<Record<string, unknown>> => {
    const response = await api.post(`/api/parser/mtproto/profiles/${profileId}/keywords`, payload);
    return response.data;
  },
  updateProfileKeyword: async (keywordId: number, payload: { enabled_ai?: boolean; prompt_override?: string | null; vertical_code?: string | null }): Promise<Record<string, unknown>> => {
    const response = await api.patch(`/api/parser/mtproto/profile_keywords/${keywordId}`, payload);
    return response.data;
  },
  deleteProfileKeyword: async (keywordId: number): Promise<Record<string, unknown>> => {
    const response = await api.delete(`/api/parser/mtproto/profile_keywords/${keywordId}`);
    return response.data;
  },
  deleteKeyword: async (keywordId: number): Promise<Record<string, unknown>> => {
    const response = await api.delete(`/api/parser/mtproto/keywords/${keywordId}`);
    return response.data;
  },
  getPrompt: async (profileId: number): Promise<{ global_prompt?: string | null; extra_prompt?: string | null }> => {
    const response = await api.get(`/api/parser/mtproto/profiles/${profileId}/prompt`);
    return response.data;
  },
  setPrompt: async (profileId: number, global_prompt: string | null): Promise<Record<string, unknown>> => {
    const response = await api.put(`/api/parser/mtproto/profiles/${profileId}/prompt`, { global_prompt });
    return response.data;
  },
  listTargets: async (profileId: number): Promise<{ targets: { id: number; target_chat_id: number; title?: string | null; username?: string | null; type?: string | null; enabled: boolean }[] }> => {
    const response = await api.get(`/api/parser/mtproto/profiles/${profileId}/targets`);
    return response.data;
  },
  addTarget: async (profileId: number, payload: { target_chat_id: number; title?: string; username?: string; type?: string }): Promise<Record<string, unknown>> => {
    const response = await api.post(`/api/parser/mtproto/profiles/${profileId}/targets`, payload);
    return response.data;
  },
  updateTarget: async (targetId: number, enabled: boolean): Promise<Record<string, unknown>> => {
    const response = await api.patch(`/api/parser/mtproto/targets/${targetId}`, undefined, { params: { enabled } });
    return response.data;
  },
  testTarget: async (profileId: number, targetId: number, payload?: { message?: string }): Promise<Record<string, unknown>> => {
    const response = await api.post(`/api/parser/mtproto/profiles/${profileId}/targets/${targetId}/test`, payload);
    return response.data;
  },
  deleteTarget: async (targetId: number): Promise<Record<string, unknown>> => {
    const response = await api.delete(`/api/parser/mtproto/targets/${targetId}`);
    return response.data;
  },
  listMtprotoAiAnalyses: async (messageId: number, channelId?: number): Promise<{ analyses: { id: number; channel_id: number; message_id: number; keyword_id: number; keyword_scope: string; keyword_value?: string | null; prompt_hash: string; status: string; result?: Record<string, unknown> | null; error_message?: string | null; latency_ms?: number | null; created_at?: string | null; updated_at?: string | null }[] }> => {
    const response = await api.get(`/api/parser/mtproto/messages/${messageId}/ai-analyses`, { params: { channel_id: channelId } });
    return response.data;
  },
};

export type InfofieldSettings = {
  title: string;
  account_mode: 'platform_shared' | 'tenant_owned';
  channel_limit: number;
  enabled_channels_count: number;
  limit_explanation: string;
  account_profile: {
    id: number | null;
    name?: string | null;
    phone?: string | null;
    status: 'not_configured' | 'pending_verification' | 'active' | 'failed' | 'needs_reauthorization';
    api_id_configured: boolean;
    api_hash_configured: boolean;
    session_configured: boolean;
    last_test_at?: string | null;
    last_error_class?: string | null;
  };
  sensitivity_threshold: number;
  send_policy: 'all_matched' | 'important_only';
  prompt: { version: string; text: string; fallback_used: boolean };
  destinations: {
    important: { target_id?: number | null; enabled: boolean };
    non_important: { target_id?: number | null; enabled: boolean };
    available_targets: Array<{ id: number; target_chat_id: number; title?: string | null; username?: string | null; enabled: boolean; post_access_status: string }>;
  };
  disabled_excess_channel_ids?: number[];
  warning?: string;
};

export type InfofieldChannel = {
  id: number;
  channel_id: number;
  display_name: string;
  source_link?: string | null;
  username?: string | null;
  enabled: boolean;
  last_scan_to?: string | null;
  last_success_at?: string | null;
  last_error_class?: string | null;
  access_status: string;
  keywords_count: number;
  account_profile: number;
};

export type InfofieldKeyword = {
  id: number;
  scope: 'tenant' | 'channel';
  channel_id?: number | null;
  profile_id?: number | null;
  keyword: string;
  match_mode?: 'contains' | 'starts_with';
  exact_match?: boolean;
  vertical_code?: string | null;
  enabled: boolean;
  category?: string | null;
  routing_hint?: string | null;
  min_relevance_threshold?: number | null;
};

export const infofieldService = {
  get: async (): Promise<InfofieldSettings> => {
    const response = await api.get<InfofieldSettings>('/api/city-settings/infofield');
    return response.data;
  },
  update: async (payload: Partial<{
    account_mode: 'platform_shared' | 'tenant_owned';
    selected_profile_id: number | null;
    sensitivity_threshold: number;
    send_policy: 'all_matched' | 'important_only';
    important_group_id: number | null;
    important_group_enabled: boolean;
    non_important_group_id: number | null;
    non_important_group_enabled: boolean;
  }>): Promise<InfofieldSettings> => {
    const response = await api.put<InfofieldSettings>('/api/city-settings/infofield', payload);
    return response.data;
  },
  listChannels: async (): Promise<{ channels: InfofieldChannel[]; limits: InfofieldSettings }> => {
    const response = await api.get('/api/city-settings/infofield/channels');
    return response.data;
  },
  createChannel: async (payload: { identifier: string; display_name?: string; enabled?: boolean }): Promise<InfofieldChannel> => {
    const response = await api.post<InfofieldChannel>('/api/city-settings/infofield/channels', payload);
    return response.data;
  },
  updateChannel: async (id: number, payload: { enabled?: boolean; display_name?: string | null }): Promise<InfofieldChannel> => {
    const response = await api.patch<InfofieldChannel>(`/api/city-settings/infofield/channels/${id}`, payload);
    return response.data;
  },
  listKeywords: async (): Promise<{ keywords: InfofieldKeyword[] }> => {
    const response = await api.get('/api/city-settings/infofield/keywords');
    return response.data;
  },
  createKeyword: async (payload: { keyword: string; channel_id?: number | null; enabled?: boolean; category?: string | null; routing_hint?: string | null; min_relevance_threshold?: number | null; vertical_code?: string | null }): Promise<InfofieldKeyword> => {
    const response = await api.post<InfofieldKeyword>('/api/city-settings/infofield/keywords', payload);
    return response.data;
  },
  updateKeyword: async (scope: 'tenant' | 'channel', id: number, payload: Partial<InfofieldKeyword>): Promise<InfofieldKeyword> => {
    const response = await api.patch<InfofieldKeyword>(`/api/city-settings/infofield/keywords/${scope}/${id}`, payload);
    return response.data;
  },
  deleteKeyword: async (scope: 'tenant' | 'channel', id: number): Promise<Record<string, unknown>> => {
    const response = await api.delete(`/api/city-settings/infofield/keywords/${scope}/${id}`);
    return response.data;
  },
  updatePrompt: async (payload: { prompt: string | null; version?: string }): Promise<InfofieldSettings['prompt']> => {
    const response = await api.put('/api/city-settings/infofield/prompt', payload);
    return response.data;
  },
  testMessage: async (payload: { text: string; channel_context?: string; keyword_id?: number; prompt_version?: string; threshold?: number }): Promise<{
    matched_keywords: InfofieldKeyword[];
    ai_score: number;
    score: number;
    threshold: number;
    ai_reason_summary: string;
    decision: 'important' | 'not_important' | 'ignore';
    would_send_to: 'important_group' | 'non_important_group' | 'none';
    dry_run: boolean;
    external_sends_performed: boolean;
    tickets_created: boolean;
    raw_text_retained: boolean;
  }> => {
    const response = await api.post('/api/city-settings/infofield/test-message', payload);
    return response.data;
  },
  instructions: async (): Promise<{ title: string; sections: Array<{ title: string; body: string }> }> => {
    const response = await api.get('/api/city-settings/infofield/instructions');
    return response.data;
  },
};

export const llmWorkbenchService = {
  getRegistry: async (): Promise<LlmWorkbenchRegistryResponse> => {
    const response = await api.get<LlmWorkbenchRegistryResponse>('/api/dev/llm-workbench/registry');
    return response.data;
  },
  previewPayload: async (payload: { step_name: string; input: LlmWorkbenchInputPayload }): Promise<LlmWorkbenchPayloadPreview> => {
    const response = await api.post<LlmWorkbenchPayloadPreview>('/api/dev/llm-workbench/payload', payload);
    return response.data;
  },
  runCompare: async (payload: {
    steps: string[];
    input: LlmWorkbenchInputPayload;
    config_a: LlmWorkbenchConfigPayload;
    config_b: LlmWorkbenchConfigPayload;
  }): Promise<LlmWorkbenchCompareResponse> => {
    const response = await api.post<LlmWorkbenchCompareResponse>('/api/dev/llm-workbench/compare', payload);
    return response.data;
  },
};

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('active_contour_changed', () => {
    clearProtectedDataCaches('active_contour_changed');
  });
}

export default api;
